import { useCallback, useDeferredValue, useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import api, { openAuthenticatedFile } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { canAccessInvoicing as hasInvoicingAccess, canEdit, hasPermission } from '@/lib/permissions';
import HistoryDialog from '@/components/HistoryDialog';
import ProjectTagsInput from '@/components/ProjectTagsInput';
import { selectPaymentScopes, summarizePaymentScopes } from '@/lib/projectPaymentSummary';
import { useAppleProjectTheme } from '@/lib/useAppleProjectTheme';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { useBreadcrumbs } from '@/components/ui/breadcrumbs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { TriangleAlert, Plus, Minus, Pencil, GripVertical, ListTodo, Loader2, Upload, History, Trash2, Copy, SquarePlus, Layers, Cpu, Server, DoorOpen, SquareDashed, Download, Save, Settings2, Printer, Link2, CalendarClock, CirclePause, CirclePlay, RefreshCw, ImagePlus, SearchX, FileDown, WandSparkles, Route, Eye } from 'lucide-react';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { TableCheckbox } from '@/components/ui/table-checkbox';
import { RowActions } from '@/components/ui/row-actions';
import { notify } from '@/components/ui/notify';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, formatNumber } from '@/lib/format';
import { useUndo } from '@/contexts/UndoContext';
import { StagePipeline, WaitingOnCard, RecentActivityCard } from '@/components/project/StagesOverview';
import { stageClocks } from '@/components/project/stagesModel';
import { BlockersCard } from '@/components/project/BlockersCard';
import { buildBlockers } from '@/components/project/blockersModel';
import { DnPhotoPanel } from '@/components/project/DnPhotoPanel';
import { FilesHealthCard, FolderPreviewCard, ScanHistoryCard, StageEvidenceCard, useFilesScans } from '@/components/project/FilesHealth';
import { useMaterialStock } from '@/components/project/useMaterialStock';
import { useFeature } from '@/hooks/useIntegrationStatus';
import { MONO as MONO_CLASS, TabHeader } from '@/components/project/ProjectUi';
import {
  PROJECT_TABS,
  ProjectDetailsList,
  ProjectHeader,
  ProjectPageSkeleton,
  ProjectTabsBar,
  TabContentSkeleton,
} from '@/components/project/ProjectHeader';
import { StageDotsStepper, StageWaitingCard, StagesList } from '@/components/project/StagesList';
import { StageEditPanel } from '@/components/project/StageEditPanel';
import {
  TAB_BLOCKING_RESOURCES,
  RESOURCE_PERMISSIONS,
  TAB_RESOURCES,
  canReadResource,
  buildHeaderFacts,
  buildTabCounts,
  filesAttention,
  folderPathKey,
  scanMatchesFolder,
  resourcesSettled,
  summarizeStages,
} from '@/components/project/overviewModel';
import { SidePanel, SidePanelSection } from '@/components/ui/side-panel';
import { useIsPhone } from '@/hooks/useMediaQuery';
import { useUnsavedChanges, withoutLeaveGuard } from '@/hooks/useUnsavedChanges';
import { readStored, storageKey, writeStored } from '@/lib/storage';
import {
  MaterialBulkBar,
  MaterialConflictCallout,
  MaterialEmptyState,
  MaterialKpiStrip,
  MaterialRowHistory,
  MaterialToolbar,
  MaterialTotalsFooter,
  MaterialVariationCallout,
} from '@/components/project/MaterialPanels';
import { FilesToolbar, FolderChecklist, FolderPathCard } from '@/components/project/FilesPanels';
import {
  REQUIRED_FOLDER_COUNT,
  buildFolderChecklist,
  buildStageEvidence,
  folderForStageLine,
  folderHealth,
  linkedStageForFolder,
} from '@/components/project/filesModel';
import {
  StructureChangesReview,
  StructureSummaryStrip,
} from '@/components/project/StructurePanels';
import { StructureImportWizard } from '@/components/project/StructureImportWizard';
import {
  buildStructureIndex,
  diffStructure,
  findDuplicateAddresses,
  structureImpactSummary,
  getBmsSubCollapseKey,
  getBuildingCollapseKey,
  getFloorCollapseKey,
  getPanelCollapseKey,
  getRoomCollapseKey,
} from '@/components/project/structureModel';
import {
  ExecutionBulkBar,
  ExecutionCards,
  ExecutionFilterBar,
  ExecutionColumnsMenu,
  ExecutionStatusSummary,
  ExecutionTable,
  SaveCountPill,
} from '@/components/project/ExecutionPanels';
import {
  BLOCKED_STATUSES,
  EXECUTION_ASSIGNEES,
  EXECUTION_FILTER_DEFAULTS,
  WAITING_STATUSES,
  applyBulkChange,
  buildExecutionRows,
  buildExecutionTree,
  countExecutionChanges,
  countMainTasksMatching,
  executionFilterOptions,
  executionStatusBreakdown,
  expandSelection,
  filterExecutionTasks,
  formatExecutionTaskTitle,
  getDescendantIds,
  planBulkChange,
  reopenDoneAncestors,
  revertBulkChange,
  syncExecutionTaskHierarchy,
  executionNamesForUser,
  splitPeople,
} from '@/components/project/executionModel';
import { HandoverBanner, TimelineControls, UpdateEndDateDialog } from '@/components/project/TimelinePanels';
import { ApplyPlanDialog, PlanCard, TimelineGantt, buildTimelineModel, openLookahead } from '@/components/project/TimelinePlan';
import { buildAutoPlan, weekendFor } from '@/components/project/planModel';
import { MaterialTracePanel } from '@/components/supply-finance/MaterialTracePanel';
import { todayGst } from '@/components/project/variationTrailModel';
import { useWorkspace } from '@/contexts/AppSettingsContext';
import { timelineItemsFromTasks } from '@/components/project/timelineModel';
import { EmptyState } from '@/components/ui/empty-state';
import { VariationPanel, VariationsSummary, VariationsTable, suggestNextVoNumber, useVariationShortages, variationsToCsv } from '@/components/project/VariationsPanels';
import { useUrlParam, useUrlState } from '@/hooks/useUrlState';
import { ExecutionBoard, ExecutionMatrix, ExecutionViewSwitch } from '@/components/project/ExecutionViews';
import { SiteMode } from '@/components/project/SiteMode';
import { TemplateDependsPicker, TemplateDurationInput } from '@/components/project/TemplatePlanFields';
import { useSavedViews } from '@/hooks/useSavedViews';
import { bmsPathSteps, describeStructureNode, findAtPath, insertAtPath, removeAtPath } from '@/components/project/structureEdit';
import { StructureEditor, structureConflicts } from '@/components/project/StructureEditor';
import { ContractMoneyBar, InvoiceScopeSwitcher, InvoicesTable, PaymentMilestonesTable, QuickBooksSyncNote, ReadyToBillCallout } from '@/components/project/InvoicingPanels';
import { InvoicePanel } from '@/components/project/InvoicePanel';
import { QbDesktopPanel, qbProjectMatchFor, useQbDesktopProject } from '@/components/project/QbDesktopPanel';
import { RETENTION_DLP_DAYS, addDaysIso, canSyncToQuickBooks, findReadyToBill, groupInvoices, invoicesToCsv, splitTypes, summarizeCollection } from '@/components/project/invoicingModel';
import { BulkBar } from '@/components/ui/bulk-bar';
import { boqRowsToPasteText, boqTemplateXlsx, materialRowsToXlsx, parseQuantity, readBoqSpreadsheet } from '@/components/project/materialExcel';
import { MaterialTable } from '@/components/project/MaterialTable';
import { isValidPartNumber, PART_NUMBER_INVALID } from '@/components/inventory/inventoryModel';
import {
  buildMaterialSavePayload,
  deliveryNoteCell,
  deliveryNoteScope,
  diffMaterialDrafts,
  inDeliveryNoteScope,
  materialRowHistory,
  qtyText,
  sortByCode,
  summarizeMaterialKpis,
  summarizeVariationNeeded,
  applyBoqPreview,
} from '@/components/project/materialModel';
import { UnsavedBar, useToastClearance } from '@/components/ui/unsaved-bar';
import { ChipSelect } from '@/components/ui/chip-select';
import { SavedViews } from '@/components/ui/saved-views';
import { SearchField } from '@/components/ui/search-field';
import { Segmented as SegmentedControl } from '@/components/ui/segmented';
import { StatusPill } from '@/components/ui/status-pill';
import {
  EXECUTION_ANNOUNCED_FIELDS,
  buildMoneyBar,
  canSeeLpoLoiStage,
  collectWaitingOn,
  countInvoicesByScope,
  createMaterialMatcher,
  describeExecutionCascade,
  findUnlinkedMaterial,
  getHandoverState,
  getMaterialProgress,
  isAdminLike,
  isStageDone,
  normalizeExecutionColumns,
  summarizeExecution,
  summarizeStructure,
  summarizeVariations,
} from '@/components/project/projectDetailModel';

const createStageLine = (overrides = {}) => ({
  id: overrides.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  date: '',
  name: '',
  note: '',
  status: 'In Progress',
  reference: '',
  extra_fields: [],
  sub_items: [],
  ...overrides
});

const createStageExtraField = (overrides = {}) => ({
  id: overrides.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  title: '',
  content: '',
  ...overrides
});

const createProjectCustomField = (overrides = {}) => ({
  id: overrides.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  title: '',
  content: '',
  ...overrides
});

const normalizeProjectCustomFields = (fields) => (
  Array.isArray(fields)
    ? fields.map((field) => createProjectCustomField({
      id: field.id,
      title: field.title || '',
      content: field.content || ''
    }))
    : []
);

const normalizeLines = (lines) => {
  if (!Array.isArray(lines) || lines.length === 0) {
    return [createStageLine()];
  }
  return lines.map((line) => createStageLine({
    id: line.id,
    date: line.date || '',
    name: line.name || '',
    note: line.note || '',
    status: line.status || 'In Progress',
    reference: line.reference || '',
    extra_fields: Array.isArray(line.extra_fields)
      ? line.extra_fields.map((field) => createStageExtraField({
        id: field.id,
        title: field.title || '',
        content: field.content || ''
      }))
      : [],
    sub_items: Array.isArray(line.sub_items)
      ? line.sub_items.map((subItem) => createStageLine({
        id: subItem.id,
        date: subItem.date || '',
        name: subItem.name || '',
        note: subItem.note || '',
        status: subItem.status || 'In Progress',
        reference: subItem.reference || '',
        extra_fields: Array.isArray(subItem.extra_fields)
          ? subItem.extra_fields.map((field) => createStageExtraField({
            id: field.id,
            title: field.title || '',
            content: field.content || ''
          }))
          : [],
        sub_items: []
      }))
      : []
  }));
};

const normalizePaymentTerms = (value) => {
  if (Array.isArray(value)) {
    return joinMultiSelectValues(
      value
        .map((item) => {
          if (item && typeof item === 'object') {
            return String(item.name || '').trim();
          }
          return String(item || '').trim();
        })
        .filter(Boolean)
    );
  }

  if (typeof value === 'string' && value.trim()) {
    return normalizeInvoiceTypeValue(value);
  }

  return '';
};

const autoFieldWidth = (value, min = 8, max = 40, fallback = '') => {
  const text = String(value || fallback || '');
  const width = Math.min(max, Math.max(min, text.length + 2));
  return { width: `${width}ch` };
};

/** The "Edit project details" form values of a project. */
const projectToForm = (project) => ({
  name: project.name || '',
  client: project.client || '',
  consultant: project.consultant || '',
  contractor: project.contractor || '',
  project_engineer: project.project_engineer || '',
  senior_project_engineer: project.senior_project_engineer || '',
  location: project.location || '',
  waze_url: project.waze_url || '',
  system_type: project.system_type || '',
  scope: Array.isArray(project.scope) ? project.scope : [],
  tags: Array.isArray(project.tags) ? project.tags : [],
  status: project.status || 'Active',
  start_date: project.start_date || '',
  end_date: project.end_date || '',
  notes: project.notes || '',
  total_project_value: project.total_project_value ?? '',
  original_scope_value: project.original_scope_value ?? project.total_project_value ?? '',
  payment_terms: normalizePaymentTerms(project.payment_terms),
  salesman: project.salesman || '',
  files_root_path: project.files_root_path || '',
  custom_fields: normalizeProjectCustomFields(project.custom_fields),
  allow_sub_boq: Boolean(project.allow_sub_boq),
  allow_duplicate_device_addresses: Boolean(project.allow_duplicate_device_addresses)
});

const createSiteRequestForm = (row = null) => ({
  boq_item_id: row?.id || '',
  requested_qty: row?.remaining > 0 ? String(row.remaining) : '',
  due_date: '',
  notes: ''
});

const parseExcelQty = parseQuantity;

const parsePastedRowsWithQty = (raw) => (
  String(raw || '')
    .split(/\r?\n/)
    .filter((line) => line.trim())
    .map((line) => {
      const columns = line.includes('\t')
        ? line.split('\t')
        : line.trim().split(/\s{2,}|,|;/);
      const part_number = String(columns[0] || '').trim();
      const description = String(columns[1] || '').trim();
      const qty = parseExcelQty(columns[2]);
      return { part_number, description, qty };
    })
    .filter((row, index) => {
      if (index === 0) {
        const first = row.part_number.toLowerCase();
        const second = row.description.toLowerCase();
        if (
          (first.includes('model') || first.includes('part'))
          && second.includes('description')
        ) {
          return false;
        }
      }
      return row.qty > 0 && Boolean(row.part_number || row.description);
    })
);

const getCatalogSelectionKey = (item) => item?.selection_key || item?.part_number || item?.description || '';
const normalizeSubBoqLabel = (value) => {
  const trimmed = String(value || '').trim();
  return trimmed || 'Unassigned';
};
const getMainBoqIdentity = (item = {}) => {
  const partKey = String(item.part_number || '').trim().toUpperCase();
  const descriptionKey = String(item.description || item.name || '').trim().toUpperCase();
  return partKey
    ? `${partKey}::${descriptionKey}`
    : `NO_PART::${descriptionKey || String(item.id || '').trim().toUpperCase()}`;
};
const normalizeCatalogResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

function SuggestionInput({ value, onChange, options, placeholder, className, disabled = false }) {
  const [open, setOpen] = useState(false);
  const filtered = options.filter((item) => item.toLowerCase().includes((value || '').toLowerCase()));

  return (
    <div className="relative">
      <Input
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 max-h-44 w-full overflow-y-auto rounded-sm border border-zinc-700 bg-zinc-900">
          {filtered.map((item) => (
            <button
              key={item}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(item);
                setOpen(false);
              }}
              className="block w-full px-3 py-2 text-left text-sm text-zinc-200 hover:bg-zinc-800"
            >
              {item}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const parseMultiSelectString = (value) => (
  [...new Set(String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean))]
);

const joinMultiSelectValues = (values) => values.join(', ');
const normalizeInvoiceTypeValue = (value) => joinMultiSelectValues(parseMultiSelectString(value));
const normalizeInvoiceTypePercentages = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.entries(value).reduce((acc, [key, raw]) => {
    const name = String(key || '').trim();
    const parsed = Number(raw);
    if (!name || !Number.isFinite(parsed) || parsed < 0) return acc;
    acc[name] = parsed;
    return acc;
  }, {});
};

const createEmptyProjectTaskForm = (source = 'material') => ({
  source,
  name: '',
  description: '',
  assigned_to_user_id: '',
  due_date: '',
  invoice_id: '',
  invoice_reference: '',
  stage_id: '',
  stage_name: '',
  stage_line_id: '',
  stage_line_name: ''
});
// Money with en-US grouping and 2 decimals whatever the browser locale (foundation formatter).
const formatAmount = (value) => formatNumber(Number(value || 0), { decimals: 2 });
// 28 #8: a project field owned by a tab whose module the role may only VIEW (the server refuses changing it)
function ViewOnlyFieldNote({ module }) {
  return <span className="block text-[11.5px] text-ink-3">{module} is view-only for your role — this stays as it is.</span>;
}

function MultiSelectPopover({ value, onChange, onClose, options, placeholder, className, disabled, style, ariaLabel }) {
  const selectedValues = parseMultiSelectString(value);

  const handleToggleValue = (option, checked) => {
    const next = checked
      ? [...new Set([...selectedValues, option])]
      : selectedValues.filter((item) => item !== option);
    onChange(joinMultiSelectValues(next));
  };

  return (
    // onClose: the person is done ticking (one commit for several ticks).
    <Popover onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={className}
          disabled={disabled}
          style={style}
          aria-label={ariaLabel ? `${ariaLabel}: ${selectedValues.length ? selectedValues.join(', ') : 'none'}` : undefined}
        >
          <span className="truncate text-left">
            {selectedValues.length ? selectedValues.join(', ') : placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[280px] border-zinc-800 bg-zinc-900 p-2 text-zinc-100">
        <div className="max-h-64 space-y-1 overflow-y-auto">
          {options.map((option) => (
            <label key={option} className="flex items-center gap-2 rounded-sm px-2 py-1 text-xs text-zinc-200 hover:bg-zinc-800">
              <Checkbox
                checked={selectedValues.includes(option)}
                onCheckedChange={(checked) => handleToggleValue(option, Boolean(checked))}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
        <div className="mt-2 flex justify-end">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100"
            onClick={() => onChange('')}
          >
            Clear
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const STAGE_STATUS_OPTIONS = ['Not Started', 'In Progress', 'Submitted', 'Approved', 'Rejected'];
const VARIATION_STATUS_OPTIONS = ['Proposed', 'Approved', 'Rejected'];
const STAGE_LINE_STATUS_OPTIONS = [
  'In Progress',
  'Pending from presales',
  'Pending from sales',
  'Pending from Execution',
  'Pending from contractor',
  'Completed'
];
const EXECUTION_CATEGORY_OPTIONS = ['Execution', 'Drawings', 'Testing', 'Programming', 'Checking Prerequisites', 'Documentation', 'Installation', 'Material', 'Graphics', 'Manufacturing', 'WIR'];
const EXECUTION_EXPANSION_OPTIONS = [
  { value: 'none', label: 'No expansion' },
  { value: 'building', label: 'Per Building' },
  { value: 'floor', label: 'Per Building -> Floor' },
  { value: 'panel', label: 'Per Building -> Floor -> Panel' },
  { value: 'panel_device', label: 'Per Building -> Floor -> Panel -> Device' }
];
const BMS_PANEL_DEVICE_TYPES = ['ASP', 'RPC', 'RPC-Dali', 'RPC-Blind', 'RPC-KNX', 'Power supply'];
const BMS_FIELD_DEVICE_TYPES = ['FCU', 'SXW', 'SLP', 'Insight sensor', 'PIR', 'TRC', 'Dali driver', 'On/off light', 'curtain', '0=10 driver', 'KNX Device'];
const BMS_NETWORK_TYPES = ['IP network', 'RS485 Network'];
const BMS_GENERIC_SUB_TYPES = ['Panel', ...BMS_NETWORK_TYPES, ...BMS_PANEL_DEVICE_TYPES, ...BMS_FIELD_DEVICE_TYPES];
const CUSTOM_BMS_SUB_TYPES_STORAGE_KEY = 'awadypm.customBmsSubTypes';
const INVOICE_TYPE_OPTIONS = ['Advanced payment', 'Against Delivery', 'Cable pulling', 'Installation', 'T&C', 'Handing Over', 'Retention'];
const INVOICE_STATUS_OPTIONS = ['Not Started', 'Submitted', 'Payment Received'];
const PROJECT_SCOPE_OPTIONS = ['Cable Pulling', 'Panels termination', 'installation', 'T&C'];
const PROJECT_SALESMEN = ['Ammar', 'Mousa', 'Qamheya', 'Tarek'];
const LOCK_HIDDEN_STAGE_NAMES = new Set([
  'pre-qualification',
  'material submittal',
  'technical submittal',
  'shop drawings',
  'schematics'
]);
const STAGE_SUBITEM_STAGE_NAMES = new Set(['received drawings', 'shop drawings', 'schematics']);
const supportsStageLineChildren = (stageName) => STAGE_SUBITEM_STAGE_NAMES.has(String(stageName || '').trim().toLowerCase());
const normalizeReferenceUrl = (value) => {
  const v = (value || '').trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v}`;
};
const isWindowsPath = (value) => /^[A-Za-z]:\\/.test((value || '').trim()) || /^\\\\/.test((value || '').trim());
const isLocalAbsolutePath = (value) => isWindowsPath(value) || /^(\/|~\/)/.test((value || '').trim());
const isWebUrl = (value) => /^https?:\/\//i.test((value || '').trim());
const isBmsSystemType = (value) => String(value || '').trim().toLowerCase() === 'bms';
const createExecutionStructure = () => ({ buildings: [] });
const createBuilding = () => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: 'Building', floors: [] });
const createFloor = () => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: 'Floor', devices: [], panels: [], rooms: [] });
const createRoom = () => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: 'Room', devices: [], panels: [] });
const createPanel = () => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: 'Panel', type: 'panel', devices: [] });
const createZone = () => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: 'Zone', type: 'zone', devices: [] });
const createBmsRootSub = (subType = 'Panel', overrides = {}) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: '',
  sub_type: subType,
  zone_name: '',
  controlled_by: '',
  devices: [],
  panels: [],
  rooms: [],
  ...overrides
});
const createBmsSub = (subType = 'Panel', overrides = {}) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  boq_item_id: '',
  part_number: '',
  name: '',
  address: '',
  device_type: subType,
  node_type: 'sub',
  zone_name: '',
  controlled_by: '',
  children: [],
  ...overrides
});
const getStructureNodeChildren = (node) => {
  if (!node || typeof node !== 'object') return [];
  if (Array.isArray(node.children)) return node.children;
  if (Array.isArray(node.devices)) return node.devices;
  return [];
};
const normalizeStructureDeviceNodes = (devices = []) => (
  (devices || []).map((device) => {
    if (!device || typeof device !== 'object') {
      return createDevice({ name: String(device || 'Device') });
    }
    const normalizedNodeType = String(device.node_type || (device.zone_name ? 'field_device' : 'device')).trim().toLowerCase();
    return {
      ...device,
      node_type: ['device', 'field_device', 'network', 'network_panel', 'sub'].includes(normalizedNodeType) ? normalizedNodeType : 'device',
      device_type: device.device_type || '',
      zone_name: device.zone_name || '',
      controlled_by: device.controlled_by || '',
      children: normalizeStructureDeviceNodes(getStructureNodeChildren(device))
    };
  })
);
const normalizeStructurePanels = (panels = []) => (
  (panels || []).map((panel) => {
    const explicitType = String(panel?.type || '').trim().toLowerCase();
    const inferredType = String(panel?.name || '').trim().toLowerCase().startsWith('zone') ? 'zone' : 'panel';
    return {
      ...panel,
      type: (explicitType === 'zone' || explicitType === 'panel') ? explicitType : inferredType,
      devices: normalizeStructureDeviceNodes(panel?.devices || [])
    };
  })
);
export const normalizeStructurePanelTypes = (structure) => ({
  ...(structure || {}),
  buildings: ((structure?.buildings || [])).map((building) => ({
    ...building,
    floors: ((building?.floors || [])).map((floor) => ({
      ...floor,
      sub_type: floor?.sub_type || '',
      zone_name: floor?.zone_name || '',
      controlled_by: floor?.controlled_by || '',
      devices: normalizeStructureDeviceNodes(floor?.devices || []),
      panels: normalizeStructurePanels(floor?.panels || []),
      rooms: ((floor?.rooms || [])).map((room) => ({
        ...room,
        devices: normalizeStructureDeviceNodes(room?.devices || []),
        panels: normalizeStructurePanels(room?.panels || [])
      }))
    }))
  }))
});
const hasBmsStructureNodes = (devices = []) => (
  (devices || []).some((device) => {
    if (!device || typeof device !== 'object') return false;
    const nodeType = String(device.node_type || '').trim().toLowerCase();
    const deviceType = String(device.device_type || '').trim();
    const zoneName = String(device.zone_name || '').trim();
    return (
      ['field_device', 'network', 'network_panel', 'sub'].includes(nodeType)
      || Boolean(deviceType)
      || Boolean(zoneName)
      || hasBmsStructureNodes(getStructureNodeChildren(device))
    );
  })
);
const hasBmsStructureContent = (structure) => (
  (structure?.buildings || []).some((building) => (
    (building?.floors || []).some((floor) => (
      Boolean(String(floor?.sub_type || '').trim())
      || Boolean(String(floor?.zone_name || '').trim())
      || hasBmsStructureNodes(floor?.devices || [])
      || (floor?.rooms || []).some((room) => hasBmsStructureNodes(room?.devices || []))
    ))
  ))
);
const prepareBmsStructureNodesForSave = (devices = []) => (
  (devices || [])
    .filter((device) => device && typeof device === 'object')
    .map((device) => {
      const explicitNodeType = String(device.node_type || '').trim().toLowerCase();
      const children = prepareBmsStructureNodesForSave(getStructureNodeChildren(device));
      const inferredNodeType = ['device', 'field_device', 'network', 'network_panel', 'sub'].includes(explicitNodeType)
        ? explicitNodeType
        : ((device.address || device.part_number) ? 'device' : 'sub');
      const inferredDeviceType = String(device.device_type || '').trim()
        || (inferredNodeType === 'device' ? '' : String(device.name || '').trim() || 'Panel');
      return {
        id: device.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        boq_item_id: device.boq_item_id || '',
        part_number: device.part_number || '',
        name: String(device.name || '').trim() || inferredDeviceType || 'Sub',
        address: device.address || '',
        device_type: inferredDeviceType,
        node_type: inferredNodeType,
        zone_name: device.zone_name || '',
        controlled_by: device.controlled_by || '',
        children
      };
    })
);
export const prepareBmsStructureForSave = (structure) => ({
  ...(structure || {}),
  buildings: ((structure?.buildings || [])).map((building) => ({
    ...building,
    id: building.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: String(building.name || '').trim(), // BUG-010: blank → the server's 400 "Building name is required"
    floors: ((building?.floors || [])).map((floor) => ({
      ...floor,
      id: floor.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: String(floor.name || '').trim() || 'Floor',
      sub_type: String(floor.sub_type || '').trim() || 'Panel',
      zone_name: floor.zone_name || '',
      controlled_by: floor.controlled_by || '',
      devices: prepareBmsStructureNodesForSave(floor.devices || []),
      panels: normalizeStructurePanels(floor.panels || []),
      rooms: ((floor.rooms || [])).map((room) => ({
        ...room,
        devices: prepareBmsStructureNodesForSave(room.devices || []),
        panels: normalizeStructurePanels(room.panels || [])
      }))
    }))
  }))
});
const createDevice = (item = {}, address = '') => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  boq_item_id: item.id || '',
  part_number: item.part_number || '',
  name: item.description || item.name || item.part_number || 'Device',
  address,
  controlled_by: item.controlled_by || ''
});
const resolveStructureIds = (roomOrPanelId = null, panelId) => (
  panelId === undefined
    ? { roomId: null, panelId: roomOrPanelId }
    : { roomId: roomOrPanelId, panelId }
);
const getPanelSelectionKey = (buildingId, floorId, roomOrPanelId = null, panelId) => {
  const { roomId, panelId: resolvedPanelId } = resolveStructureIds(roomOrPanelId, panelId);
  return `${buildingId}::${floorId}::${roomId || 'floor'}::${resolvedPanelId || 'container'}`;
};
const getBmsSubSelectionKey = (buildingId, floorId, path = []) => `bms-select::${buildingId}::${floorId}::${path.join('::') || 'root'}`;
const createDeviceBatchForm = () => ({
  boq_item_ids: [],
  item_settings: {}
});
const flattenStructureDevices = (devices = []) => (
  (devices || []).flatMap((device) => (
    !device || typeof device !== 'object'
      ? []
      : [device, ...flattenStructureDevices(getStructureNodeChildren(device))]
  ))
);
const appendBmsNodeAtPath = (devices = [], path = [], node) => {
  if (!path.length) return [...devices, node];
  return (devices || []).map((device) => (
    device.id === path[0]
      ? { ...device, children: appendBmsNodeAtPath(getStructureNodeChildren(device), path.slice(1), node) }
      : device
  ));
};
const updateBmsNodeAtPath = (devices = [], path = [], updater) => (
  (devices || []).map((device) => {
    if (device.id !== path[0]) return device;
    if (path.length === 1) return updater(device);
    return { ...device, children: updateBmsNodeAtPath(getStructureNodeChildren(device), path.slice(1), updater) };
  })
);
export const cloneStructureDevice = (device, preserveAddresses = false) => ({
  ...device,
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  address: preserveAddresses ? (device.address || '') : '',
  children: getStructureNodeChildren(device).map((child) => cloneStructureDevice(child, preserveAddresses))
});
export const cloneStructurePanel = (panel, preserveAddresses = false) => ({
  ...panel,
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  devices: (panel.devices || []).map((device) => cloneStructureDevice(device, preserveAddresses))
});
export const cloneStructureRoom = (room, preserveAddresses = false) => ({
  ...room,
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  devices: (room.devices || []).map((device) => cloneStructureDevice(device, preserveAddresses)),
  panels: (room.panels || []).map((panel) => cloneStructurePanel(panel, preserveAddresses))
});
export const cloneStructureFloor = (floor, preserveAddresses = false) => ({
  ...floor,
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  devices: (floor.devices || []).map((device) => cloneStructureDevice(device, preserveAddresses)),
  panels: (floor.panels || []).map((panel) => cloneStructurePanel(panel, preserveAddresses)),
  rooms: (floor.rooms || []).map((room) => cloneStructureRoom(room, preserveAddresses))
});
export const cloneStructureBuilding = (building, preserveAddresses = false) => ({
  ...building,
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  floors: (building.floors || []).map((floor) => cloneStructureFloor(floor, preserveAddresses))
});
const cloneBmsNode = cloneStructureDevice;
const reorderSelectedItems = (items = [], selectedIds = [], direction = 'up') => {
  if (!selectedIds.length) return items;
  const selectedSet = new Set(selectedIds);
  const next = [...items];
  if (direction === 'up') {
    for (let index = 1; index < next.length; index += 1) {
      if (selectedSet.has(next[index].id) && !selectedSet.has(next[index - 1].id)) {
        [next[index - 1], next[index]] = [next[index], next[index - 1]];
      }
    }
    return next;
  }
  for (let index = next.length - 2; index >= 0; index -= 1) {
    if (selectedSet.has(next[index].id) && !selectedSet.has(next[index + 1].id)) {
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
    }
  }
  return next;
};
const hasDuplicateProjectDeviceAddress = (structure, address, excludedDeviceId = '') => {
  const normalizedAddress = String(address || '').trim();
  if (!normalizedAddress) return false;

  return (structure?.buildings || []).some((building) => (
    (building.floors || []).some((floor) => (
      flattenStructureDevices(floor.devices || []).some((device) => (
        device.id !== excludedDeviceId && String(device.address || '').trim() === normalizedAddress
      )) || (
      (floor.panels || []).some((panel) => (
        flattenStructureDevices(panel.devices || []).some((device) => (
          device.id !== excludedDeviceId && String(device.address || '').trim() === normalizedAddress
        ))
      ))
      ) || (
      (floor.rooms || []).some((room) => (
        flattenStructureDevices(room.devices || []).some((device) => (
          device.id !== excludedDeviceId && String(device.address || '').trim() === normalizedAddress
        )) || (
        (room.panels || []).some((panel) => (
          flattenStructureDevices(panel.devices || []).some((device) => (
            device.id !== excludedDeviceId && String(device.address || '').trim() === normalizedAddress
          ))
        ))
        )
      ))
      )
    ))
  ));
};
const collectImportedStructureIds = (structure) => {
  const ids = { buildings: [], floors: [], rooms: [], panels: [], devices: [] };
  (structure?.buildings || []).forEach((building) => {
    ids.buildings.push(building.id);
    (building.floors || []).forEach((floor) => {
      ids.floors.push(floor.id);
      flattenStructureDevices(floor.devices || []).forEach((device) => ids.devices.push(device.id));
      (floor.panels || []).forEach((panel) => {
        ids.panels.push(panel.id);
        flattenStructureDevices(panel.devices || []).forEach((device) => ids.devices.push(device.id));
      });
      (floor.rooms || []).forEach((room) => {
        ids.rooms.push(room.id);
        flattenStructureDevices(room.devices || []).forEach((device) => ids.devices.push(device.id));
        (room.panels || []).forEach((panel) => {
          ids.panels.push(panel.id);
          flattenStructureDevices(panel.devices || []).forEach((device) => ids.devices.push(device.id));
        });
      });
    });
  });
  return ids;
};
const downloadTextFile = (filename, content, mimeType = 'text/plain;charset=utf-8') => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};
const downloadBlobFile = (filename, blob) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};
const escapeXml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');
const buildStructureExportRows = (structure) => {
  const rows = [[
    'depth',
    'kind',
    'name',
    'building_name',
    'sub_type',
    'zone_name',
    'controlled_by',
    'panel_type',
    'node_type',
    'device_type',
    'part_number',
    'address'
  ]];

  const appendDevices = (devices, buildingName, depth) => {
    (devices || []).forEach((device) => {
      const normalizedNodeType = String(device?.node_type || '').trim().toLowerCase();
      const rowKind = ['sub', 'field_device', 'network', 'network_panel'].includes(normalizedNodeType) ? normalizedNodeType : 'device';
      rows.push([
        String(depth),
        rowKind,
        device?.name || '',
        buildingName,
        '',
        device?.zone_name || '',
        device?.controlled_by || '',
        '',
        device?.node_type || '',
        device?.device_type || '',
        device?.part_number || '',
        device?.address || ''
      ]);
      appendDevices(getStructureNodeChildren(device), buildingName, depth + 1);
    });
  };

  (structure?.buildings || []).forEach((building) => {
    const buildingName = building?.name || 'Building';
    rows.push(['0', 'building', buildingName, buildingName, '', '', '', '', '', '', '', '']);

    (building?.floors || []).forEach((floor) => {
      const floorKind = (floor?.sub_type || floor?.zone_name) ? 'root_sub' : 'floor';
      rows.push([
        '1',
        floorKind,
        floor?.name || 'Floor',
        buildingName,
        floor?.sub_type || '',
        floor?.zone_name || '',
        floor?.controlled_by || '',
        '',
        '',
        '',
        '',
        ''
      ]);

      appendDevices(floor?.devices || [], buildingName, 2);

      (floor?.panels || []).forEach((panel) => {
        rows.push([
          '2',
          'panel',
          panel?.name || 'Panel',
          buildingName,
          '',
          '',
          '',
          panel?.type || '',
          '',
          '',
          '',
          ''
        ]);
        appendDevices(panel?.devices || [], buildingName, 3);
      });

      (floor?.rooms || []).forEach((room) => {
        rows.push(['2', 'room', room?.name || 'Room', buildingName, '', '', '', '', '', '', '', '']);
        appendDevices(room?.devices || [], buildingName, 3);
        (room?.panels || []).forEach((panel) => {
          rows.push([
            '3',
            'panel',
            panel?.name || 'Panel',
            buildingName,
            '',
            '',
            '',
            panel?.type || '',
            '',
            '',
            '',
            ''
          ]);
          appendDevices(panel?.devices || [], buildingName, 4);
        });
      });
    });
  });

  return rows;
};
const exportStructureExcelClientSide = (structure, projectName) => {
  const rows = buildStructureExportRows(structure);
  const xmlRows = rows.map((row) => `
    <Row>
      ${row.map((cell) => `<Cell><Data ss:Type="String">${escapeXml(cell)}</Data></Cell>`).join('')}
    </Row>
  `).join('');

  const workbook = `<?xml version="1.0"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
  <Worksheet ss:Name="Structure">
    <Table>
      ${xmlRows}
    </Table>
  </Worksheet>
</Workbook>`;

  const safeName = String(projectName || 'project').replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'project';
  downloadTextFile(`${safeName}-structure.xls`, workbook, 'application/vnd.ms-excel;charset=utf-8');
};
const formatStructureDeviceCountsTooltip = (label, counts) => {
  if (!counts.length) {
    return `No devices in this ${label.toLowerCase()}.`;
  }

  const total = counts.reduce((sum, item) => sum + item.count, 0);
  return [
    `${label} device totals (${total})`,
    ...counts.map((item) => `${item.name}: ${item.count}`)
  ].join('\n');
};
// Export / Print dates follow the workspace date format and time zone (Admin › Settings), like the rest of the app.
const formatDateLabel = (value) => (value ? formatDate(value, { fallback: '-' }) : '-');

const EXECUTION_COLUMNS_STORAGE_KEY = 'awadypm.executionColumns';
const TIMELINE_VIEW_DEFAULTS = { zoom: 'month', group: 'task' };
const TIMELINE_GROUP_LABELS = { task: 'main task', category: 'category', building: 'building' };
// What a saved Execution view captures (the search box is not part of a view).
const EXECUTION_VIEW_DEFAULTS = { status: [], category: [], building: [], doneBy: [], monitoring: '', comments: false };
const EXECUTION_FIELD_LABELS = {
  status: 'Status', category: 'Category', start_date: 'Start date', due_date: 'Due date',
  done_by: 'Done by', checked_by: 'Checked by', monitoring: 'Monitoring', comments: 'Comments',
};
const formatExecutionFieldValue = (field, value) => {
  if (field === 'monitoring') return value ? 'On' : 'Off';
  if (field === 'start_date' || field === 'due_date') return value ? formatDate(value) : 'no date';
  const text = String(value ?? '').trim();
  return text || 'empty';
};
const onedriveAccountLabel = (status) => (status?.connected ? (status.user_email || status.user_name || 'Microsoft account') : '');

// projectId → { tab, epoch }: the last open tab, restored only after an Undo remount (see activeTab).
const lastProjectTabMemory = new Map();
const PROJECT_TAB_LABELS = {
  stages: 'Stages', material: 'Material', files: 'Files', invoicing: 'Invoicing', structure: 'Project Structure',
  execution: 'Execution', timeline: 'Timeline', variations: 'Variations',
};
// ?tab= values other pages link with (Dashboard, Tasks, Documents, AI, Invoicing) → the tab's value here.
const PROJECT_TAB_ALIASES = { project_structure: 'structure', 'project-structure': 'structure' };
const projectTabFromParam = (value) => {
  const key = String(value || '').trim().toLowerCase();
  return PROJECT_TAB_ALIASES[key] || key;
};

export default function ProjectDetailPage() {
  useAppleProjectTheme();
  const { id } = useParams();
  const { user } = useAuth();
  const canAccessInvoicing = hasInvoicingAccess(user);
  // Owner rule 13 Oct 2026: money — the contract / original scope value, payment terms, VO values and their payment
  // terms, "% of contract", invoices — only for roles with Invoicing (the user's own permission list; the server
  // leaves those fields out for everyone else and ignores them in their saves).
  const canViewFinance = canAccessInvoicing;
  const canViewStages = hasPermission(user, 'stages');
  const canViewMaterial = hasPermission(user, 'material');
  const canViewFiles = hasPermission(user, 'files');
  const canViewVariations = hasPermission(user, 'variations');
  const canViewProjectStructure = hasPermission(user, 'project_structure');
  const canViewExecution = hasPermission(user, 'execution');
  const canViewTimeline = hasPermission(user, 'timeline');
  /*
    28 #8 (Admin › Access View / Edit levels): every write button on this page follows the module the server
    checks for that write (permission_levels.project_tab_module, PROJECT_FIELD_MODULES in routes/projects.py).
    canEdit() is false for a module the role may only VIEW, for Viewer accounts and for a module the role cannot
    open, so a View-only tab HIDES its buttons instead of offering clicks the server refuses.
  */
  const canEditProject = canEdit(user, 'projects'); // PUT /projects/{id}: details, status, handover date
  const canEditStages = canEdit(user, 'stages'); // /projects/{id}/stages/…
  const canEditMaterial = canEdit(user, 'material'); // BOQ, delivery notes, site requests, quantities
  const canEditFiles = canEdit(user, 'files'); // OneDrive connect / disconnect, create folders, upload
  const canEditStructure = canEdit(user, 'project_structure'); // /projects/{id}/execution-structure…
  const canEditExecutionModule = canEdit(user, 'execution'); // execution template + tasks, site mode
  const canEditTimeline = canEdit(user, 'timeline'); // /projects/{id}/schedule/apply
  const canEditInvoices = canEdit(user, 'invoicing'); // invoices, variation invoices, QuickBooks sync
  const canCreateTasks = canEdit(user, 'tasks'); // POST /tasks ("Create task" / "Add task" from a tab)
  const defaultProjectTab = [
    canViewStages && 'stages',
    canViewMaterial && 'material',
    canViewFiles && 'files',
    canAccessInvoicing && 'invoicing',
    canViewProjectStructure && 'structure',
    canViewExecution && 'execution',
    canViewTimeline && 'timeline',
    canViewVariations && 'variations',
  ].find(Boolean) || 'stages';
  const confirm = useConfirm();
  const undoEpoch = useUndo()?.undoEpoch ?? 0;
  const canViewHistory = isAdminLike(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabVisible = {
    stages: canViewStages,
    material: canViewMaterial,
    files: canViewFiles,
    invoicing: canAccessInvoicing,
    structure: canViewProjectStructure,
    execution: canViewExecution,
    timeline: canViewTimeline,
    variations: canViewVariations,
  };
  // Tabs are controlled so other tabs can link here (Timeline → Execution, Variations → Invoicing).
  // An Undo from the toast remounts the page (Layout keys it by undoEpoch): reopen the tab the user was on.
  // Otherwise a link's ?tab= (e.g. /projects/:id?tab=invoicing) opens that tab when this role can see it.
  const [activeTab, setActiveTab] = useState(() => {
    const remembered = lastProjectTabMemory.get(id);
    if (remembered && remembered.epoch !== undoEpoch) return remembered.tab;
    const requested = projectTabFromParam(searchParams.get('tab'));
    return tabVisible[requested] ? requested : defaultProjectTab;
  });
  useEffect(() => {
    lastProjectTabMemory.set(id, { tab: activeTab, epoch: undoEpoch });
  }, [activeTab, id, undoEpoch]);
  // QuickBooks Desktop, read only (owner decision 9 Oct 2026): the project's QuickBooks side, read once Invoicing is
  // opened; null while the integration is off / not read yet / the request fails (the tab is then as before).
  const qbDesktop = useQbDesktopProject(id, { enabled: canAccessInvoicing && activeTab === 'invoicing' });
  // The open tab lives in the URL too, so a reload or a copied link keeps it.
  useEffect(() => {
    if (projectTabFromParam(searchParams.get('tab')) === activeTab) return;
    // A view-state write, not leaving the page: never "Leave without saving?" for it.
    withoutLeaveGuard(() => setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('tab', activeTab);
      return next;
    }, { replace: true }));
  }, [activeTab, searchParams, setSearchParams]);
  const [project, setProject] = useState(null);
  // Top bar: "Projects / LIV Waterside / Stages" (the project name once it has loaded).
  useBreadcrumbs([project?.name || null, PROJECT_TAB_LABELS[activeTab]]);
  const [executionStructure, setExecutionStructure] = useState(createExecutionStructure());
  const [executionTemplate, setExecutionTemplate] = useState([]);
  const [executionTasks, setExecutionTasks] = useState([]);
  // Latest tasks for handlers that run after an await (confirm) or on blur.
  const executionTasksRef = useRef(executionTasks);
  executionTasksRef.current = executionTasks;
  // `${taskId}:${field}` → { value, updated } the parent had when a commit-on-blur edit started.
  const [projectFieldOptions, setProjectFieldOptions] = useState({
    clients: [],
    consultants: [],
    contractors: [],
    systemTypes: []
  });
  const [invoices, setInvoices] = useState([]);
  const [variationInvoices, setVariationInvoices] = useState([]);
  const [invoiceScope, setInvoiceScope] = useState('base');
  const [variations, setVariations] = useState([]);
  const [boqItems, setBoqItems] = useState([]);
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [siteRequests, setSiteRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stageLinesByStage, setStageLinesByStage] = useState({});
  // The old page-wide "Edit" toggle is gone (04 #4): Stages / Material / Invoicing are read-first with drawers or
  // a save bar. Project Structure still reads this flag (always on) until its own read mode lands.
  const [isStageLinesEditMode] = useState(true);
  const [isMaterialEditMode, setIsMaterialEditMode] = useState(true);
  const [collapsedMaterialSubBoqs, setCollapsedMaterialSubBoqs] = useState({});
  const [collapsedExecutionParents, setCollapsedExecutionParents] = useState({});
  const [collapsedStructureSections, setCollapsedStructureSections] = useState({});
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [executionTemplateDialogOpen, setExecutionTemplateDialogOpen] = useState(false);
  const [addBooqDialogOpen, setAddBooqDialogOpen] = useState(false);
  const [siteNeedsDialogOpen, setSiteNeedsDialogOpen] = useState(false);
  const [addDnDialogOpen, setAddDnDialogOpen] = useState(false);
  const [editingDeliveryNoteId, setEditingDeliveryNoteId] = useState(null);
  const [siteRequestDialogOpen, setSiteRequestDialogOpen] = useState(false);
  const [deviceBatchDialogOpen, setDeviceBatchDialogOpen] = useState(false);
  const [deviceBatchSearch, setDeviceBatchSearch] = useState('');
  // Execution filters (09 #3) live in the URL (?ex.status=Done …): a reload or a copied link keeps the view.
  const [executionFilter, setExecutionFilter, { isDefault: executionFilterPristine }] = useUrlState(EXECUTION_FILTER_DEFAULTS, { prefix: 'ex.' });
  // 09 #9: the search box keeps its own text (ExecutionFilterBar) and hands ?ex.q over after a 250 ms pause: a
  // keystroke re-renders the filter bar only, never this page or the 2,500-row filter. The list filters on a deferred copy.
  const deferredExecutionQuery = useDeferredValue(executionFilter.q || '');
  const clearExecutionFilters = () => setExecutionFilter({ ...EXECUTION_FILTER_DEFAULTS });
  // Timeline zoom + grouping (10 #6), in the URL too (?tl.zoom=week&tl.group=building).
  const [timelineView, setTimelineView] = useUrlState(TIMELINE_VIEW_DEFAULTS, { prefix: 'tl.' });
  // 09 #7: Table | Board | Matrix (in the URL: ?ex.view=board); 09 #8: phones open in site mode.
  const [executionViewParam, setExecutionView] = useUrlParam('ex.view', 'table');
  const executionView = ['table', 'board', 'matrix'].includes(executionViewParam) ? executionViewParam : 'table';
  const [executionMatrixMetric, setExecutionMatrixMetric] = useState('done');
  const [phoneExecutionView, setPhoneExecutionView] = useState('site');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogOptions, setCatalogOptions] = useState([]);
  const [selectedCatalog, setSelectedCatalog] = useState({});
  const [materialPhoto, setMaterialPhoto] = useState(null);
  const [siteRequestTarget, setSiteRequestTarget] = useState(null);
  const [deviceBatchTarget, setDeviceBatchTarget] = useState(null);
  const [selectedStructureDevices, setSelectedStructureDevices] = useState({});
  const [selectedBmsSubs, setSelectedBmsSubs] = useState({});
  const [copiedBmsSubs, setCopiedBmsSubs] = useState([]);
  const [deviceBatchForm, setDeviceBatchForm] = useState(createDeviceBatchForm());
  const [draggingStructureDevice, setDraggingStructureDevice] = useState(null);
  const [draggingBmsSub, setDraggingBmsSub] = useState(null);
  const [draggingBmsRootSub, setDraggingBmsRootSub] = useState(null);
  const [bmsSubDragMode, setBmsSubDragMode] = useState(false);
  const [ttlImportErrorLog, setTtlImportErrorLog] = useState('');
  const [highlightedStructureIds, setHighlightedStructureIds] = useState({ buildings: [], floors: [], rooms: [], panels: [], devices: [] });
  const [materialSiteNeeds, setMaterialSiteNeeds] = useState([]);
  const [siteNeedsDrafts, setSiteNeedsDrafts] = useState({});
  const [newCatalogForm, setNewCatalogForm] = useState({ part_number: '', description: '', unit: 'pcs', category: '', brand: '' });
  const [boqPasteText, setBoqPasteText] = useState('');
  const [boqSubGroupDraft, setBoqSubGroupDraft] = useState('');
  const [deliveryPasteText, setDeliveryPasteText] = useState('');
  const [subBoqQtyDrafts, setSubBoqQtyDrafts] = useState({});
  const [draggingBoqItemId, setDraggingBoqItemId] = useState(null);
  const [selectedBoqRowIds, setSelectedBoqRowIds] = useState([]);
  const [siteRequestForm, setSiteRequestForm] = useState(createSiteRequestForm());
  const boqSelectionAnchorRef = useRef(null);
  const boqSelectionInteractionRef = useRef({ shiftKey: false });
  const structureSelectionAnchorRef = useRef({});
  const structureSelectionInteractionRef = useRef({ shiftKey: false });
  const [selectedExecutionTaskIds, setSelectedExecutionTaskIds] = useState([]);
  const [deliveryForm, setDeliveryForm] = useState({
    dn_number: '',
    delivery_date: '',
    supplier: '',
    received_by: '',
    lpo_id: '',
    delivery_type: 'to_stock',
    sub_boq: '',
    notes: '',
    line_qty: {}
  });
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskForm, setTaskForm] = useState(createEmptyProjectTaskForm);
  const [projectTasks, setProjectTasks] = useState([]);
  const [platformUsers, setPlatformUsers] = useState([]);
  const [invoiceDrafts, setInvoiceDrafts] = useState({});
  const [filesPathDraft, setFilesPathDraft] = useState('');
  const [filesAudit, setFilesAudit] = useState(null);
  const [filesAuditLoading, setFilesAuditLoading] = useState(false);
  const [onedriveAuthLoading, setOnedriveAuthLoading] = useState(false);
  const [structureSaving, setStructureSaving] = useState(false);
  // Structure, wave 2 (screen 08): read mode (rename / edit one device at a time), search focus, import wizard.
  const [renamingStructureId, setRenamingStructureId] = useState(null);
  const [editingStructureDevice, setEditingStructureDevice] = useState(null);
  const [structureFocusId, setStructureFocusId] = useState(null);
  const [structureImportOpen, setStructureImportOpen] = useState(false);
  const [structureSaveStatus, setStructureSaveStatus] = useState({ status: 'idle', error: '' });
  const [duplicateAddressSettingSaving, setDuplicateAddressSettingSaving] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const isPhone = useIsPhone();
  // "All project details" open / closed is remembered per browser (a view preference, not data).
  const [detailsOpen, setDetailsOpen] = useState(() => Boolean(readStored(storageKey('project', 'detailsOpen'), false)));
  const handleDetailsOpenChange = (open) => {
    setDetailsOpen(open);
    writeStored(storageKey('project', 'detailsOpen'), open);
  };
  // Stage drawer (04 #4): which stage, and whether it opens with a new empty item / on one item.
  const [stagePanel, setStagePanel] = useState({ open: false, stageId: null, startWith: null, focusLineId: null });
  // Invoicing (07): drawer, selection, search, QuickBooks status and a running bulk sync.
  const [invoicePanel, setInvoicePanel] = useState({ open: false, mode: 'edit', invoiceId: null, focus: null });
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [invoiceQuery, setInvoiceQuery] = useState('');
  const [quickBooksStatus, setQuickBooksStatus] = useState(null);
  const [qbSync, setQbSync] = useState(null);
  const [highlightedStageId, setHighlightedStageId] = useState(null);
  const [boqDialogFocus, setBoqDialogFocus] = useState(null);
  const [endDateDialogOpen, setEndDateDialogOpen] = useState(false);
  const [projectStatusSaving, setProjectStatusSaving] = useState(false);
  const [executionColumns, setExecutionColumns] = useState(() => {
    try {
      return normalizeExecutionColumns(JSON.parse(localStorage.getItem(EXECUTION_COLUMNS_STORAGE_KEY) || 'null'));
    } catch {
      return normalizeExecutionColumns(null);
    }
  });
  const boqExcelInputRef = useRef(null);
  const stageHighlightTimerRef = useRef(null);
  const [customBmsSubTypes, setCustomBmsSubTypes] = useState([]);
  const structureSaveRequestSeqRef = useRef(0);
  const [projectForm, setProjectForm] = useState({
    name: '',
    client: '',
    consultant: '',
    contractor: '',
    project_engineer: '',
    senior_project_engineer: '',
    location: '',
    waze_url: '',
    system_type: '',
    scope: [],
    tags: [],
    status: 'Active',
    start_date: '',
    end_date: '',
    notes: '',
    total_project_value: '',
    original_scope_value: '',
    payment_terms: '',
    salesman: '',
    files_root_path: '',
    custom_fields: [],
    allow_sub_boq: false,
    allow_duplicate_device_addresses: false
  });
  const isBmsProject = isBmsSystemType(project?.system_type || projectForm.system_type);
  const allowSubBoq = Boolean(project?.allow_sub_boq || projectForm.allow_sub_boq);
  const allowDuplicateDeviceAddresses = Boolean(project?.allow_duplicate_device_addresses);

  // Data loading (wave 2, screen 04 idea 2): the page renders as soon as the project arrives (its
  // `overview` feeds the header and the tab counts); every tab loads its own records when it is opened
  // (TAB_RESOURCES), with a skeleton meanwhile. fetchData() refreshes the project and every record set
  // that has been loaded so far — the existing callers after a save keep working.
  const [projectOverview, setProjectOverview] = useState(null);
  const [resourceStatus, setResourceStatus] = useState({});
  const resourceStatusRef = useRef({});
  const setResourceStatusFor = useCallback((keys, value) => {
    const next = { ...resourceStatusRef.current };
    keys.forEach((key) => { next[key] = value; });
    resourceStatusRef.current = next;
    setResourceStatus(next);
  }, []);

  // A string, so the loaders below keep their identity across renders (the user object may not).
  const unreadableResources = Object.keys(RESOURCE_PERMISSIONS).filter((key) => !canReadResource(key, user)).join(',');
  const fetchResource = useCallback(async (key) => {
    switch (key) {
      case 'boq': {
        const res = await api.get('/materials/boq-items', { params: { project_id: id } });
        setBoqItems(res.data || []);
        return;
      }
      case 'deliveryNotes': {
        const res = await api.get('/delivery-notes', { params: { project_id: id } });
        setDeliveryNotes(res.data?.items || res.data || []);
        return;
      }
      case 'siteRequests': {
        const res = await api.get('/site-requests', { params: { project_id: id } });
        setSiteRequests(res.data || []);
        return;
      }
      case 'variations': {
        const res = await api.get('/variations', { params: { project_id: id } });
        setVariations(res.data || []);
        return;
      }
      case 'tasks': {
        const res = await api.get('/tasks', { params: { project_id: id } });
        setProjectTasks(res.data?.items || res.data || []);
        return;
      }
      case 'invoices': {
        if (!canAccessInvoicing) { setInvoices([]); return; }
        const res = await api.get('/invoices', { params: { project_id: id } });
        setInvoices(res.data || []);
        return;
      }
      case 'variationInvoices': {
        if (!canAccessInvoicing) { setVariationInvoices([]); return; }
        const res = await api.get('/variation-invoices', { params: { project_id: id } });
        setVariationInvoices(res.data || []);
        return;
      }
      case 'fieldOptions': {
        const res = await api.get('/projects/field-options');
        const fieldOptions = res.data || {};
        setProjectFieldOptions({
          clients: Array.isArray(fieldOptions.clients) ? fieldOptions.clients : [],
          consultants: Array.isArray(fieldOptions.consultants) ? fieldOptions.consultants : [],
          contractors: Array.isArray(fieldOptions.contractors) ? fieldOptions.contractors : [],
          systemTypes: Array.isArray(fieldOptions.systemTypes) ? fieldOptions.systemTypes : [],
          tags: Array.isArray(fieldOptions.tags) ? fieldOptions.tags : [],
        });
        return;
      }
      case 'users': {
        const res = await api.get('/admin/users');
        setPlatformUsers(res.data || []);
        return;
      }
      case 'qbStatus': {
        const res = await api.get('/quickbooks/status');
        setQuickBooksStatus(res.data || null);
        return;
      }
      default:
    }
  }, [canAccessInvoicing, id]);

  /** Load record sets that are not loaded yet (refresh: load them again). Resolves when all settled. */
  const ensureResources = useCallback(async (keys = [], { refresh = false, quiet = false } = {}) => {
    const pending = [...new Set(keys)].filter((key) => refresh || !resourceStatusRef.current[key]);
    // Record sets the role may not read are never requested (no 403, no error toast); they stay empty.
    const denied = new Set(unreadableResources.split(',').filter(Boolean));
    const skipped = pending.filter((key) => denied.has(key));
    if (skipped.length) setResourceStatusFor(skipped, 'skipped');
    const wanted = pending.filter((key) => !denied.has(key));
    if (!wanted.length) return;
    if (!refresh) setResourceStatusFor(wanted, 'loading');
    const results = await Promise.allSettled(wanted.map((key) => fetchResource(key)));
    const failed = wanted.filter((key, index) => results[index].status === 'rejected');
    const loaded = wanted.filter((key, index) => results[index].status === 'fulfilled');
    if (loaded.length) setResourceStatusFor(loaded, 'ready');
    if (failed.length) {
      setResourceStatusFor(failed, 'error');
      // Optional lists (users, field options, QuickBooks status) fail quietly for roles that cannot read them.
      if (!quiet && failed.some((key) => !['users', 'fieldOptions', 'qbStatus'].includes(key))) {
        toast.error('Some project sections failed to load');
      }
    }
  }, [fetchResource, setResourceStatusFor, unreadableResources]);

  const initialLoadRef = useRef(true);
  const fetchData = useCallback(async () => {
    const initial = initialLoadRef.current;
    initialLoadRef.current = false;
    try {
      const res = await api.get(`/projects/${id}`);
      setProject(res.data);
      setProjectOverview(res.data?.overview || null);
    } catch {
      setProject(null);
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
    // The first load runs next to the open tab's own loading (effect below); later calls refresh what is loaded.
    if (initial) return;
    const loaded = Object.keys(resourceStatusRef.current);
    if (loaded.length) await ensureResources(loaded, { refresh: true });
  }, [ensureResources, id]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // The open tab's records, fetched when the tab is first opened (in parallel with the project on page load).
  useEffect(() => {
    void ensureResources(TAB_RESOURCES[activeTab] || []);
  }, [activeTab, ensureResources]);
  useEffect(() => () => window.clearTimeout(stageHighlightTimerRef.current), []);

  useEffect(() => {
    try {
      localStorage.setItem(EXECUTION_COLUMNS_STORAGE_KEY, JSON.stringify(executionColumns));
    } catch {
      // Private mode / storage full: the choice simply is not remembered.
    }
  }, [executionColumns]);

  useEffect(() => {
    if (!addBooqDialogOpen || !boqDialogFocus) return undefined;
    const timer = window.setTimeout(() => {
      const field = document.querySelector(`[data-boq-dialog-focus="${boqDialogFocus}"]`);
      if (field) {
        field.scrollIntoView({ block: 'center' });
        field.focus();
      }
      setBoqDialogFocus(null);
    }, 150);
    return () => window.clearTimeout(timer);
  }, [addBooqDialogOpen, boqDialogFocus]);

  useEffect(() => {
    try {
      const rawValue = localStorage.getItem(CUSTOM_BMS_SUB_TYPES_STORAGE_KEY);
      if (!rawValue) return;
      const parsed = JSON.parse(rawValue);
      if (!Array.isArray(parsed)) return;
      setCustomBmsSubTypes(
        Array.from(new Set(parsed.map((item) => String(item || '').trim()).filter(Boolean)))
      );
    } catch {
      setCustomBmsSubTypes([]);
    }
  }, []);

  const registerCustomBmsSubType = (value) => {
    const trimmedValue = String(value || '').trim();
    if (!trimmedValue) return trimmedValue;
    setCustomBmsSubTypes((prev) => {
      const existingValue = prev.find((item) => item.toLowerCase() === trimmedValue.toLowerCase());
      const next = existingValue ? prev : [...prev, trimmedValue];
      localStorage.setItem(CUSTOM_BMS_SUB_TYPES_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
    return trimmedValue;
  };

  // Local edit state (stage lines, execution, structure, site needs) is reset only when ITS source on the
  // project changes — not on every project update — so saving the status, end date or folder path (which
  // merge just those fields, see mergeSavedProjectFields) never drops unsaved Execution / Structure edits.
  const loadedProjectId = project?.id;
  const projectStages = project?.stages;
  const projectExecutionStructure = project?.execution_structure;
  const projectExecutionTemplate = project?.execution_template;
  const projectExecutionTasks = project?.execution_tasks;
  const projectMaterialSiteNeeds = project?.material_site_needs;
  const projectFilesRootPath = project?.files_root_path;

  useEffect(() => {
    if (!projectStages) return;
    const stageLines = {};
    projectStages.forEach((stage) => {
      stageLines[stage.id] = normalizeLines(stage.lines);
    });
    setStageLinesByStage(stageLines);
  }, [projectStages]);

  useEffect(() => {
    if (!project) return;
    // An open "Edit details" drawer keeps what is being typed; it gets a fresh copy when it opens.
    if (projectDialogOpenRef.current) return;
    setProjectForm(projectToForm(project));
  }, [project]);
  const projectDialogOpenRef = useRef(false);
  projectDialogOpenRef.current = projectDialogOpen;
  // "Edit details" drawer (04 #1): how many fields differ from the saved project (its unsaved-changes count).
  const projectFormDirtyCount = useMemo(() => {
    if (!projectDialogOpen || !project) return 0;
    const saved = projectToForm(project);
    const normalizeValue = (key, value) => (key === 'custom_fields'
      ? JSON.stringify((value || []).map((field) => [field.title || '', field.content || '']))
      : JSON.stringify(value ?? ''));
    return Object.keys(saved).filter((key) => normalizeValue(key, saved[key]) !== normalizeValue(key, projectForm[key])).length;
  }, [projectDialogOpen, project, projectForm]);

  useEffect(() => {
    if (!loadedProjectId) return;
    setFilesPathDraft(projectFilesRootPath || '');
    setFilesAudit(null);
  }, [loadedProjectId, projectFilesRootPath]);

  useEffect(() => {
    if (!loadedProjectId) return;
    setExecutionStructure(normalizeStructurePanelTypes(projectExecutionStructure || createExecutionStructure()));
  }, [loadedProjectId, projectExecutionStructure]);

  useEffect(() => {
    if (!loadedProjectId) return;
    setExecutionTemplate(projectExecutionTemplate || []);
  }, [loadedProjectId, projectExecutionTemplate]);

  useEffect(() => {
    if (!loadedProjectId) return;
    setExecutionTasks(projectExecutionTasks || []);
  }, [loadedProjectId, projectExecutionTasks]);

  useEffect(() => {
    if (!loadedProjectId) return;
    setMaterialSiteNeeds(projectMaterialSiteNeeds || []);
  }, [loadedProjectId, projectMaterialSiteNeeds]);

  /**
   * A partial project save → page state: only `fields` (+ updated_at) change, every other field keeps its
   * object identity, so the reset effects above leave local Execution / Structure / stage edits alone.
   * `requested` (what was sent) fills in a field the response leaves out.
   */
  const mergeSavedProjectFields = (saved, fields, requested = {}) => {
    const source = { ...requested, ...(saved && typeof saved === 'object' ? saved : {}) };
    setProject((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      [...fields, 'updated_at'].forEach((field) => {
        if (Object.prototype.hasOwnProperty.call(source, field)) next[field] = source[field];
      });
      return next;
    });
  };

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type !== 'onedrive-auth-complete') return;
      if (event.data?.projectId !== id) return;

      setOnedriveAuthLoading(false);
      if (event.data?.success) {
        toast.success('OneDrive connected');
        fetchData();
        return;
      }
      toast.error(event.data?.message || 'Failed to connect OneDrive');
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [fetchData, id]);

  useEffect(() => {
    setCollapsedExecutionParents((prev) => {
      const parentIds = new Set(executionTasks.filter((task) => task.parent_id).map((task) => task.parent_id));
      const next = {};
      executionTasks
        .filter((task) => parentIds.has(task.id))
        .forEach((task) => {
          next[task.id] = prev[task.id] ?? true;
        });
      return next;
    });
  }, [executionTasks]);

  useEffect(() => {
    setCollapsedStructureSections((prev) => {
      const next = {};
      const registerBmsSubKeys = (buildingId, floorId, devices = [], path = []) => {
        (devices || []).forEach((device) => {
          const nodePath = [...path, device.id];
          const childNodes = getStructureNodeChildren(device);
          if (childNodes.length > 0) {
            const subKey = getBmsSubCollapseKey(buildingId, floorId, nodePath);
            next[subKey] = prev[subKey] ?? true;
            registerBmsSubKeys(buildingId, floorId, childNodes, nodePath);
          }
        });
      };
      (executionStructure.buildings || []).forEach((building) => {
        const buildingKey = getBuildingCollapseKey(building.id);
        next[buildingKey] = prev[buildingKey] ?? true;
        (building.floors || []).forEach((floor) => {
          const floorKey = getFloorCollapseKey(building.id, floor.id);
          next[floorKey] = prev[floorKey] ?? true;
          registerBmsSubKeys(building.id, floor.id, floor.devices || []);
          (floor.panels || []).forEach((panel) => {
            const panelKey = getPanelCollapseKey(building.id, floor.id, panel.id);
            next[panelKey] = prev[panelKey] ?? true;
          });
          (floor.rooms || []).forEach((room) => {
            const roomKey = getRoomCollapseKey(building.id, floor.id, room.id);
            next[roomKey] = prev[roomKey] ?? true;
            (room.panels || []).forEach((panel) => {
              const panelKey = getPanelCollapseKey(building.id, floor.id, room.id, panel.id);
              next[panelKey] = prev[panelKey] ?? true;
            });
          });
        });
      });
      return next;
    });
  }, [executionStructure]);

  const selectedVariation = useMemo(
    () => variations.find((variation) => variation.id === invoiceScope) || null,
    [invoiceScope, variations]
  );

  const {
    scopes: paymentScopes, scopedInvoices, scopeTotalValue,
  } = useMemo(() => selectPaymentScopes(project, variations, invoices, variationInvoices, invoiceScope),
    [project, variations, invoices, variationInvoices, invoiceScope]);

  useEffect(() => {
    if (invoiceScope === 'base' || invoiceScope === 'total') return;
    if (!variations.some((variation) => variation.id === invoiceScope)) {
      setInvoiceScope('base');
    }
  }, [invoiceScope, variations]);

  const selectedScopeLabel = invoiceScope === 'base' ? 'Selected Scope: Base Scope'
    : invoiceScope === 'total' ? 'Selected Scope: Total Project (Base + Approved Variations)'
      : `Selected Variation: ${selectedVariation?.vo_number || 'Variation'} (${selectedVariation?.status || 'Proposed'})`;

  useEffect(() => {
    const drafts = {};
    scopedInvoices.forEach((inv) => {
      drafts[inv.id] = {
        invoice_type: normalizeInvoiceTypeValue(inv.invoice_type) || 'Advanced payment',
        invoice_type_percentages: normalizeInvoiceTypePercentages(inv.invoice_type_percentages),
        status: inv.status || 'Not Started',
        amount: inv.amount ?? 0,
        reference: inv.reference || '',
        note: inv.note || ''
      };
    });
    setInvoiceDrafts(drafts);
  }, [scopedInvoices]);

  const invoiceSummary = useMemo(
    () => summarizePaymentScopes(paymentScopes, invoiceDrafts),
    [paymentScopes, invoiceDrafts]
  );

  // Whole-contract money bar (base + approved VOs), independent of the selected scope.
  const contractMoneyBar = useMemo(() => {
    const base = selectPaymentScopes(project, variations, invoices, variationInvoices, 'base');
    const total = selectPaymentScopes(project, variations, invoices, variationInvoices, 'total');
    const approvedVariationScopes = total.scopes.slice(1);
    return buildMoneyBar({
      baseValue: base.originalScopeValue,
      variationsValue: total.approvedVariationsTotal,
      base: summarizePaymentScopes(base.scopes, invoiceDrafts),
      variations: summarizePaymentScopes(approvedVariationScopes, invoiceDrafts),
    });
  }, [project, variations, invoices, variationInvoices, invoiceDrafts]);
  // Overview numbers used by several tabs (memoised: the page re-renders on every keystroke).
  const structureSummary = useMemo(() => summarizeStructure(executionStructure), [executionStructure]);
  // 08 #4: what Save structure would change, against the saved structure (same normalisation as the editor).
  const savedStructure = useMemo(
    () => normalizeStructurePanelTypes(projectExecutionStructure || createExecutionStructure()),
    [projectExecutionStructure]
  );
  const structureIsBms = isBmsSystemType(project?.system_type);
  const structureDiff = useMemo(
    () => diffStructure(savedStructure, executionStructure, { isBms: structureIsBms }),
    [savedStructure, executionStructure, structureIsBms]
  );
  const structureDirtyCount = loadedProjectId ? structureDiff.count : 0;
  const { markClean: markStructureClean } = useUnsavedChanges(structureDirtyCount > 0, {
    title: `You have ${structureDirtyCount} unsaved structure change${structureDirtyCount === 1 ? '' : 's'}`,
    description: 'Your Project Structure edits are not saved. Leave without saving them?',
    confirmLabel: 'Discard changes',
    cancelLabel: 'Keep editing',
  });
  // 08 #5: search index (built only while the Structure tab is open).
  const structureIndex = useMemo(
    () => (activeTab === 'structure' ? buildStructureIndex(executionStructure, { isBms: structureIsBms }) : []),
    [activeTab, executionStructure, structureIsBms]
  );
  // 08 #1: the node open in the right pane of the two-pane editor (none = the project overview).
  // 08 #1: the chosen building / floor / panel is in the URL (?st.node=…): a reload or a shared link keeps it.
  const [structureNodeParam, setStructureNodeParam] = useUrlParam('st.node', '');
  const structureSelectedId = structureNodeParam || null;
  const setStructureSelectedId = useCallback((nodeId) => setStructureNodeParam(nodeId || ''), [setStructureNodeParam]);
  // 08 #6: addresses really used more than once (KNX / LCS devices).
  const structureAddressConflicts = useMemo(
    () => (activeTab === 'structure' ? structureConflicts(findDuplicateAddresses(structureIndex)) : []),
    [activeTab, structureIndex]
  );
  // 08 #8: what "Save structure" would do to Execution (a dry run on the server, nothing is saved).
  const [structureImpact, setStructureImpact] = useState(null);
  useEffect(() => {
    if (activeTab !== 'structure' || !structureDirtyCount || !canViewProjectStructure) {
      setStructureImpact(null);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const res = await api.post(`/projects/${id}/execution-structure/preview`, { execution_structure: executionStructure });
        if (!cancelled) setStructureImpact(res.data || null);
      } catch {
        if (!cancelled) setStructureImpact(null); // the save itself still reports any problem
      }
    }, 600);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [activeTab, structureDirtyCount, canViewProjectStructure, executionStructure, id]);
  const executionSummary = useMemo(() => summarizeExecution(executionTasks), [executionTasks]);
  const openExecutionTaskNames = useMemo(() => executionTasks
    .filter((task) => Number(task.level || 1) === 1 && !['Done', 'Not required'].includes(task.status || 'Not Started'))
    .map((task) => task.title || 'Task'), [executionTasks]);
  const materialConflict = useMemo(
    () => findUnlinkedMaterial({ boqItems, variations, deliveryNotes, identityOf: getMainBoqIdentity }),
    [boqItems, variations, deliveryNotes]
  );
  const invoiceScopeCounts = useMemo(
    () => countInvoicesByScope(invoices, variationInvoices, variations),
    [invoices, variationInvoices, variations]
  );

  const sortedInvoices = useMemo(() => (
    [...scopedInvoices].sort((a, b) => {
      const aReference = String(invoiceDrafts[a.id]?.reference ?? a.reference ?? '').trim();
      const bReference = String(invoiceDrafts[b.id]?.reference ?? b.reference ?? '').trim();
      const aInvoiceType = normalizeInvoiceTypeValue(invoiceDrafts[a.id]?.invoice_type ?? a.invoice_type ?? '');
      const bInvoiceType = normalizeInvoiceTypeValue(invoiceDrafts[b.id]?.invoice_type ?? b.invoice_type ?? '');

      if (!aReference && !bReference) {
        return aInvoiceType.localeCompare(bInvoiceType, undefined, { numeric: true, sensitivity: 'base' });
      }
      if (!aReference) return -1;
      if (!bReference) return 1;

      return bReference.localeCompare(aReference, undefined, { numeric: true, sensitivity: 'base' });
    })
  ), [invoiceDrafts, scopedInvoices]);

  useEffect(() => {
    if (!isStageLinesEditMode) {
      setIsMaterialEditMode(false);
    }
  }, [isStageLinesEditMode]);

  useEffect(() => {
    const subBoqQtyByLabel = {};
    boqItems.forEach((item) => {
      const label = normalizeSubBoqLabel(item.sub_boq);
      if (subBoqQtyByLabel[label] === undefined) {
        subBoqQtyByLabel[label] = item.sub_boq_qty ?? 1;
      }
    });
    setSubBoqQtyDrafts((prev) => ({
      ...subBoqQtyByLabel,
      ...Object.fromEntries(
        Object.entries(prev).filter(([label]) => subBoqQtyByLabel[label] !== undefined)
      )
    }));
  }, [boqItems]);

  useEffect(() => {
    if (!addBooqDialogOpen) return;
    const fetchCatalog = async () => {
      try {
        const res = await api.get('/catalog', { params: { search: catalogSearch } });
        setCatalogOptions(normalizeCatalogResponse(res.data));
      } catch {
        toast.error('Failed to load inventory items');
      }
    };
    fetchCatalog();
  }, [addBooqDialogOpen, catalogSearch]);

  const sortedDeliveryNotes = useMemo(() => {
    return [...deliveryNotes].sort((a, b) => {
      const da = a.delivery_date || a.created_at || '';
      const db = b.delivery_date || b.created_at || '';
      return da.localeCompare(db);
    });
  }, [deliveryNotes]);

  const mainDeliveryNoteColumns = useMemo(() => {
    const byName = new Map();
    sortedDeliveryNotes.forEach((dn) => {
      const key = String(dn.dn_number || '').trim() || dn.id;
      if (!byName.has(key)) {
        byName.set(key, {
          id: key,
          dn_number: dn.dn_number || 'DN',
          delivery_date: dn.delivery_date || dn.created_at || '',
          note_ids: []
        });
      }
      byName.get(key).note_ids.push(dn.id);
    });
    return Array.from(byName.values()).sort((a, b) => (a.delivery_date || '').localeCompare(b.delivery_date || ''));
  }, [sortedDeliveryNotes]);

  const mainDeliveryNoteKeyById = useMemo(() => {
    const byId = new Map();
    mainDeliveryNoteColumns.forEach((column) => {
      column.note_ids.forEach((noteId) => byId.set(noteId, column.id));
    });
    return byId;
  }, [mainDeliveryNoteColumns]);

  const materialDeliveryNoteColumns = useMemo(() => {
    const byName = new Map();
    sortedDeliveryNotes.forEach((dn) => {
      const dnName = String(dn.dn_number || '').trim() || dn.id;
      if (!byName.has(dnName)) {
        byName.set(dnName, {
          id: dnName,
          primary_note_id: dn.id,
          dn_number: dn.dn_number || 'DN',
          delivery_date: dn.delivery_date || dn.created_at || '',
          notes: [],
          note_ids: []
        });
      }
      if (String(dn.notes || '').trim()) {
        byName.get(dnName).notes.push(String(dn.notes).trim());
      }
      byName.get(dnName).note_ids.push(dn.id);
    });
    return Array.from(byName.values()).sort((a, b) => (a.delivery_date || '').localeCompare(b.delivery_date || ''));
  }, [sortedDeliveryNotes]);

  const materialVariationColumns = useMemo(
    () => variations.filter((variation) => Array.isArray(variation.items) && variation.items.length > 0),
    [variations]
  );

  const materialRows = useMemo(() => {
    // Same rule as the "not linked to any BOQ line" callout (findUnlinkedMaterial): a delivery line with a
    // live BOQ id counts toward that line; without one — or when its BOQ line was deleted — it counts toward
    // the first BOQ line with the same part number + description.
    // The cell rule (deliveryNoteCell) is the one "Save changes" uses to address the line it changes.
    const matcher = createMaterialMatcher(boqItems, getMainBoqIdentity);
    return boqItems.map((item) => {
      const byDn = {};
      let deliveredTotal = 0;
      sortedDeliveryNotes.forEach((dn) => {
        const { line } = deliveryNoteCell(dn, item, matcher);
        const qty = Number(line?.delivered_qty || 0);
        byDn[dn.id] = qty;
        deliveredTotal += qty;
      });
      const variationById = {};
      let variationTotal = 0;
      materialVariationColumns.forEach((variation) => {
        const variationItem = (variation.items || []).find((entry) => matcher.variationItemMatches(entry, item));
        const qty = Number(variationItem?.qty || 0);
        variationById[variation.id] = qty;
        variationTotal += qty;
      });
      const siteNeedsEntry = (materialSiteNeeds || []).find((entry) => (
        entry?.boq_item_id === item.id || entry?.part_number === item.part_number
      ));
      const siteNeedsQty = Number(siteNeedsEntry?.qty || 0);
      const totalPlannedQty = Number(item.boq_qty || 0) + variationTotal;
      const itemSiteRequests = siteRequests
        .filter((request) => request.boq_item_id === item.id)
        .sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''));
      const requestedTotal = itemSiteRequests.reduce((sum, request) => sum + Number(request.remaining_qty || 0), 0);
      return {
        ...item,
        deliveredByDn: byDn,
        deliveredTotal,
        variationById,
        variationTotal,
        siteNeedsQty,
        totalPlannedQty,
        remaining: totalPlannedQty - deliveredTotal,
        variationNeeded: siteNeedsQty - totalPlannedQty,
        siteRequests: itemSiteRequests,
        requestedTotal
      };
    });
  }, [boqItems, sortedDeliveryNotes, siteRequests, materialVariationColumns, materialSiteNeeds]);

  // 11 #5: Material lines whose site needs exceed BOQ + VOs — "From Material shortages" in the VO drawer.
  const variationShortages = useVariationShortages(materialRows);

  const subBoqOptions = useMemo(() => {
    if (!allowSubBoq) return [];
    const options = new Set();
    boqItems.forEach((item) => {
      const trimmed = String(item.sub_boq || '').trim();
      if (trimmed) {
        options.add(trimmed);
      }
    });
    const draftValue = String(boqSubGroupDraft || '').trim();
    if (draftValue) {
      options.add(draftValue);
    }
    return Array.from(options).sort((a, b) => a.localeCompare(b));
  }, [allowSubBoq, boqItems, boqSubGroupDraft]);

  const materialRowsBySubBoq = useMemo(() => {
    if (!allowSubBoq) {
      return { All: materialRows };
    }
    const grouped = {};
    materialRows.forEach((row) => {
      const label = normalizeSubBoqLabel(row.sub_boq);
      if (!grouped[label]) {
        grouped[label] = [];
      }
      grouped[label].push(row);
    });
    return grouped;
  }, [allowSubBoq, materialRows]);

  const groupedMaterialRows = useMemo(() => {
    if (!allowSubBoq) {
      return materialRows.map((row) => ({ type: 'row', row }));
    }
    const labels = Object.keys(materialRowsBySubBoq).sort((a, b) => a.localeCompare(b));
    return labels.flatMap((label) => ([
      { type: 'header', label },
      ...(collapsedMaterialSubBoqs[label] !== false ? [] : materialRowsBySubBoq[label].map((row) => ({ type: 'row', row })))
    ]));
  }, [allowSubBoq, materialRows, materialRowsBySubBoq, collapsedMaterialSubBoqs]);

  const materialDisplayRowIds = useMemo(
    () => groupedMaterialRows.filter((entry) => entry.type === 'row').map((entry) => entry.row.id),
    [groupedMaterialRows]
  );

  const mainBoqRows = useMemo(() => {
    if (!allowSubBoq) return [];
    const byPart = new Map();
    materialRows.forEach((row) => {
      const key = getMainBoqIdentity(row);
      if (!byPart.has(key)) {
        byPart.set(key, {
          id: row.id,
          part_number: row.part_number,
          description: row.description,
          unit: row.unit,
          boq_qty: 0,
          variationById: {},
          variationTotal: 0,
          deliveredByDn: {},
          deliveredTotal: 0,
          siteNeedsQty: 0,
          requestedTotal: 0,
        });
      }
      const agg = byPart.get(key);
      const parsedSubBoqMultiplier = Number(row.sub_boq_qty ?? 1);
      const subBoqMultiplier = Number.isFinite(parsedSubBoqMultiplier) ? Math.max(parsedSubBoqMultiplier, 0) : 1;
      const multipliedBoqQty = Number(row.boq_qty || 0) * subBoqMultiplier;
      agg.part_number = agg.part_number || row.part_number;
      agg.description = agg.description || row.description;
      agg.unit = agg.unit || row.unit;
      agg.boq_qty += multipliedBoqQty;
      agg.variationTotal += Number(row.variationTotal || 0);
      agg.siteNeedsQty += Number(row.siteNeedsQty || 0);
      agg.requestedTotal += Number(row.requestedTotal || 0);
      Object.entries(row.variationById || {}).forEach(([variationId, qty]) => {
        agg.variationById[variationId] = Number(agg.variationById[variationId] || 0) + Number(qty || 0);
      });
    });

    const mainIdentityByBoqItemId = new Map(
      boqItems.map((item) => [item.id, getMainBoqIdentity(item)])
    );
    sortedDeliveryNotes.forEach((dn) => {
      const mainDnKey = mainDeliveryNoteKeyById.get(dn.id) || dn.id;
      (dn.line_items || []).forEach((line) => {
        const identity = mainIdentityByBoqItemId.get(line.boq_item_id) || getMainBoqIdentity(line);
        const agg = byPart.get(identity);
        if (!agg) return;
        const qty = Number(line.delivered_qty || 0);
        agg.deliveredByDn[mainDnKey] = Number(agg.deliveredByDn[mainDnKey] || 0) + qty;
        agg.deliveredTotal += qty;
      });
    });

    const rows = Array.from(byPart.values()).map((row) => {
      const totalPlannedQty = Number(row.boq_qty || 0) + Number(row.variationTotal || 0);
      return {
        ...row,
        totalPlannedQty,
        remaining: totalPlannedQty - Number(row.deliveredTotal || 0),
        variationNeeded: Number(row.siteNeedsQty || 0) - totalPlannedQty,
      };
    });

    return rows.sort((a, b) => String(a.part_number || '').localeCompare(String(b.part_number || ''), undefined, { numeric: true }));
  }, [allowSubBoq, materialRows, mainDeliveryNoteKeyById, boqItems, sortedDeliveryNotes]);

  const deliveryBoqItems = useMemo(() => (
    allowSubBoq
      ? mainBoqRows.map((row) => ({
        id: row.id,
        part_number: row.part_number,
        description: row.description,
        unit: row.unit,
        boq_qty: row.boq_qty,
        remaining: row.remaining,
        sub_boq: ''
      }))
      : boqItems
  ), [allowSubBoq, boqItems, mainBoqRows]);

  const deliveryIdentityByBoqItemId = useMemo(
    () => new Map(boqItems.map((item) => [item.id, getMainBoqIdentity(item)])),
    [boqItems]
  );

  useEffect(() => {
    const validIds = new Set(materialRows.map((row) => row.id));
    setSelectedBoqRowIds((prev) => prev.filter((rowId) => validIds.has(rowId)));
    if (boqSelectionAnchorRef.current && !validIds.has(boqSelectionAnchorRef.current)) {
      boqSelectionAnchorRef.current = null;
    }
  }, [materialRows]);

  // ---- Material tab, wave 2 (screen 05) -------------------------------------------------------------
  // Typed quantities live in sparse "edit" maps until "Save changes" (one request, one Undo); a reload of
  // the BOQ in the background never drops them. Sub BOQ quantities keep using subBoqQtyDrafts.
  const [boqEdits, setBoqEdits] = useState({});
  const [dnEdits, setDnEdits] = useState({});
  const [materialSave, setMaterialSave] = useState({ status: 'idle', error: '' });
  const [materialVoOpen, setMaterialVoOpen] = useState(() => Boolean(readStored(storageKey('project', 'material', 'voColumns'), false)));
  const [materialDnOpen, setMaterialDnOpen] = useState(() => Boolean(readStored(storageKey('project', 'material', 'dnColumns'), false)));
  const [expandedMaterialRows, setExpandedMaterialRows] = useState(() => new Set());
  // 22 #10 "Where is it?": the part traced from request to site (MaterialTracePanel), or null.
  const [materialTrace, setMaterialTrace] = useState(null);
  const [materialQuery, setMaterialQuery] = useState('');
  const [materialView, setMaterialView] = useState('all');
  // 05 filter row (after_spec): "Sub BOQ: All" on Sub BOQ projects — one Sub BOQ group, opened (null = all).
  const [materialSubBoq, setMaterialSubBoq] = useState(null);

  const materialVoColumns = useMemo(() => sortByCode(materialVariationColumns), [materialVariationColumns]);
  const materialDiff = useMemo(() => diffMaterialDrafts({
    boqItems,
    boqDrafts: boqEdits,
    allowSubBoq,
    subBoqQtyDrafts,
    groups: allowSubBoq ? materialRowsBySubBoq : {},
    dnEdits,
    dnColumns: materialDeliveryNoteColumns,
    rows: materialRows,
  }), [boqItems, boqEdits, allowSubBoq, subBoqQtyDrafts, materialRowsBySubBoq, dnEdits, materialDeliveryNoteColumns, materialRows]);
  const materialDirtyCount = materialDiff.changes.length + materialDiff.invalid.length;
  // 05 #5: an edited (valid, unsaved) BOQ qty already moves that row's Remaining / Progress, the totals and the KPIs.
  const materialPreviewRows = useMemo(() => applyBoqPreview(materialRows, materialDiff.changes), [materialRows, materialDiff.changes]);
  const { confirmLeave: confirmLeaveMaterial, markClean: markMaterialClean } = useUnsavedChanges(materialDirtyCount > 0, {
    title: `You have ${materialDirtyCount} unsaved change${materialDirtyCount === 1 ? '' : 's'}`,
    description: 'Your quantity edits on the Material tab are not saved. Leave without saving them?',
    confirmLabel: 'Discard changes',
    cancelLabel: 'Keep editing',
  });
  const materialKpis = useMemo(() => summarizeMaterialKpis({
    rows: allowSubBoq ? mainBoqRows : materialPreviewRows,
    lineCount: boqItems.length,
    variationColumns: materialVariationColumns,
    deliveryNoteColumns: materialDeliveryNoteColumns,
    siteRequests,
  }), [allowSubBoq, mainBoqRows, materialPreviewRows, boqItems.length, materialVariationColumns, materialDeliveryNoteColumns, siteRequests]);

  // The material bars (selection + unsaved quantities) float at the bottom: toasts rise above them.
  const materialBarsRef = useRef(null);
  useToastClearance(materialBarsRef, activeTab === 'material' && (materialDirtyCount > 0 || selectedBoqRowIds.length > 0));

  const resetMaterialEdits = () => {
    setBoqEdits({});
    setDnEdits({});
    const subBoqQtyByLabel = {};
    boqItems.forEach((item) => {
      const label = normalizeSubBoqLabel(item.sub_boq);
      if (subBoqQtyByLabel[label] === undefined) subBoqQtyByLabel[label] = item.sub_boq_qty ?? 1;
    });
    setSubBoqQtyDrafts(subBoqQtyByLabel);
    setMaterialSave({ status: 'idle', error: '' });
  };

  // Every tab switch goes through here (the tab bar, "Open Variations", Timeline → Execution …): a tab with
  // unsaved edits asks first. Tabs live in the URL (?tab=) via the effect next to activeTab.
  const handleTabChange = async (next) => {
    if (next === activeTab) return;
    if (activeTab === 'material' && materialDirtyCount > 0) {
      const ok = await confirmLeaveMaterial();
      if (!ok) return;
      resetMaterialEdits();
    }
    // 08 #4 / 09 #6: "Save 5 changes before leaving?" — save, discard, or stay on the tab.
    if (activeTab === 'structure' && structureDirtyCount > 0) {
      const ok = await askSaveBeforeLeaving({
        count: structureDirtyCount,
        what: 'Project Structure',
        detail: structureDiff.summary,
        save: () => saveStructure({ rethrow: true }),
        discard: () => discardStructureChanges({ quiet: true }),
      });
      if (!ok) return;
    }
    if (activeTab === 'execution' && executionDirtyCount > 0) {
      const ok = await askSaveBeforeLeaving({
        count: executionDirtyCount,
        what: 'Execution',
        save: () => saveExecutionTasks({ rethrow: true }),
        discard: () => discardExecutionChanges(),
      });
      if (!ok) return;
    }
    setActiveTab(next);
  };

  /** Resolves true when the tab may close: the edits were saved, or the person chose to discard them. */
  const askSaveBeforeLeaving = async ({ count, what, detail, save, discard }) => {
    let discardChosen = false;
    const saved = await confirm({
      title: `Save ${count} change${count === 1 ? '' : 's'} before leaving?`,
      subtitle: detail || undefined,
      description: `Your ${what} edits are not saved yet.`,
      confirmLabel: `Save and continue`,
      cancelLabel: 'Keep editing',
      onConfirm: save,
      alternative: { text: 'Or leave without them.', label: 'Discard changes', onSelect: () => { discardChosen = true; } },
    });
    if (saved) return true;
    if (discardChosen) {
      discard();
      return true;
    }
    return false;
  };

  const boqDeviceOptions = useMemo(() => (
    boqItems
      .filter((item) => (item.part_number || '').trim() || (item.description || '').trim())
      .map((item) => ({
        id: item.id,
        part_number: item.part_number || '',
        name: item.description || item.part_number || 'Device'
      }))
  ), [boqItems]);

  const selectedBoqDeviceOptions = useMemo(
    () => boqDeviceOptions.filter((item) => (deviceBatchForm.boq_item_ids || []).includes(item.id)),
    [boqDeviceOptions, deviceBatchForm.boq_item_ids]
  );

  const filteredBoqDeviceOptions = useMemo(() => {
    const query = deviceBatchSearch.trim().toLowerCase();
    if (!query) {
      return boqDeviceOptions.slice(0, 60);
    }
    return boqDeviceOptions
      .filter((item) => `${item.part_number} ${item.name}`.toLowerCase().includes(query))
      .slice(0, 60);
  }, [boqDeviceOptions, deviceBatchSearch]);
  // how many devices "Add N devices" makes, and where they go ("Villa 1 (A) › GF › Living")
  const deviceBatchCount = selectedBoqDeviceOptions.reduce((sum, item) => (
    sum + Math.max(1, Number(deviceBatchForm.item_settings?.[item.id]?.quantity ?? 1) || 1)
  ), 0);
  const deviceBatchTargetLabel = useMemo(() => {
    if (!deviceBatchTarget) return '';
    const { buildingId, floorId, roomId, panelId } = deviceBatchTarget;
    const building = (executionStructure.buildings || []).find((entry) => entry.id === buildingId);
    const floor = (building?.floors || []).find((entry) => entry.id === floorId);
    const room = roomId ? (floor?.rooms || []).find((entry) => entry.id === roomId) : null;
    const panel = panelId ? ((room || floor)?.panels || []).find((entry) => entry.id === panelId) : null;
    return [building?.name, floor?.name, room?.name, panel?.name].map((value) => String(value || '').trim()).filter(Boolean).join(' › ');
  }, [deviceBatchTarget, executionStructure]);
  const toggleDeviceBatchItem = (itemId, checked) => {
    setDeviceBatchForm((prev) => ({
      ...prev,
      boq_item_ids: checked
        ? [...new Set([...(prev.boq_item_ids || []), itemId])]
        : (prev.boq_item_ids || []).filter((entryId) => entryId !== itemId),
      item_settings: checked
        ? { ...(prev.item_settings || {}), [itemId]: prev.item_settings?.[itemId] || { quantity: 1, start_index: 1, address_prefix: '' } }
        : Object.fromEntries(Object.entries(prev.item_settings || {}).filter(([key]) => key !== itemId)),
    }));
  };

  const executionMainTasksCount = useMemo(
    () => executionTasks.filter((task) => Number(task.level || 1) === 1).length,
    [executionTasks]
  );

  const executionTimelineTasks = useMemo(() => {
    return [...executionTasks]
      .filter((task) => task.start_date || task.due_date)
      .sort((a, b) => {
        const aDate = a.start_date || a.due_date || '';
        const bDate = b.start_date || b.due_date || '';
        return aDate.localeCompare(bDate) || (Number(a.level || 1) - Number(b.level || 1));
      });
  }, [executionTasks]);

  // ---- Execution, wave 2 (screen 09) ----------------------------------------------------------------
  // Rows: only open parents' sub-items are built (buildExecutionRows) and the DataTable renders the first
  // 100 + "Show more", so 2,500 sub-items stay fast (#9). Filters + views (#3), bulk edits (#4) and the
  // unsaved-changes count / leave guard (#6) all work on the same local list until "Save execution".
  const executionTree = useMemo(() => buildExecutionTree(executionTasks), [executionTasks]);
  const executionTaskById = executionTree.byId;
  // 10 #6: dated tasks as timeline items (built only while the Timeline tab is open).
  const timelineItems = useMemo(
    () => (activeTab === 'timeline' ? timelineItemsFromTasks(executionTimelineTasks, executionTree) : []),
    [activeTab, executionTimelineTasks, executionTree]
  );
  // Keyed on the filters WITHOUT q (+ the deferred text): typing changes only the deferred render's filter.
  const { q: _executionUrlQuery, ...executionFilterRest } = executionFilter;
  const executionFilterRestKey = JSON.stringify(executionFilterRest);
  const executionListFilter = useMemo(
    () => ({ ...JSON.parse(executionFilterRestKey), q: deferredExecutionQuery }),
    [executionFilterRestKey, deferredExecutionQuery]
  );
  const executionFiltered = useMemo(
    () => filterExecutionTasks(executionTasks, executionListFilter, executionTree),
    [executionTasks, executionListFilter, executionTree]
  );
  const executionFilterKey = JSON.stringify(executionListFilter);
  const executionFilteredRef = useRef(executionFiltered);
  executionFilteredRef.current = executionFiltered;
  const executionFilterWasActiveRef = useRef(false);
  const executionHasTasks = executionTasks.length > 0;
  // A new filter opens the parents that have matches below them and closes the others; clearing the
  // filters closes every parent again. Manual open / close in between is kept.
  useEffect(() => {
    const { active, openParents } = executionFilteredRef.current;
    if (!active && !executionFilterWasActiveRef.current) return;
    executionFilterWasActiveRef.current = active;
    setCollapsedExecutionParents((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((taskId) => {
        next[taskId] = active ? !openParents.has(taskId) : true;
      });
      return next;
    });
  }, [executionFilterKey, executionHasTasks]);

  const executionRowCacheRef = useRef(new Map());
  const executionRows = useMemo(() => buildExecutionRows({
    tree: executionTree,
    collapsed: collapsedExecutionParents,
    included: executionFiltered.included,
    cache: executionRowCacheRef.current,
  }), [executionTree, collapsedExecutionParents, executionFiltered]);
  const executionChipOptions = useMemo(() => executionFilterOptions(executionTasks), [executionTasks]);
  const executionAssignees = useMemo(() => {
    const names = new Set(EXECUTION_ASSIGNEES);
    executionChipOptions.doneBy.forEach((option) => { if (option.value !== 'none') names.add(option.value); });
    return [...names];
  }, [executionChipOptions]);

  const savedExecutionTasks = project?.execution_tasks;
  const executionChanges = useMemo(
    () => countExecutionChanges(savedExecutionTasks || [], executionTasks),
    [savedExecutionTasks, executionTasks]
  );
  const executionDirtyCount = executionChanges.count;
  // "Shop Drawings, Graphics + 24 sub-items": the changed tasks whose parent did not change, then the rest.
  const executionChangesDetail = useMemo(() => {
    if (!executionChanges.count) return '';
    const changed = new Set(executionChanges.ids);
    const roots = executionChanges.ids.filter((taskId) => !changed.has(executionTaskById.get(taskId)?.parent_id));
    const names = roots.slice(0, 3).map((taskId) => formatExecutionTaskTitle(executionTaskById.get(taskId), executionTaskById)).filter(Boolean);
    const rest = executionChanges.count - names.length;
    const restLabel = roots.length > names.length ? 'more' : `sub-item${rest === 1 ? '' : 's'}`;
    return `${names.join(', ')}${rest > 0 ? ` + ${rest} ${restLabel}` : ''}`;
  }, [executionChanges, executionTaskById]);
  const [executionSave, setExecutionSave] = useState({ status: 'idle', error: '' });
  // The execution bars (selection + unsaved edits) float at the bottom: toasts rise above them.
  const executionBarsRef = useRef(null);
  useToastClearance(executionBarsRef, activeTab === 'execution' && (executionDirtyCount > 0 || selectedExecutionTaskIds.length > 0));
  const { markClean: markExecutionClean } = useUnsavedChanges(executionDirtyCount > 0, {
    title: `You have ${executionDirtyCount} unsaved execution change${executionDirtyCount === 1 ? '' : 's'}`,
    description: 'Your edits on the Execution tab are not saved. Leave without saving them?',
    confirmLabel: 'Discard changes',
    cancelLabel: 'Keep editing',
  });

  // 09 #3 "My tasks": the Done-by names that are this person — any case / spacing, or the one full name an account
  // with a first name only stands for ("Yahya" → "Yahya Marei"; ambiguous first names match nobody else).
  const myExecutionNames = useMemo(() => executionNamesForUser(
    user?.name,
    (executionTasks || []).flatMap((task) => splitPeople(task?.done_by)),
  ), [user?.name, executionTasks]);
  const executionViewCounts = useMemo(() => ({
    all: countMainTasksMatching(executionTasks, {}, executionTree),
    mine: myExecutionNames.length ? countMainTasksMatching(executionTasks, { doneBy: myExecutionNames }, executionTree) : 0,
    waiting: countMainTasksMatching(executionTasks, { status: WAITING_STATUSES }, executionTree),
    blocked: countMainTasksMatching(executionTasks, { status: BLOCKED_STATUSES }, executionTree),
  }), [executionTasks, executionTree, myExecutionNames]);
  const executionViews = useSavedViews('project-execution', {
    presets: [
      { id: 'all', label: 'All', state: {}, count: executionViewCounts.all },
      { id: 'mine', label: 'My tasks', state: { doneBy: myExecutionNames.length ? myExecutionNames : ['me'] }, count: executionViewCounts.mine },
      { id: 'waiting', label: 'Waiting', state: { status: WAITING_STATUSES }, count: executionViewCounts.waiting },
      { id: 'blocked', label: 'Blocked', state: { status: BLOCKED_STATUSES }, count: executionViewCounts.blocked },
    ],
    state: {
      status: executionFilter.status,
      category: executionFilter.category,
      building: executionFilter.building,
      doneBy: executionFilter.doneBy,
      monitoring: executionFilter.monitoring,
      comments: executionFilter.comments,
    },
    defaults: EXECUTION_VIEW_DEFAULTS,
    pristine: executionFilterPristine,
    onApply: (state) => setExecutionFilter(state),
  });

  const getExecutionTaskContext = useCallback((task) => {
    let current = task;
    let level1Task = Number(task?.level || 1) === 1 ? task : null;
    let level2Task = Number(task?.level || 1) === 2 ? task : null;
    while (current?.parent_id) {
      current = executionTaskById.get(current.parent_id) || null;
      if (!current) break;
      if (!level2Task && Number(current.level || 1) === 2) level2Task = current;
      if (!level1Task && Number(current.level || 1) === 1) level1Task = current;
    }
    return {
      level1: level1Task ? formatExecutionTaskTitle(level1Task, executionTaskById) : '',
      level2: level2Task ? formatExecutionTaskTitle(level2Task, executionTaskById) : '',
    };
  }, [executionTaskById]);

  /** Rows for Export / Print: one per task, with its main task (L1) and L2 for context. */
  const buildExecutionExportRows = (tasks) => tasks.map((task) => {
    const context = getExecutionTaskContext(task);
    return {
      project: project?.name || '',
      level1: context.level1,
      level2: context.level2,
      level: `L${task.level || 1}`,
      task: formatExecutionTaskTitle(task, executionTaskById),
      status: task.status || '',
      category: task.category || '',
      start_date: task.start_date || '',
      due_date: task.due_date || '',
      done_by: task.done_by || '',
      checked_by: task.checked_by || '',
      comments: task.comments || '',
      monitoring: task.monitoring ? 'Yes' : 'No',
      zone: task.zone || '',
      zone_2: task.zone_2 || '',
      zone_3: task.zone_3 || '',
      device_type: task.device_type || '',
      updated: formatDateLabel(task.updated)
    };
  });

  useEffect(() => {
    const validTaskIds = new Set(executionTasks.map((task) => task.id));
    setSelectedExecutionTaskIds((prev) => {
      const kept = prev.filter((taskId) => validTaskIds.has(taskId));
      return kept.length === prev.length ? prev : kept;
    });
  }, [executionTasks]);

  const toggleCatalogItem = (item, checked) => {
    const key = getCatalogSelectionKey(item);
    setSelectedCatalog((prev) => {
      const next = { ...prev };
      next[key] = {
        item,
        checked,
        qty: next[key]?.qty ?? 1
      };
      return next;
    });
  };

  const updateCatalogQty = (selectionKey, qty) => {
    setSelectedCatalog((prev) => ({
      ...prev,
      [selectionKey]: {
        ...prev[selectionKey],
        qty
      }
    }));
  };

  const resetAddBoqDialogDraft = () => {
    setBoqPasteText('');
    setBoqSubGroupDraft('');
    setSelectedCatalog({});
    setMaterialPhoto(null);
    setCatalogSearch('');
    setNewCatalogForm({ part_number: '', description: '', unit: 'pcs', category: '', brand: '' });
  };

  const handleOpenAddBoqDialog = () => {
    resetAddBoqDialogDraft();
    setAddBooqDialogOpen(true);
  };

  const handleAddBoqDialogOpenChange = (open) => {
    setAddBooqDialogOpen(open);
    if (!open) {
      resetAddBoqDialogDraft();
    }
  };

  // Material toolbar: open the Add BOQ dialog on the part the user asked for (paste box / catalog search).
  const handleOpenBoqDialogFor = (focus) => {
    handleOpenAddBoqDialog();
    setBoqDialogFocus(focus);
  };

  const handleTriggerBoqExcelImport = () => {
    if (!isStageLinesEditMode) return;
    boqExcelInputRef.current?.click();
  };

  const handleBoqExcelFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const rows = await readBoqSpreadsheet(file);
      if (!rows.length) {
        notify.error(`No BOQ rows found in ${file.name}`, { description: 'Use the columns Part No, Description and BOQ Qty (download the template from the Add BOQ items menu).' });
        return;
      }
      handleOpenAddBoqDialog();
      setBoqPasteText(boqRowsToPasteText(rows));
      setBoqDialogFocus('paste');
      notify.info(`Read ${rows.length} row${rows.length === 1 ? '' : 's'} from ${file.name}`, {
        description: 'Check them in the paste box, then press Import Pasted BOQ and Add Selected BOQ Items.',
      });
    } catch (err) {
      notify.error(`Could not read ${file.name}`, { description: getErrorMessage(err) });
    }
  };

  const handleDownloadBoqTemplate = () => {
    downloadBlobFile('boq-template.xlsx', new Blob([boqTemplateXlsx()], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  };

  const handleExportMaterial = () => {
    if (!materialRows.length) return;
    const safeName = String(project?.name || 'project').replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'project';
    downloadBlobFile(
      `${safeName}-material.xlsx`,
      new Blob([materialRowsToXlsx(materialRows, getMaterialProgress)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    );
  };

  const handleCreateCatalogInline = async () => {
    try {
      const payload = {
        part_number: newCatalogForm.part_number.trim(),
        description: newCatalogForm.description.trim(),
        unit: newCatalogForm.unit || 'pcs',
        category: newCatalogForm.category.trim(),
        brand: newCatalogForm.brand.trim()
      };
      if (!payload.part_number) {
        toast.error('Part number is required');
        return;
      }
      // BUG-003: the server's rule (and words) for a catalog part number, before a 400
      if (!isValidPartNumber(payload.part_number)) {
        toast.error(PART_NUMBER_INVALID);
        return;
      }
      const res = await api.post('/catalog', payload);
      setCatalogOptions((prev) => [res.data, ...prev]);
      setSelectedCatalog((prev) => ({
        ...prev,
        [res.data.part_number]: { item: res.data, checked: true, qty: 1 }
      }));
      setNewCatalogForm({ part_number: '', description: '', unit: 'pcs', category: '', brand: '' });
      toast.success('Catalog item created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create catalog item');
    }
  };

  const handleImportPhoto = async () => {
    if (!materialPhoto) {
      toast.error('Select a photo first');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', materialPhoto);
      const res = await api.post('/materials/boq-items/import-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data?.message || 'Photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo import failed');
    }
  };

  const handleAddSelectedBoqItems = async () => {
    const subBoqValue = allowSubBoq ? String(boqSubGroupDraft || '').trim() : '';
    const subBoqLabel = normalizeSubBoqLabel(subBoqValue);
    const parsedSubBoqQty = Number(subBoqQtyDrafts[subBoqLabel] ?? 1);
    const subBoqQty = subBoqValue && Number.isFinite(parsedSubBoqQty) ? Math.max(parsedSubBoqQty, 0) : 1;
    const picked = Object.values(selectedCatalog)
      .filter((entry) => entry?.checked)
      .map((entry) => ({
        part_number: entry.item.part_number,
        description: entry.item.description || '',
        unit: entry.item.unit || 'pcs',
        boq_qty: Number(entry.qty || 0),
        ...(subBoqValue ? { sub_boq: subBoqValue, sub_boq_qty: subBoqQty } : {})
      }))
      .filter((entry) => entry.boq_qty > 0);

    if (picked.length === 0) {
      toast.error('Select at least one item with quantity');
      return;
    }
    try {
      const existingCatalogPartNumbers = new Set(
        catalogOptions
          .filter((item) => item.id)
          .map((item) => String(item.part_number || '').trim().toUpperCase())
          .filter(Boolean)
      );
      // Only real part numbers are offered for the Catalog (BUG-003): /catalog/batch refuses the whole batch for one
      // slip such as '-' or '12', which would stop this BOQ add. Such lines still go to the BOQ as they are.
      let missingCatalogItems = picked
        .filter((entry) => {
          const partNumber = String(entry.part_number || '').trim();
          return partNumber && isValidPartNumber(partNumber) && !existingCatalogPartNumbers.has(partNumber.toUpperCase());
        })
        .reduce((acc, entry) => {
          const key = String(entry.part_number || '').trim().toUpperCase();
          if (!acc.has(key)) {
            acc.set(key, {
              part_number: String(entry.part_number || '').trim(),
              description: String(entry.description || '').trim(),
              unit: entry.unit || 'pcs',
              category: '',
              brand: ''
            });
          }
          return acc;
        }, new Map());

      if (missingCatalogItems.size > 0) {
        const verifiedEntries = await Promise.all([...missingCatalogItems.values()].map(async (item) => {
          try {
            const res = await api.get('/catalog', { params: { search: item.part_number, limit: 100 } });
            const matches = res.data?.items || res.data || [];
            const exists = matches.some((match) => (
              String(match.part_number || '').trim().toUpperCase() === item.part_number.toUpperCase()
            ));
            return exists ? null : item;
          } catch (err) {
            return item;
          }
        }));
        missingCatalogItems = new Map(
          verifiedEntries
            .filter(Boolean)
            .map((item) => [item.part_number.toUpperCase(), item])
        );
      }

      if (missingCatalogItems.size > 0) {
        const missingPartNumbers = [...missingCatalogItems.values()].map((item) => item.part_number).join(', ');
        const shouldCreateCatalogItems = window.confirm(
          `${missingCatalogItems.size} BOQ part number(s) are not in the Catalog: ${missingPartNumbers}. Add them to the Catalog automatically?`
        );
        if (shouldCreateCatalogItems) {
          const payload = { items: [...missingCatalogItems.values()] };
          const res = missingCatalogItems.size === 1
            ? await api.post('/catalog', payload.items[0])
            : await api.post('/catalog/batch', payload);
          const createdItems = missingCatalogItems.size === 1 ? [res.data] : (res.data?.items || []);
          if (createdItems.length) {
            setCatalogOptions((prev) => {
              const existingKeys = new Set(prev.map((item) => String(item.part_number || '').trim().toUpperCase()));
              return [
                ...createdItems.filter((item) => !existingKeys.has(String(item.part_number || '').trim().toUpperCase())),
                ...prev
              ];
            });
          }
          toast.success(`${missingCatalogItems.size} catalog item(s) created`);
        }
      }

      const normalizePartNumber = (value) => String(value || '').trim().toUpperCase();
      const normalizeDescription = (value) => String(value || '').trim().toUpperCase();
      const normalizeSubBoq = (value) => String(value || '').trim().toUpperCase();
      const getBoqIdentityKey = (item) => [
        normalizeSubBoq(allowSubBoq ? item.sub_boq : ''),
        normalizePartNumber(item.part_number),
        normalizeDescription(item.description || item.name)
      ].join('::');
      const existingByIdentity = new Map(
        boqItems.map((item) => [getBoqIdentityKey(item), item])
      );

      const createItems = [];
      const updateItems = [];

      picked.forEach((entry) => {
        const partKey = normalizePartNumber(entry.part_number);
        const existing = partKey ? existingByIdentity.get(getBoqIdentityKey(entry)) : null;
        if (existing) {
          const shouldApplySubBoq = subBoqValue && !String(existing.sub_boq || '').trim();
          updateItems.push({
            id: existing.id,
            boq_qty: Number(existing.boq_qty || 0) + Number(entry.boq_qty || 0),
            ...(shouldApplySubBoq ? { sub_boq: subBoqValue, sub_boq_qty: subBoqQty } : {})
          });
        } else {
          createItems.push(entry);
        }
      });

      await Promise.all([
        ...updateItems.map((item) => api.put(`/materials/boq-items/${item.id}`, {
          boq_qty: item.boq_qty,
          ...(allowSubBoq && item.sub_boq !== undefined ? { sub_boq: item.sub_boq, sub_boq_qty: item.sub_boq_qty } : {})
        })),
        ...(createItems.length
          ? [api.post('/materials/boq-items/bulk', { project_id: id, items: createItems })]
          : [])
      ]);

      toast.success(
        `${createItems.length} added, ${updateItems.length} updated`
      );
      setAddBooqDialogOpen(false);
      resetAddBoqDialogDraft();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add BOQ items');
    }
  };

  const handleImportBoqFromPaste = () => {
    const parsedRows = parsePastedRowsWithQty(boqPasteText);
    if (!parsedRows.length) {
      toast.error('Paste rows with Part No, Description and Qty');
      return;
    }

    const normalizePartNumber = (value) => String(value || '').trim().toUpperCase();
    const normalizeDescription = (value) => String(value || '').trim().toUpperCase();
    const aggregated = parsedRows.reduce((acc, row, index) => {
      const partKey = normalizePartNumber(row.part_number);
      const descKey = normalizeDescription(row.description);
      const key = partKey ? `${partKey}::${descKey}` : `blank-part-${index}`;
      if (!acc[key]) {
        acc[key] = {
          part_number: row.part_number,
          description: row.description,
          qty: 0,
          selection_key: `paste-${key}`
        };
      }
      acc[key].qty += row.qty;
      return acc;
    }, {});

    const optionsByPart = new Map(
      catalogOptions.map((item) => [String(item.part_number || '').trim().toUpperCase(), item])
    );
    const optionsByIdentity = new Map(
      catalogOptions.map((item) => [
        `${normalizePartNumber(item.part_number)}::${normalizeDescription(item.description)}`,
        item
      ])
    );
    const missingOptions = [];

    setSelectedCatalog((prev) => {
      const next = { ...prev };
      Object.values(aggregated).forEach((row) => {
        const partKey = normalizePartNumber(row.part_number);
        const identityKey = `${partKey}::${normalizeDescription(row.description)}`;
        const matchingCatalogItem = partKey ? optionsByIdentity.get(identityKey) : null;
        const item = matchingCatalogItem || {
          part_number: row.part_number,
          description: row.description,
          unit: 'pcs',
          selection_key: row.selection_key
        };
        const selectionKey = getCatalogSelectionKey(item);

        if (!matchingCatalogItem) {
          missingOptions.push(item);
          if (partKey && !optionsByPart.has(partKey)) {
            optionsByPart.set(partKey, item);
          }
          optionsByIdentity.set(identityKey, item);
        }

        const currentQty = Number(next[selectionKey]?.qty || 0);
        next[selectionKey] = {
          item,
          checked: true,
          qty: currentQty + row.qty
        };
      });
      return next;
    });

    if (missingOptions.length) {
      setCatalogOptions((prev) => {
        const existing = new Set(prev.map((item) => getCatalogSelectionKey(item)));
        const toAdd = missingOptions.filter((item) => !existing.has(getCatalogSelectionKey(item)));
        return [...toAdd, ...prev];
      });
    }

    setBoqPasteText('');
    toast.success(`Imported ${Object.keys(aggregated).length} BOQ row(s)`);
  };

  const handleOpenSiteNeedsDialog = () => {
    const nextDrafts = {};
    boqItems.forEach((item) => {
      const existing = (materialSiteNeeds || []).find((entry) => (
        entry?.boq_item_id === item.id || entry?.part_number === item.part_number
      ));
      nextDrafts[item.id] = existing?.qty ?? 0;
    });
    setSiteNeedsDrafts(nextDrafts);
    setSiteNeedsDialogOpen(true);
  };

  const handleSiteNeedsQtyChange = (itemId, value) => {
    setSiteNeedsDrafts((prev) => ({
      ...prev,
      [itemId]: value
    }));
  };

  const handleSaveSiteNeeds = async (e) => {
    e.preventDefault();
    const payload = boqItems
      .map((item) => ({
        boq_item_id: item.id,
        part_number: item.part_number,
        description: item.description || '',
        unit: item.unit || 'pcs',
        qty: Number(siteNeedsDrafts[item.id] || 0)
      }))
      .filter((item) => item.qty > 0);

    try {
      await api.put(`/projects/${id}`, { material_site_needs: payload });
      toast.success('Site needs updated');
      setSiteNeedsDialogOpen(false);
      setMaterialSiteNeeds(payload);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save site needs');
    }
  };

  // 05 #5: typed values go into the edit maps; the unsaved bar saves them all at once.
  const handleBoqQtyDraftChange = (itemId, value) => {
    setBoqEdits((prev) => ({ ...prev, [itemId]: { ...prev[itemId], boq_qty: value } }));
  };

  const handleBoqSubGroupDraftChange = (itemId, value) => {
    setBoqEdits((prev) => ({ ...prev, [itemId]: { ...prev[itemId], sub_boq: value } }));
  };

  const handleSubBoqQtyDraftChange = (label, value) => {
    setSubBoqQtyDrafts((prev) => ({
      ...prev,
      [label]: value
    }));
  };

  const getSubBoqQtyDisplayValue = (label) => {
    if (subBoqQtyDrafts[label] !== undefined && subBoqQtyDrafts[label] !== '') {
      return subBoqQtyDrafts[label];
    }
    const rowWithQty = (materialRowsBySubBoq[label] || []).find((row) => row.sub_boq_qty !== undefined && row.sub_boq_qty !== null);
    return rowWithQty?.sub_boq_qty ?? 1;
  };

  const handleDeleteBoqItem = async (itemId) => {
    try {
      await api.delete(`/materials/boq-items/${itemId}`);
      toast.success('BOQ item deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete BOQ item');
    }
  };

  const handleToggleBoqSelection = (rowId, checked) => {
    const orderedRowIds = allowSubBoq ? materialDisplayRowIds : materialRows.map((row) => row.id);
    const shouldRangeSelect = boqSelectionInteractionRef.current.shiftKey;
    const anchorId = boqSelectionAnchorRef.current;

    setSelectedBoqRowIds((prev) => {
      const current = new Set(prev);

      if (shouldRangeSelect && anchorId) {
        const anchorIndex = orderedRowIds.indexOf(anchorId);
        const targetIndex = orderedRowIds.indexOf(rowId);
        if (anchorIndex >= 0 && targetIndex >= 0) {
          orderedRowIds
            .slice(Math.min(anchorIndex, targetIndex), Math.max(anchorIndex, targetIndex) + 1)
            .forEach((id) => {
              if (checked) {
                current.add(id);
              } else {
                current.delete(id);
              }
            });
        } else if (checked) {
          current.add(rowId);
        } else {
          current.delete(rowId);
        }
      } else if (checked) {
        current.add(rowId);
      } else {
        current.delete(rowId);
      }
      return [...current];
    });

    boqSelectionAnchorRef.current = rowId;
    boqSelectionInteractionRef.current = { shiftKey: false };
  };

  const handleToggleMaterialSubBoqCollapse = (label) => {
    setCollapsedMaterialSubBoqs((prev) => ({
      ...prev,
      [label]: prev[label] === false
    }));
  };

  const handleDeleteSelectedBoqItems = async () => {
    if (!selectedBoqRowIds.length) {
      toast.error('Select BOQ items first');
      return;
    }
    const count = selectedBoqRowIds.length;
    const selectedRows = materialRows.filter((row) => selectedBoqRowIds.includes(row.id));
    const deliveredRows = selectedRows.filter((row) => Number(row.deliveredTotal || 0) > 0).length;
    const requestRows = selectedRows.filter((row) => (row.siteRequests || []).length > 0).length;
    const ok = await confirm({
      title: `Delete ${count} BOQ line${count === 1 ? '' : 's'}?`,
      subtitle: selectedRows.slice(0, 3).map((row) => row.part_number || row.description).filter(Boolean).join(', ')
        + (selectedRows.length > 3 ? ` +${selectedRows.length - 3} more` : ''),
      description: 'The lines are removed from this project\'s BOQ.',
      consequences: [
        deliveredRows ? `${deliveredRows} line${deliveredRows === 1 ? ' has' : 's have'} deliveries recorded` : null,
        requestRows ? `${requestRows} line${requestRows === 1 ? ' has' : 's have'} open site requests` : null,
      ].filter(Boolean),
      tone: 'danger',
      confirmLabel: `Delete ${count} line${count === 1 ? '' : 's'}`,
    });
    if (!ok) return;
    try {
      // One request = one history entry, so the shell's Undo toast brings back every line of the batch.
      const res = await api.post('/materials/boq-items/bulk-delete', { item_ids: selectedBoqRowIds });
      const deleted = Number(res.data?.deleted ?? count);
      setSelectedBoqRowIds([]);
      boqSelectionAnchorRef.current = null;
      fetchData();
      notify.success(`${deleted} BOQ line${deleted === 1 ? '' : 's'} deleted`);
    } catch (err) {
      notify.error(`Could not delete the BOQ line${count === 1 ? '' : 's'}`, { description: getErrorMessage(err) });
    }
  };

  const handleBoqDragStart = (itemId) => {
    if (!isMaterialEditMode) return;
    setDraggingBoqItemId(itemId);
  };

  const handleBoqDrop = async (targetItemId) => {
    if (!isMaterialEditMode || !draggingBoqItemId || draggingBoqItemId === targetItemId) {
      setDraggingBoqItemId(null);
      return;
    }

    const currentIndex = boqItems.findIndex((item) => item.id === draggingBoqItemId);
    const targetIndex = boqItems.findIndex((item) => item.id === targetItemId);
    if (currentIndex < 0 || targetIndex < 0) {
      setDraggingBoqItemId(null);
      return;
    }

    const nextItems = [...boqItems];
    const [movedItem] = nextItems.splice(currentIndex, 1);
    nextItems.splice(targetIndex, 0, movedItem);

    setBoqItems(nextItems);
    setDraggingBoqItemId(null);

    try {
      const res = await api.put('/materials/boq-items/reorder', {
        item_ids: nextItems.map((item) => item.id)
      });
      setBoqItems(res.data || nextItems);
      toast.success('Material order updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reorder BOQ items');
      fetchData();
    }
  };

  const handleDnQtyChange = (dnId, itemId, value) => {
    setDnEdits((prev) => ({ ...prev, [dnId]: { ...(prev[dnId] || {}), [itemId]: value } }));
  };

  /**
   * "Save changes" on the Material unsaved bar: every typed quantity in one request (one Undo). A typed
   * delivered quantity is sent as a patch of that one cell — the rest of the delivery note is kept.
   */
  const handleSaveMaterialChanges = async () => {
    if (materialDiff.invalid.length) {
      const first = materialDiff.invalid[0];
      setMaterialSave({ status: 'error', error: `${first.label}: ${first.error}${materialDiff.invalid.length > 1 ? ` (+${materialDiff.invalid.length - 1} more)` : ''}` });
      return;
    }
    if (!materialDiff.changes.length) return;
    const count = materialDiff.changes.length;
    setMaterialSave({ status: 'saving', error: '' });
    try {
      const payload = buildMaterialSavePayload(materialDiff.changes, { deliveryNotes, boqItems, identityOf: getMainBoqIdentity });
      const res = await api.put(`/projects/${id}/material-quantities`, payload);
      const savedItems = new Map((res.data?.boq_items || []).map((item) => [item.id, item]));
      const savedNotes = new Map((res.data?.delivery_notes || []).map((note) => [note.id, note]));
      if (savedItems.size) setBoqItems((prev) => prev.map((item) => (savedItems.has(item.id) ? { ...item, ...savedItems.get(item.id) } : item)));
      if (savedNotes.size) setDeliveryNotes((prev) => prev.map((note) => (savedNotes.has(note.id) ? { ...note, ...savedNotes.get(note.id) } : note)));
      setBoqEdits({});
      setDnEdits({});
      markMaterialClean();
      setMaterialSave({ status: 'saved', error: '' });
      toast.success(`${count} quantity change${count === 1 ? '' : 's'} saved`);
    } catch (err) {
      setMaterialSave({ status: 'error', error: getErrorMessage(err, 'Nothing was saved. Try again.') });
    }
  };

  const handleDiscardMaterialChanges = () => resetMaterialEdits();

  const toggleMaterialRow = (rowId) => setExpandedMaterialRows((prev) => {
    const next = new Set(prev);
    if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
    return next;
  });

  const handleConfirmDeleteBoqItem = async (row) => {
    const ok = await confirm({
      title: `Delete ${row.part_number || 'this BOQ line'}?`,
      subtitle: row.description || undefined,
      description: 'The line is removed from this project\'s BOQ.',
      consequences: [
        Number(row.deliveredTotal || 0) > 0 ? `${qtyText(row.deliveredTotal)} pcs delivered on it` : null,
        (row.siteRequests || []).length ? `${row.siteRequests.length} site request${row.siteRequests.length === 1 ? '' : 's'} (deleted too)` : null,
      ].filter(Boolean),
      tone: 'danger',
      confirmLabel: 'Delete line',
      note: 'You can undo this from the toast.',
    });
    if (ok) await handleDeleteBoqItem(row.id);
  };

  const handleConfirmDeleteSiteRequest = async (request, row) => {
    const ok = await confirm({
      title: 'Delete this site request?',
      subtitle: `${row?.part_number || ''} · ${qtyText(request.requested ?? request.qty)} ${request.unit || 'pcs'}${request.due ? ` · due ${formatDate(request.due)}` : ''}`,
      tone: 'danger',
      confirmLabel: 'Delete request',
      note: 'You can undo this from the toast.',
    });
    if (ok) await handleDeleteSiteRequest(request.id);
  };

  // 22 #10: where a BOQ line's part is now — requested, ordered, expected, delivered, in store, issued to site.
  const materialTraceItem = (row) => ({
    label: 'Where is it?',
    icon: Route,
    onSelect: () => setMaterialTrace({ part: row.part_number, description: row.description || '' }),
    disabled: !row.part_number,
    hint: row.part_number ? undefined : 'No part number',
    testId: `material-trace-${row.id}`,
  });
  // Without Material edit rights the ⋯ menu only traces the part.
  const materialReadMenu = (row) => [materialTraceItem(row)];
  const materialRowMenu = (row) => [
    materialTraceItem(row),
    { label: expandedMaterialRows.has(row.id) ? 'Hide history' : 'Show history', icon: History, onSelect: () => toggleMaterialRow(row.id) },
    {
      label: 'New site request',
      icon: SquarePlus,
      onSelect: () => handleOpenSiteRequestDialog(row),
      disabled: row.remaining <= 0,
      hint: row.remaining <= 0 ? 'Nothing remaining' : undefined,
    },
    canCreateTasks && { label: 'Create task', icon: ListTodo, onSelect: () => handleOpenTaskDialog('material') },
    { label: 'Delete line…', icon: Trash2, danger: true, onSelect: () => handleConfirmDeleteBoqItem(row), testId: `material-delete-line-${row.id}` },
  ];

  const handleDeleteDeliveryNote = async (dnId) => {
    const dn = deliveryNotes.find((entry) => entry.id === dnId);
    const label = dn?.dn_number || 'this delivery note';
    const ok = await confirm({
      title: `Delete delivery note ${label}?`,
      subtitle: dn?.delivery_date ? formatDate(dn.delivery_date) : undefined,
      description: 'Its delivered quantities no longer count toward the BOQ lines.',
      consequences: [(dn?.line_items || []).length ? `${dn.line_items.length} delivered line${dn.line_items.length === 1 ? '' : 's'}` : null].filter(Boolean),
      tone: 'danger',
      confirmLabel: 'Delete delivery note',
      note: 'You can undo this from the toast.',
    });
    if (!ok) return false;
    try {
      await api.delete(`/delivery-notes/${dnId}`);
      toast.success('Delivery note deleted');
      setDeliveryNotes((prev) => prev.filter((entry) => entry.id !== dnId));
      // Quantities typed in the deleted note's column go with it.
      setDnEdits((prev) => {
        if (!prev[dnId]) return prev;
        const next = { ...prev };
        delete next[dnId];
        return next;
      });
      fetchData();
      return true;
    } catch (err) {
      notify.error('Could not delete the delivery note', { description: getErrorMessage(err) });
      return false;
    }
  };

  const openAddDeliveryDialog = () => {
    const lineQty = {};
    deliveryBoqItems.forEach((item) => { lineQty[item.id] = 0; });
    setDeliveryForm({
      dn_number: '',
      delivery_date: '',
      supplier: '',
      received_by: '',
      lpo_id: '',
      delivery_type: 'to_stock',
      sub_boq: '',
      notes: '',
      line_qty: lineQty
    });
    setEditingDeliveryNoteId(null);
    setDeliveryPasteText('');
    setAddDnDialogOpen(true);
  };

  const openDeliveryNoteDialog = (dnId) => {
    const dn = deliveryNotes.find((entry) => entry.id === dnId);
    if (!dn) {
      toast.error('Delivery note not found');
      return;
    }
    const lineQty = {};
    deliveryBoqItems.forEach((item) => {
      const itemIdentity = getMainBoqIdentity(item);
      const matchingLines = (dn.line_items || []).filter((entry) => (
        (deliveryIdentityByBoqItemId.get(entry.boq_item_id) || getMainBoqIdentity(entry))
          === itemIdentity
      ));
      lineQty[item.id] = matchingLines.reduce(
        (sum, line) => sum + Number(line.delivered_qty || 0),
        0
      );
    });
    setDeliveryForm({
      dn_number: dn.dn_number || '',
      delivery_date: dn.delivery_date || '',
      supplier: dn.supplier || '',
      received_by: dn.received_by || '',
      lpo_id: dn.lpo_id || '',
      delivery_type: dn.delivery_type || 'to_stock',
      sub_boq: '',
      notes: dn.notes || '',
      line_qty: lineQty
    });
    setEditingDeliveryNoteId(dn.id);
    setDeliveryPasteText('');
    setAddDnDialogOpen(true);
  };

  const handleDeliveryQtyChange = (itemId, value) => {
    setDeliveryForm((prev) => ({
      ...prev,
      line_qty: { ...prev.line_qty, [itemId]: value }
    }));
  };

  const handleApplyDeliveryPaste = () => {
    const parsedRows = parsePastedRowsWithQty(deliveryPasteText);
    if (!parsedRows.length) {
      toast.error('Paste rows with Part No, Description and Qty');
      return;
    }

    const boqByIdentity = new Map(
      deliveryBoqItems.map((item) => [getMainBoqIdentity(item), item])
    );
    const boqByPart = new Map();
    deliveryBoqItems.forEach((item) => {
      const partKey = String(item.part_number || '').trim().toUpperCase();
      if (!boqByPart.has(partKey)) boqByPart.set(partKey, []);
      boqByPart.get(partKey).push(item);
    });

    const nextLineQty = {};
    let unmatched = 0;
    parsedRows.forEach((row) => {
      const partKey = String(row.part_number || '').trim().toUpperCase();
      const exactMatch = boqByIdentity.get(getMainBoqIdentity(row));
      const partMatches = boqByPart.get(partKey) || [];
      const matchedItem = exactMatch || (partMatches.length === 1 ? partMatches[0] : null);
      if (!matchedItem) {
        unmatched += 1;
        return;
      }
      nextLineQty[matchedItem.id] = Number(nextLineQty[matchedItem.id] || 0) + Number(row.qty || 0);
    });

    if (!Object.keys(nextLineQty).length) {
      toast.error('No pasted rows matched BOQ part numbers');
      return;
    }

    setDeliveryForm((prev) => ({
      ...prev,
      line_qty: {
        ...prev.line_qty,
        ...nextLineQty
      }
    }));
    setDeliveryPasteText('');
    toast.success(`Applied ${Object.keys(nextLineQty).length} delivery row(s)${unmatched ? `, ${unmatched} unmatched` : ''}`);
  };

  const handleSubmitDeliveryNote = async (e) => {
    e.preventDefault();
    const lineItems = deliveryBoqItems
      .map((item) => ({
        boq_item_id: item.id,
        part_number: item.part_number,
        description: item.description || '',
        sub_boq: item.sub_boq || '',
        delivered_qty: Number(deliveryForm.line_qty[item.id] || 0)
      }))
      .filter((li) => li.delivered_qty > 0);
    const deliveredSubBoqs = [...new Set(lineItems
      .map((item) => String(item.sub_boq || '').trim())
      .filter(Boolean))];

    if (!deliveryForm.dn_number.trim()) {
      toast.error('DN number is required');
      return;
    }
    try {
      const payload = {
        project_id: id,
        lpo_id: deliveryForm.lpo_id.trim(),
        dn_number: deliveryForm.dn_number.trim(),
        supplier: deliveryForm.supplier.trim(),
        delivery_date: deliveryForm.delivery_date,
        received_by: deliveryForm.received_by.trim(),
        notes: deliveryForm.notes.trim(),
        delivery_type: deliveryForm.delivery_type,
        sub_boq: deliveredSubBoqs.length === 1 ? deliveredSubBoqs[0] : '',
        line_items: lineItems
      };
      if (editingDeliveryNoteId) {
        const updatePayload = { ...payload };
        delete updatePayload.project_id;
        await api.put(`/delivery-notes/${editingDeliveryNoteId}`, updatePayload);
        toast.success('Delivery note saved');
      } else {
        await api.post('/delivery-notes', payload);
        toast.success('Delivery note added');
      }
      setAddDnDialogOpen(false);
      setEditingDeliveryNoteId(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${editingDeliveryNoteId ? 'save' : 'add'} delivery note`);
    }
  };

  const handleSiteRequestDialogChange = (open) => {
    setSiteRequestDialogOpen(open);
    if (!open) {
      setSiteRequestTarget(null);
      setSiteRequestForm(createSiteRequestForm());
    }
  };

  const handleOpenSiteRequestDialog = (row) => {
    setSiteRequestTarget(row);
    setSiteRequestForm(createSiteRequestForm(row));
    setSiteRequestDialogOpen(true);
  };

  const handleCreateSiteRequest = async (e) => {
    e.preventDefault();
    if (!siteRequestTarget) return;

    const requestedQty = Number(siteRequestForm.requested_qty || 0);
    if (requestedQty <= 0) {
      toast.error('Requested qty must be greater than zero');
      return;
    }
    if (!siteRequestForm.due_date) {
      toast.error('Due date is required');
      return;
    }

    try {
      await api.post('/site-requests', {
        project_id: id,
        boq_item_id: siteRequestTarget.id,
        requested_qty: requestedQty,
        due_date: siteRequestForm.due_date,
        notes: siteRequestForm.notes.trim()
      });
      toast.success('Site request added');
      handleSiteRequestDialogChange(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add site request');
    }
  };

  const handleDeleteSiteRequest = async (requestId) => {
    try {
      await api.delete(`/site-requests/${requestId}`);
      toast.success('Site request deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete site request');
    }
  };

  const openInvoicePanel = (mode, invoice = null, focus = null) => {
    setInvoicePanel({ open: true, mode, invoiceId: invoice?.id || null, focus });
  };

  /** The invoice drawer's Save (07 #6): add (POST with photos), edit / raise (PUT, then new photos). */
  const handleSaveInvoicePanel = async ({ files, ...fields }) => {
    if (invoicePanel.mode === 'add') {
      const scope = invoiceScope;
      if (scope === 'total') throw new Error('Pick Base scope or one variation first');
      const formData = new FormData();
      formData.append('project_id', id);
      if (scope !== 'base') formData.append('variation_id', scope);
      formData.append('invoice_type', fields.invoice_type);
      formData.append('status', fields.status);
      formData.append('amount', String(fields.amount));
      formData.append('reference', fields.reference);
      formData.append('note', fields.note);
      formData.append('invoice_type_percentages', JSON.stringify(fields.invoice_type_percentages || {}));
      if (fields.due_date) formData.append('due_date', fields.due_date);
      if (files.reference) formData.append('invoice_reference_photo', files.reference);
      if (files.invoice) formData.append('invoice_photo', files.invoice);
      await api.post(scope === 'base' ? '/invoices' : '/variation-invoices', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Invoice ${fields.reference || fields.invoice_type} added`);
    } else {
      const invoice = [...invoices, ...variationInvoices].find((entry) => entry.id === invoicePanel.invoiceId);
      if (!invoice) throw new Error('This invoice no longer exists. Reload the page.');
      const path = invoice.variation_id ? `/variation-invoices/${invoice.id}` : `/invoices/${invoice.id}`;
      const before = {
        invoice_type: normalizeInvoiceTypeValue(invoice.invoice_type),
        invoice_type_percentages: normalizeInvoiceTypePercentages(invoice.invoice_type_percentages),
        status: invoice.status || 'Not Started',
        amount: Number(invoice.amount || 0),
        reference: invoice.reference || '',
        note: invoice.note || '',
      };
      const fieldsChanged = JSON.stringify(before) !== JSON.stringify({
        ...fields,
        invoice_type_percentages: normalizeInvoiceTypePercentages(fields.invoice_type_percentages),
      });
      if (fieldsChanged) await api.put(path, fields);
      if (files.reference || files.invoice) {
        const formData = new FormData();
        if (files.reference) formData.append('invoice_reference_photo', files.reference);
        if (files.invoice) formData.append('invoice_photo', files.invoice);
        await api.post(`${path}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(invoicePanel.mode === 'raise' ? `Invoice ${fields.reference} raised` : `Invoice ${fields.reference || fields.invoice_type} saved`);
    }
    fetchData();
  };

  /** "Sync to QuickBooks" for several invoices (07 #9): one at a time, then one summary toast. */
  const handleSyncInvoices = async (list = []) => {
    const configured = Boolean(quickBooksStatus?.configured && quickBooksStatus?.connected);
    const eligible = list.filter((invoice) => canSyncToQuickBooks(invoice, { configured }).ok);
    if (!eligible.length) {
      notify.info('Nothing to sync', { description: configured ? 'These invoices are already in QuickBooks, not invoiced yet, or have no amount.' : 'QuickBooks is not connected.' });
      return;
    }
    const failed = [];
    for (let index = 0; index < eligible.length; index += 1) {
      const invoice = eligible[index];
      setQbSync({ done: index, total: eligible.length });
      try {
        await api.post(invoice.variation_id ? `/quickbooks/variation-invoices/${invoice.id}/sync` : `/quickbooks/invoices/${invoice.id}/sync`);
      } catch (err) {
        failed.push(`${invoice.reference || invoice.invoice_type}: ${getErrorMessage(err)}`);
      }
    }
    setQbSync(null);
    setSelectedInvoiceIds([]);
    const synced = eligible.length - failed.length;
    if (failed.length) {
      notify.error(`${synced} of ${eligible.length} invoice${eligible.length === 1 ? '' : 's'} synced to QuickBooks`, { description: failed.slice(0, 3).join(' · ') });
    } else {
      notify.success(`${synced} invoice${synced === 1 ? '' : 's'} synced to QuickBooks`);
    }
    void ensureResources(['invoices', 'variationInvoices'], { refresh: true });
  };

  const handleExportInvoices = (rows) => {
    const scopeName = invoiceScope === 'base' ? 'base' : invoiceScope === 'total' ? 'all' : (variations.find((variation) => variation.id === invoiceScope)?.vo_number || 'variation');
    downloadTextFile(
      `${(project?.name || 'project').replace(/[^\w.-]+/g, '_')}-invoices-${scopeName}.csv`,
      `\uFEFF${invoicesToCsv(rows, { scopeValue: scopeTotalValue })}`,
      'text/csv;charset=utf-8',
    );
  };

  // 11 #4 / #5: Add / Edit variation in a drawer (always available — free-text items when there is no BOQ).
  const [variationPanel, setVariationPanel] = useState({ open: false, variation: null, prefill: null });
  const [expandedVariationIds, setExpandedVariationIds] = useState([]);
  // `prefill` (05 #8 "Draft VO-005"): a new variation with the Material shortages already filled in.
  const openVariationPanel = (variation = null, prefill = null) => {
    void ensureResources(['boq', 'deliveryNotes']);
    setVariationPanel({ open: true, variation, prefill: variation ? null : prefill });
  };
  const toggleVariationRow = (variationId) => setExpandedVariationIds((prev) => (
    prev.includes(variationId) ? prev.filter((entry) => entry !== variationId) : [...prev, variationId]
  ));
  const handleSaveVariationPanel = async (payload) => {
    const editing = variationPanel.variation;
    try {
      if (editing) {
        await api.put(`/variations/${editing.id}`, payload);
        toast.success(`${payload.vo_number} saved`);
      } else {
        const res = await api.post('/variations', { project_id: id, ...payload });
        toast.success(`${payload.vo_number} added`);
        if (res.data?.id) setExpandedVariationIds((prev) => [...prev, res.data.id]);
      }
    } catch (err) {
      throw new Error(getErrorMessage(err, editing ? 'Could not save the variation' : 'Could not add the variation'));
    }
    fetchData();
  };

  const handleOpenVariationInvoicing = (variation) => {
    setInvoiceScope(variation.id);
    setActiveTab('invoicing');
  };
  // 11 #7: "Raise" in the VO's Invoicing cell — Invoicing on that VO's scope with the add drawer open.
  const handleRaiseVariationInvoice = (variation) => {
    setInvoiceScope(variation.id);
    setActiveTab('invoicing');
    openInvoicePanel('add');
  };
  // 11 #6: a recorded / removed approval step answers with the VO as saved — swap it in (status may change).
  const handleVariationTrailChanged = (updated) => {
    if (!updated?.id) return;
    setVariations((prev) => prev.map((entry) => (entry.id === updated.id ? { ...entry, ...updated } : entry)));
  };

  const handleDeleteVariation = async (variationId) => {
    const variation = variations.find((entry) => entry.id === variationId);
    // The server deletes the VO's invoices with it (DELETE /variations/{id}) — say so, and name each one. Only an
    // Admin / Super Admin may delete a VO that has invoices (BUG-006: the server answers 409 to anyone else).
    const linkedInvoices = variationInvoices.filter((invoice) => invoice.variation_id === variationId);
    if (linkedInvoices.length && !isAdminLike(user)) {
      notify.info(`${variation?.vo_number || 'This variation'} still has ${linkedInvoices.length === 1 ? 'an invoice' : `${linkedInvoices.length} invoices`}`, {
        description: 'Deleting invoices needs an Admin’s approval. Request deletion of each invoice first, or ask an Admin to delete the variation.',
      });
      return;
    }
    const billedInvoices = linkedInvoices.filter((invoice) => ['Submitted', 'Payment Received'].includes(invoice.status || ''));
    const ok = await confirm({
      title: `Delete ${variation?.vo_number || 'this variation'}?`,
      subtitle: [variation?.description, variation?.status].filter(Boolean).join(' · '),
      description: linkedInvoices.length ? (
        <div className="space-y-2" data-testid="variation-delete-invoices">
          <p>
            The variation is removed from this project, and{' '}
            <b className="font-semibold text-status-red-ink">
              {linkedInvoices.length === 1 ? '1 variation invoice is' : `${linkedInvoices.length} variation invoices are`} deleted with it
            </b>:
          </p>
          <ul className="divide-y divide-line-2 rounded-xl border border-line text-[13px]">
            {linkedInvoices.map((invoice) => (
              <li key={invoice.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-3 py-2">
                <span className="font-mono font-semibold text-ink">{invoice.reference || 'No ref'}</span>
                <span className="text-ink-3">·</span>
                <span className="min-w-0 text-ink-2">{invoice.invoice_type || 'Invoice'}</span>
                <span className="text-ink-3">·</span>
                <span className="font-semibold tabular-nums text-ink">{formatNumber(Number(invoice.amount || 0), { decimals: 2 })}</span>
                <span className="ml-auto"><StatusPill status={invoice.status || 'Not Started'} domain="invoice" size="sm" /></span>
              </li>
            ))}
          </ul>
          {billedInvoices.length ? (
            <p className="text-[12.5px] font-semibold text-status-red-ink">
              {billedInvoices.length === 1 && linkedInvoices.length === 1 ? 'It is' : `${billedInvoices.length} of them ${billedInvoices.length === 1 ? 'is' : 'are'}`} already
              {' '}{billedInvoices.every((invoice) => invoice.status === 'Payment Received') ? 'paid' : 'submitted to the client'} — check with Finance before deleting.
            </p>
          ) : null}
        </div>
      ) : 'The variation is removed from this project.',
      consequences: [
        // the amount only for roles with Invoicing, and only when the VO carries one (never "0.00 AED")
        variation && canViewFinance && !variation.finance_hidden && (variation.total_value ?? variation.financial_impact) !== undefined && (variation.total_value ?? variation.financial_impact) !== null
          ? `${formatNumber(Number(variation.total_value ?? variation.financial_impact ?? 0), { decimals: 2 })} AED from the contract value`
          : null,
        variation?.items?.length ? `${variation.items.length} item line${variation.items.length === 1 ? '' : 's'} counted in Material` : null,
      ].filter(Boolean),
      tone: 'danger',
      confirmLabel: linkedInvoices.length ? `Delete VO and ${linkedInvoices.length === 1 ? 'its invoice' : `${linkedInvoices.length} invoices`}` : 'Delete variation',
      // A submitted / paid invoice goes with it: typing the VO number confirms.
      ...(billedInvoices.length && variation?.vo_number ? { requireText: variation.vo_number } : {}),
    });
    if (!ok) return;
    try {
      await api.delete(`/variations/${variationId}`);
      toast.success('Variation deleted');
      fetchData();
    } catch (err) {
      notify.error('Could not delete the variation', { description: getErrorMessage(err) });
    }
  };

  // 08 #9: names are text; Rename (row button, ⋯ menu) or a double-click turns one name into a field.
  const focusStructureName = (nodeId) => {
    setEditingStructureDevice(null);
    setRenamingStructureId(nodeId);
  };
  const stopStructureRename = () => setRenamingStructureId(null);

  const focusStageCard = (stageId, lineId = null) => {
    const card = document.getElementById(`stage-card-${stageId}`);
    if (!card) return;
    card.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setHighlightedStageId(stageId);
    window.clearTimeout(stageHighlightTimerRef.current);
    stageHighlightTimerRef.current = window.setTimeout(() => setHighlightedStageId(null), 1800);
    if (lineId) {
      window.setTimeout(() => {
        const field = document.querySelector(`[data-stage-line-name="${lineId}"]`);
        if (field) field.focus({ preventScroll: true });
      }, 450);
    }
  };

  // A link with &stage=<id> (a task's "Open in project") scrolls to that stage card once it is on screen.
  const linkedStageId = searchParams.get('stage') || '';
  const linkedStageDoneRef = useRef('');
  useEffect(() => {
    if (!linkedStageId || linkedStageDoneRef.current === linkedStageId || activeTab !== 'stages' || !project) return undefined;
    const timer = window.setTimeout(() => {
      if (!document.getElementById(`stage-card-${linkedStageId}`)) return;
      linkedStageDoneRef.current = linkedStageId;
      focusStageCard(linkedStageId);
    }, 0);
    return () => window.clearTimeout(timer);
  });

  /** Open the stage drawer (04 #4) — on a stage row, "Update <next stage>", or an item from "Waiting on". */
  const openStagePanel = (stage, { startWith = null, focusLineId = null } = {}) => {
    if (!stage?.id) return;
    setStagePanel({ open: true, stageId: stage.id, startWith, focusLineId });
    void ensureResources(['tasks']);
  };

  /** The drawer's single Save: status and items in ONE request → one history entry → one Undo toast. */
  const handleSaveStagePanel = async ({ status, lines }) => {
    const stageId = stagePanel.stageId;
    const stage = (project?.stages || []).find((entry) => entry.id === stageId);
    if (!stage) throw new Error('This stage no longer exists. Reload the page.');
    const res = await api.put(`/projects/${id}/stages/${stageId}`, {
      ...(status !== undefined ? { status } : {}),
      ...(lines !== undefined ? { lines } : {}),
    });
    const saved = res.data?.stage || {};
    setProject((prev) => (prev ? {
      ...prev,
      // A new updated_at refreshes "Recent activity" (keyed on it) with the entry this save just wrote.
      updated_at: res.data?.updated_at || new Date().toISOString(),
      stages: (prev.stages || []).map((entry) => (entry.id === stageId ? {
        ...entry,
        ...(status !== undefined ? { status: saved.status || status } : {}),
        ...(lines !== undefined ? { lines: saved.lines || lines } : {}),
      } : entry)),
    } : prev));
    // A plain success toast is folded into the shell's "… · Undo" toast for this change.
    toast.success(`${stage.name} saved`);
  };

  const handleClearStageItems = async (stage, summary) => {
    const ok = await confirm({
      title: `Delete all items of ${stage.name}?`,
      subtitle: `${summary.count} item${summary.count === 1 ? '' : 's'}${summary.names.length ? ` · ${summary.names.slice(0, 3).join(', ')}` : ''}`,
      description: 'The stage keeps its status; every item, its sub-items, extra fields and file references are removed.',
      tone: 'danger',
      confirmLabel: `Delete ${summary.count} item${summary.count === 1 ? '' : 's'}`,
      note: 'You can undo this from the toast.',
    });
    if (!ok) return;
    try {
      const res = await api.put(`/projects/${id}/stages/${stage.id}`, { lines: [] });
      setProject((prev) => (prev ? {
        ...prev,
        updated_at: res.data?.updated_at || new Date().toISOString(),
        stages: (prev.stages || []).map((entry) => (entry.id === stage.id ? { ...entry, lines: [] } : entry)),
      } : prev));
      toast.success(`Items of ${stage.name} deleted`);
    } catch (err) {
      notify.error('Could not delete the items', { description: getErrorMessage(err) });
    }
  };

  const copyText = async (value, message) => {
    try {
      await navigator.clipboard.writeText(value);
      notify.success(message);
    } catch {
      notify.error('Could not copy the link', { description: value });
    }
  };
  const copyStageLink = (stage) => copyText(`${window.location.origin}/projects/${id}?tab=stages&stage=${stage.id}`, `Link to ${stage.name} copied`);
  const copyPageLink = () => copyText(window.location.href, 'Link to this tab copied');

  const handleOpenReference = async (reference) => {
    const ref = (reference || '').trim();
    if (!ref) return;

    if (isLocalAbsolutePath(ref)) {
      try {
        await api.post('/open-file', { path: ref });
        toast.success('Path opened');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to open path');
      }
      return;
    }

    const url = isWebUrl(ref) ? ref : normalizeReferenceUrl(ref);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleOpenProjectEditor = () => {
    void ensureResources(['users', 'fieldOptions'], { quiet: true });
    if (project) setProjectForm(projectToForm(project));
    setProjectDialogOpen(true);
  };

  const handleOpenTaskDialog = (source, invoice = null) => {
    void ensureResources(['users'], { quiet: true });
    const next = createEmptyProjectTaskForm(source);
    if (source === 'invoicing' && invoice) {
      next.invoice_id = invoice.id || '';
      next.invoice_reference = invoice.reference || '';
    }
    setTaskForm(next);
    setTaskDialogOpen(true);
  };

  const handleOpenStageTaskDialog = (stage, line) => {
    void ensureResources(['users'], { quiet: true });
    const next = createEmptyProjectTaskForm('stage');
    next.stage_id = stage.id || '';
    next.stage_name = stage.name || '';
    next.stage_line_id = line.id || '';
    next.stage_line_name = line.name || '';
    next.name = line.name || stage.name || '';
    next.description = line.note || '';
    setTaskForm(next);
    setTaskDialogOpen(true);
  };

  const handleCreateProjectTask = async (e) => {
    e.preventDefault();
    const taskName = String(taskForm.name || '').trim();
    if (!taskName) {
      toast.error('Task name is required');
      return;
    }
    const assignedUser = platformUsers.find((entry) => entry.id === taskForm.assigned_to_user_id);
    try {
      const res = await api.post('/tasks', {
        project_id: id,
        source: taskForm.source,
        name: taskName,
        description: String(taskForm.description || '').trim(),
        assigned_to_user_id: taskForm.assigned_to_user_id || '',
        assigned_to_name: assignedUser?.name || '',
        due_date: taskForm.due_date || '',
        invoice_id: taskForm.invoice_id || '',
        invoice_reference: taskForm.invoice_reference || '',
        stage_id: taskForm.stage_id || '',
        stage_name: taskForm.stage_name || '',
        stage_line_id: taskForm.stage_line_id || '',
        stage_line_name: taskForm.stage_line_name || ''
      });
      setProjectTasks((prev) => [res.data, ...prev]);
      toast.success('Task added');
      setTaskDialogOpen(false);
      setTaskForm(createEmptyProjectTaskForm('material'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add task');
    }
  };

  // SidePanel onSave: a thrown error stays in the drawer footer (and the drawer stays open).
  const handleSaveProjectDetails = async () => {
    if (!String(projectForm.name || '').trim()) throw new Error('Project name is required');
    const payload = {
      name: projectForm.name.trim(),
      client: projectForm.client.trim(),
      consultant: projectForm.consultant.trim(),
      contractor: projectForm.contractor.trim(),
      project_engineer: projectForm.project_engineer.trim(),
      senior_project_engineer: projectForm.senior_project_engineer.trim(),
      location: projectForm.location.trim(),
      waze_url: projectForm.waze_url.trim(),
      system_type: projectForm.system_type.trim(),
      scope: Array.isArray(projectForm.scope) ? projectForm.scope : [],
      tags: projectForm.tags,
      status: projectForm.status,
      start_date: projectForm.start_date || null,
      end_date: projectForm.end_date || null,
      notes: projectForm.notes.trim(),
      // Money only from roles with Invoicing: the others never had the fields, and sending '' / 0 would wipe them.
      ...(canViewFinance ? {
        total_project_value: Number(projectForm.original_scope_value || 0),
        original_scope_value: Number(projectForm.original_scope_value || 0),
        payment_terms: normalizeInvoiceTypeValue(projectForm.payment_terms),
      } : {}),
      salesman: projectForm.salesman,
      files_root_path: projectForm.files_root_path.trim(),
      custom_fields: normalizeProjectCustomFields(projectForm.custom_fields)
        .map((field) => ({
          ...field,
          title: field.title.trim(),
          content: field.content.trim()
        }))
        .filter((field) => field.title || field.content),
      allow_sub_boq: Boolean(projectForm.allow_sub_boq),
      allow_duplicate_device_addresses: Boolean(projectForm.allow_duplicate_device_addresses)
    };
    await api.put(`/projects/${id}`, payload);
    toast.success('Project details updated');
    projectDialogOpenRef.current = false;
    fetchData();
  };

  // Timeline handover banner: change only the end date / status (partial PUT), keep everything else.
  const handleSaveEndDate = async (nextDate) => {
    try {
      const res = await api.put(`/projects/${id}`, { end_date: nextDate });
      // Merge only what changed: unsaved Execution / Structure edits stay (see the reset effects).
      mergeSavedProjectFields(res.data, ['end_date'], { end_date: nextDate });
      toast.success(`Handover date set to ${formatDate(nextDate)}`);
      return true;
    } catch (err) {
      notify.error('Could not update the handover date', { description: getErrorMessage(err) });
      return false;
    }
  };

  const handleSetProjectStatus = async (nextStatus) => {
    if (nextStatus === 'On Hold') {
      const ok = await confirm({
        title: `Put “${project?.name || 'this project'}” on hold?`,
        subtitle: project?.end_date ? `Planned handover ${formatDate(project.end_date)}` : undefined,
        description: 'The project stays visible with the status On Hold, so it no longer shows as an overdue active project. You can set it Active again any time.',
        confirmLabel: 'Put On Hold',
      });
      if (!ok) return;
    }
    setProjectStatusSaving(true);
    try {
      const res = await api.put(`/projects/${id}`, { status: nextStatus });
      mergeSavedProjectFields(res.data, ['status'], { status: nextStatus });
      toast.success(`${project?.name || 'Project'} is now ${nextStatus}`);
    } catch (err) {
      notify.error('Could not change the project status', { description: getErrorMessage(err) });
    } finally {
      setProjectStatusSaving(false);
    }
  };

  // 10 #1: "set dates in Execution →" opens the table with the open main tasks that have no dates already ticked,
  // so the bulk bar's Set dates is one click away (its date popover opens too).
  const handleOpenExecutionDates = () => {
    const undated = executionTasks.filter((task) => !task.parent_id && Number(task.level || 1) === 1
      && !['Done', 'Not required'].includes(task.status || 'Not Started')
      && !task.start_date && !task.due_date);
    setExecutionColumns((prev) => normalizeExecutionColumns([...prev, 'start_date', 'due_date']));
    if (isPhone) setPhoneExecutionView('list');
    else if (executionView !== 'table') setExecutionView('table');
    setSelectedExecutionTaskIds(undated.map((task) => task.id));
    setActiveTab('execution');
    window.requestAnimationFrame(() => {
      document.querySelector(`[data-testid="${isPhone ? 'execution-cards' : 'execution-table'}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (undated.length && canEditExecutionModule) {
        window.setTimeout(() => document.querySelector('[data-testid="execution-bulk-dates"]')?.click(), 450);
      }
    });
  };

  const [filesPathSaving, setFilesPathSaving] = useState(false);
  const handleSaveFolderPath = async (nextPath) => {
    setFilesPathSaving(true);
    try {
      const folderChanged = folderPathKey(nextPath) !== folderPathKey(project?.files_root_path);
      const res = await api.put(`/projects/${id}`, { files_root_path: nextPath });
      // A new folder is "not scanned yet": the server drops the old folder's scan summary — so does the page.
      mergeSavedProjectFields(res.data, ['files_root_path', 'onedrive_status', ...(folderChanged ? ['files_scan_summary'] : [])], {
        files_root_path: nextPath,
        ...(folderChanged ? { files_scan_summary: null } : {}),
      });
      setFilesPathDraft(res.data?.files_root_path ?? nextPath);
      setFilesAudit(null);
      if (folderChanged) setSelectedFolderPath('');
      filesScans.reload();
      toast.success(nextPath ? 'Project folder saved' : 'Project folder cleared');
      return true;
    } catch (err) {
      notify.error('Could not save the project folder', { description: getErrorMessage(err) });
      return false;
    } finally {
      setFilesPathSaving(false);
    }
  };

  const handleRequestDisconnectOneDrive = async () => {
    const ok = await confirm({
      title: 'Disconnect OneDrive?',
      subtitle: onedriveAccountLabel(project?.onedrive_status),
      description: 'The app stops reading this project\'s OneDrive folder until someone connects again. Files in OneDrive are not touched.',
      confirmLabel: 'Disconnect',
      tone: 'danger',
    });
    if (ok) await handleDisconnectOneDrive();
  };

  const handleConnectOneDrive = async () => {
    setOnedriveAuthLoading(true);
    try {
      const res = await api.get(`/projects/${id}/onedrive-auth-url`, {
        params: { frontend_origin: window.location.origin }
      });
      const popup = window.open(res.data.auth_url, 'onedrive-auth', 'width=640,height=760');
      if (!popup) {
        setOnedriveAuthLoading(false);
        toast.error('Allow pop-ups to connect OneDrive');
        return;
      }
      const timer = window.setInterval(() => {
        if (!popup.closed) return;
        window.clearInterval(timer);
        setOnedriveAuthLoading(false);
      }, 500);
    } catch (err) {
      setOnedriveAuthLoading(false);
      toast.error(getErrorMessage(err, 'Failed to start OneDrive authentication'));
    }
  };

  const handleDisconnectOneDrive = async () => {
    try {
      await api.delete(`/projects/${id}/onedrive-auth`);
      setFilesAudit(null);
      setProject((prev) => (
        prev ? { ...prev, onedrive_status: { connected: false, folder_path: prev.files_root_path || '' } } : prev
      ));
      toast.success('OneDrive disconnected');
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to disconnect OneDrive'));
    }
  };

  const handleScanProjectFiles = async () => {
    const nextPath = filesPathDraft.trim();
    if (!nextPath) {
      toast.error('OneDrive folder path is required');
      return;
    }
    if (!project?.onedrive_status?.connected) {
      toast.error('Connect OneDrive first');
      return;
    }

    setFilesAuditLoading(true);
    try {
      const res = await api.get(`/projects/${id}/files-audit`, { params: { path: nextPath } });
      setFilesAudit(res.data || null);
      if ((project?.files_root_path || '') !== nextPath) {
        setProject((prev) => (prev ? { ...prev, files_root_path: nextPath } : prev));
      }
      toast.success('Project files scanned');
    } catch (err) {
      setFilesAudit(null);
      toast.error(getErrorMessage(err, 'Failed to scan project files'));
    } finally {
      setFilesAuditLoading(false);
    }
  };

  // 06 #5: from a folder chip / Stage evidence to the stage on the Stages tab
  const jumpToStage = async (stageId) => {
    await handleTabChange('stages');
    window.setTimeout(() => focusStageCard(stageId), 150);
  };

  // 06 #6: create the folders the last scan found missing, then scan again
  const handleCreateMissingFolders = async () => {
    const missing = (filesAudit || filesScans.data?.latest)?.summary?.missing_paths
      || buildFolderChecklist(filesAudit || filesScans.data?.latest).rows.filter((row) => row.status === 'missing').map((row) => row.path);
    const ok = await confirm({
      title: `Create ${missing.length} missing folder${missing.length === 1 ? '' : 's'} on OneDrive?`,
      subtitle: project?.files_root_path || '',
      description: 'They are created with the required names inside the project folder. Nothing else on OneDrive changes.',
      consequences: missing.slice(0, 8),
      confirmLabel: 'Create folders',
    });
    if (!ok) return;
    setCreatingFolders(true);
    try {
      const res = await api.post(`/projects/${id}/onedrive/folders`, {});
      notify.success(res.data?.message || 'Folders created');
      await handleScanProjectFiles();
    } catch (err) {
      notify.error('Could not create the folders', { description: getErrorMessage(err) });
    } finally {
      setCreatingFolders(false);
    }
  };

  // 05 #10: the lines checked in "Delivery note from photo" fill the usual Add Delivery Note form
  const handleConfirmDnPhoto = ({ dn_number: dnNumber, delivery_date: deliveryDate, supplier, line_qty: quantities }) => {
    const lineQty = {};
    deliveryBoqItems.forEach((item) => { lineQty[item.id] = quantities[item.id] || 0; });
    setDeliveryForm({
      dn_number: dnNumber || '',
      delivery_date: deliveryDate || '',
      supplier: supplier || '',
      received_by: '',
      lpo_id: '',
      delivery_type: 'to_stock',
      sub_boq: '',
      notes: 'Quantities read from a photo of the delivery note and checked before saving.',
      line_qty: lineQty,
    });
    setEditingDeliveryNoteId(null);
    setDeliveryPasteText('');
    setAddDnDialogOpen(true);
  };

  const handleExecutionStructureChange = (updater) => {
    setExecutionStructure((prev) => updater(prev));
  };

  const persistExecutionStructure = async (nextStructure) => {
    const requestSeq = ++structureSaveRequestSeqRef.current;
    const payloadStructure = (isBmsProject || hasBmsStructureContent(nextStructure))
      ? prepareBmsStructureForSave(nextStructure)
      : nextStructure;
    const res = await api.put(`/projects/${id}/execution-structure`, {
      execution_structure: payloadStructure
    });

    // Ignore outdated responses that arrive after a newer save request.
    if (requestSeq !== structureSaveRequestSeqRef.current) {
      return res.data;
    }

    const syncedStructure = normalizeStructurePanelTypes(res.data.execution_structure || createExecutionStructure());
    setExecutionStructure(syncedStructure);
    setExecutionTasks(res.data.execution_tasks || []);
    // The saved version is the new baseline of "N unsaved changes" (structure and execution).
    mergeSavedProjectFields(res.data, ['execution_structure', 'execution_tasks', 'execution_structure_save_token']);
    return res.data;
  };

  // 08 #4: Save structure (toolbar, save bar, "save before leaving?", import wizard). `rethrow` → the caller
  // shows the error (dialog / wizard); otherwise it stays on the save bar and in a toast.
  const saveStructure = async ({ rethrow = false } = {}) => {
    if (structureSaving) return false;
    setStructureSaving(true);
    setStructureSaveStatus({ status: 'saving', error: '' });
    try {
      await persistExecutionStructure(executionStructure);
      setRenamingStructureId(null);
      setEditingStructureDevice(null);
      setStructureSaveStatus({ status: 'saved', error: '' });
      markStructureClean();
      toast.success('Project structure updated');
      return true;
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to update project structure');
      setStructureSaveStatus({ status: 'error', error: message });
      if (rethrow) throw new Error(message);
      notify.error('Could not save the structure', { description: message });
      return false;
    } finally {
      setStructureSaving(false);
    }
  };
  // 08 #8: a save that removes sub-items with work asks first (the save bar shows it in red as well).
  const handleSaveExecutionStructure = async () => {
    const impact = structureImpactSummary(structureImpact);
    if (impact.removedWithWork > 0) {
      const ok = await confirm({
        title: `Remove ${impact.removedWithWork} sub-item${impact.removedWithWork === 1 ? '' : 's'} with work?`,
        description: 'Saving this structure deletes Execution sub-items that already have a status, a name in Done by / Checked by, dates or comments. Undo in History can bring them back.',
        consequences: [impact.text.replace(/^On save: /, '')],
        tone: 'danger',
        confirmLabel: 'Save and remove them',
        cancelLabel: 'Keep editing',
      });
      if (!ok) return false;
    }
    return saveStructure();
  };

  const discardStructureChanges = ({ quiet = false } = {}) => {
    const before = executionStructure;
    const count = structureDirtyCount;
    setRenamingStructureId(null);
    setEditingStructureDevice(null);
    setExecutionStructure(savedStructure);
    setStructureSaveStatus({ status: 'idle', error: '' });
    if (!quiet && count) {
      notify.success(`Discarded ${count} structure change${count === 1 ? '' : 's'}`, {
        undo: () => setExecutionStructure(before),
        undoneMessage: 'Your structure changes are back',
      });
    }
  };

  // 08 #5: open a search result — its parents expand, the row scrolls into view and is highlighted.
  const structureFocusTimerRef = useRef(null);
  const openStructureNode = (entry) => {
    if (!entry) return;
    // 08 #1: a device opens its panel / room / floor in the right pane; a container opens itself.
    setStructureSelectedId(['device', 'sub-item'].includes(entry.kind) ? entry.parentId : entry.id);
    setCollapsedStructureSections((prev) => {
      const next = { ...prev };
      (entry.collapseKeys || []).forEach((key) => { next[key] = false; });
      return next;
    });
    setStructureFocusId(entry.id);
    window.clearTimeout(structureFocusTimerRef.current);
    structureFocusTimerRef.current = window.setTimeout(() => setStructureFocusId(null), 3200);
    window.setTimeout(() => {
      const node = document.querySelector(`[data-structure-node="${entry.id}"]`);
      if (node) {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (typeof node.focus === 'function') node.focus({ preventScroll: true });
      }
    }, 80);
  };
  useEffect(() => () => window.clearTimeout(structureFocusTimerRef.current), []);
  const structureFocusClass = (nodeId) => (structureFocusId === nodeId ? 'bg-brand-soft ring-2 ring-[color:var(--brand)] ring-offset-2 ring-offset-[color:var(--surface)]' : '');

  // 08 #10: the Import wizard applied the file — same follow-up as the old one-step import.
  const applyImportedStructure = (projectData, file) => {
    const nextStructure = normalizeStructurePanelTypes(projectData?.execution_structure || createExecutionStructure());
    setProject((prev) => ({ ...(prev || {}), ...(projectData || {}), overview: prev?.overview }));
    setExecutionStructure(nextStructure);
    setExecutionTasks(projectData?.execution_tasks || []);
    const importedIds = collectImportedStructureIds(nextStructure);
    setHighlightedStructureIds(importedIds);
    setCollapsedStructureSections((prev) => {
      const next = { ...prev };
      importedIds.buildings.forEach((buildingId) => { next[getBuildingCollapseKey(buildingId)] = false; });
      return next;
    });
    const csvContent = String(projectData?.csv_content || '');
    const csvFilename = String(projectData?.csv_filename || '');
    if (csvContent && csvFilename) {
      downloadTextFile(csvFilename, csvContent, 'text/csv;charset=utf-8');
    }
    setTtlImportErrorLog('');
    toast.success(`Structure imported from ${file?.name || 'the file'}`);
  };

  const handleAddProjectCustomField = () => {
    setProjectForm((prev) => ({
      ...prev,
      custom_fields: [...normalizeProjectCustomFields(prev.custom_fields), createProjectCustomField()]
    }));
  };

  const handleProjectCustomFieldChange = (fieldId, field, value) => {
    setProjectForm((prev) => ({
      ...prev,
      custom_fields: normalizeProjectCustomFields(prev.custom_fields).map((customField) => (
        customField.id === fieldId ? { ...customField, [field]: value } : customField
      ))
    }));
  };

  const handleDeleteProjectCustomField = (fieldId) => {
    setProjectForm((prev) => ({
      ...prev,
      custom_fields: normalizeProjectCustomFields(prev.custom_fields).filter((customField) => customField.id !== fieldId)
    }));
  };

  const handleProjectScopeToggle = (scopeItem, checked) => {
    setProjectForm((prev) => {
      const currentScope = Array.isArray(prev.scope) ? prev.scope : [];
      if (checked) return { ...prev, scope: [...new Set([...currentScope, scopeItem])] };
      return { ...prev, scope: currentScope.filter((item) => item !== scopeItem) };
    });
  };

  const handleAllowDuplicateDeviceAddressesChange = async (checked) => {
    if (duplicateAddressSettingSaving) return;
    const nextValue = Boolean(checked);
    const previousValue = allowDuplicateDeviceAddresses;
    setDuplicateAddressSettingSaving(true);
    setProject((prev) => (
      prev ? { ...prev, allow_duplicate_device_addresses: nextValue } : prev
    ));
    setProjectForm((prev) => ({
      ...prev,
      allow_duplicate_device_addresses: nextValue
    }));
    try {
      const res = await api.put(`/projects/${id}`, {
        allow_duplicate_device_addresses: nextValue
      });
      setProject((prev) => ({
        ...prev,
        ...(res.data && typeof res.data === 'object' ? res.data : {}),
        allow_duplicate_device_addresses: nextValue
      }));
      toast.success(nextValue ? 'Duplicate device addresses allowed' : 'Duplicate device addresses blocked');
    } catch (err) {
      setProject((prev) => (
        prev ? { ...prev, allow_duplicate_device_addresses: previousValue } : prev
      ));
      setProjectForm((prev) => ({
        ...prev,
        allow_duplicate_device_addresses: previousValue
      }));
      toast.error(err.response?.data?.detail || 'Failed to update device address validation');
    } finally {
      setDuplicateAddressSettingSaving(false);
    }
  };

  const isImportedStructureNode = (level, id) => (highlightedStructureIds[level] || []).includes(id);

  // 08 #10: Import opens the wizard (Upload › Map columns › Preview › Apply) instead of a file picker + confirm.
  const handleTriggerTtlImport = () => {
    if (!isStageLinesEditMode) return;
    setStructureImportOpen(true);
  };

  const handleExportStructureExcel = async () => {
    try {
      const token = localStorage.getItem('token');
      const requestUrl = api.getUri({ url: `/projects/${id}/execution-structure/export-excel` });
      const response = await fetch(requestUrl, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (!response.ok) {
        let payload = null;
        try {
          payload = await response.json();
        } catch {
          payload = null;
        }
        if (response.status === 404) {
          exportStructureExcelClientSide(executionStructure, project?.name);
          toast.success('Structure exported');
          return;
        }
        throw new Error(payload?.detail || `Structure export failed (${response.status})`);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/i);
      const filename = match?.[1] || `${(project?.name || 'project').replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'project'}-structure.xlsx`;
      downloadBlobFile(filename, blob);
      toast.success('Structure exported');
    } catch (err) {
      if (String(err?.message || '').includes('Failed to fetch')) {
        exportStructureExcelClientSide(executionStructure, project?.name);
        toast.success('Structure exported');
        return;
      }
      toast.error(err.message || 'Failed to export structure');
    }
  };

  // Structure deletes: in-app confirm, then an Undo toast that puts exactly that node back where it was.
  // Nothing reaches the server until Save Structure (unchanged behaviour).
  const removeStructureNodeWithUndo = async ({ steps, kind, fallbackName, afterRemove }) => {
    const located = findAtPath(executionStructure, steps);
    if (!located) return false;
    const node = located.node;
    const name = String(node?.name || '').trim() || fallbackName || kind;
    const consequences = describeStructureNode(node);
    const ok = await confirm({
      title: `Delete ${kind.toLowerCase()} “${name}”?`,
      subtitle: 'Project structure',
      description: consequences.length
        ? 'It is removed from the structure together with:'
        : 'It is removed from the structure.',
      consequences,
      note: 'Execution sub-items generated from it are removed when you press Save Structure. Until then you can undo.',
      tone: 'danger',
      confirmLabel: `Delete ${kind.toLowerCase()}`,
    });
    if (!ok) return false;
    const removedIndex = located.index;
    setExecutionStructure((prev) => removeAtPath(prev, steps)?.next || prev);
    afterRemove?.();
    notify.success(`${kind} “${name}” deleted`, {
      description: 'Not saved yet — press Save Structure to apply.',
      undo: () => setExecutionStructure((prev) => insertAtPath(prev, steps, node, removedIndex)),
      undoneMessage: `${kind} “${name}” restored`,
    });
    return true;
  };

  // Bulk structure deletes: Undo restores the snapshot only if nothing else changed since.
  const removeStructureNodesWithUndo = async ({ title, count, kind, apply, afterRemove }) => {
    const ok = await confirm({
      title,
      subtitle: 'Project structure',
      description: `${count} ${kind}${count === 1 ? '' : 's'} and everything nested under them are removed from the structure.`,
      note: 'Execution sub-items generated from them are removed when you press Save Structure. Until then you can undo.',
      tone: 'danger',
      confirmLabel: `Delete ${count} ${kind}${count === 1 ? '' : 's'}`,
    });
    if (!ok) return false;
    const snapshot = executionStructure;
    const after = apply(snapshot);
    setExecutionStructure(after);
    afterRemove?.();
    notify.success(`${count} ${kind}${count === 1 ? '' : 's'} deleted`, {
      description: 'Not saved yet — press Save Structure to apply.',
      undo: () => {
        let restored = false;
        setExecutionStructure((prev) => {
          if (prev !== after) return prev;
          restored = true;
          return snapshot;
        });
        if (!restored) throw new Error('The structure changed after this delete, so it cannot be undone here.');
      },
    });
    return true;
  };

  // 08 #3: a new building / floor / room / panel is chosen in the editor and its name is ready to type.
  const revealNewStructureNode = (nodeId, openKeys = []) => {
    if (openKeys.length) {
      setCollapsedStructureSections((prev) => {
        const next = { ...prev };
        openKeys.forEach((key) => { next[key] = false; });
        return next;
      });
    }
    setStructureSelectedId(nodeId);
    window.requestAnimationFrame(() => focusStructureName(nodeId));
  };

  const handleAddBuilding = () => {
    const building = createBuilding();
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: [...(prev.buildings || []), building]
    }));
    if (activeTab === 'structure') revealNewStructureNode(building.id);
  };

  const handleBuildingChange = (buildingId, value) => {
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === buildingId ? { ...building, name: value } : building
      ))
    }));
  };

  const handleDeleteBuilding = (buildingId) => removeStructureNodeWithUndo({
    steps: [{ key: 'buildings', id: buildingId }],
    kind: 'Building',
  });

  const handleDuplicateBuilding = (buildingId) => {
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).flatMap((building) => (
        building.id === buildingId ? [building, cloneStructureBuilding(building, allowDuplicateDeviceAddresses)] : [building]
      ))
    }));
  };

  const handleAddFloor = (buildingId) => {
    const floor = createFloor();
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === buildingId
          ? { ...building, floors: [...(building.floors || []), floor] }
          : building
      ))
    }));
    revealNewStructureNode(floor.id, [getBuildingCollapseKey(buildingId)]);
  };

  const handleDuplicateFloor = (buildingId, floorId) => {
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === buildingId
          ? {
            ...building,
            floors: (building.floors || []).flatMap((floor) => (
              floor.id === floorId ? [floor, cloneStructureFloor(floor, allowDuplicateDeviceAddresses)] : [floor]
            ))
          }
          : building
      ))
    }));
  };

  const handleAddRoom = (buildingId, floorId) => {
    const room = isBmsProject ? { ...createRoom(), name: 'Zone' } : createRoom();
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === buildingId
          ? {
            ...building,
            floors: (building.floors || []).map((floor) => (
              floor.id === floorId
                ? { ...floor, rooms: [...(floor.rooms || []), room] }
                : floor
            ))
          }
          : building
      ))
    }));
    revealNewStructureNode(room.id, [getBuildingCollapseKey(buildingId), getFloorCollapseKey(buildingId, floorId)]);
  };

  const handleDuplicateRoom = (buildingId, floorId, roomId) => {
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === buildingId
          ? {
            ...building,
            floors: (building.floors || []).map((floor) => (
              floor.id === floorId
                ? {
                  ...floor,
                  rooms: (floor.rooms || []).flatMap((room) => (
                    room.id === roomId ? [room, cloneStructureRoom(room, allowDuplicateDeviceAddresses)] : [room]
                  ))
                }
                : floor
            ))
          }
          : building
      ))
    }));
  };

  const handleAddPanelAtBuilding = (buildingId) => {
    const floor = { ...createFloor(), panels: [createPanel()] };
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === buildingId
          ? { ...building, floors: [...(building.floors || []), floor] }
          : building
      ))
    }));
    revealNewStructureNode(floor.id, [getBuildingCollapseKey(buildingId)]);
  };

  const handleFloorChange = (buildingId, floorId, value) => {
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === buildingId
          ? {
            ...building,
            floors: (building.floors || []).map((floor) => (
              floor.id === floorId ? { ...floor, name: value } : floor
            ))
          }
          : building
      ))
    }));
  };

  const handleDeleteFloor = (buildingId, floorId) => removeStructureNodeWithUndo({
    steps: [{ key: 'buildings', id: buildingId }, { key: 'floors', id: floorId }],
    kind: 'Floor',
  });

  const handleRoomChange = (buildingId, floorId, roomId, value) => {
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === buildingId
          ? {
            ...building,
            floors: (building.floors || []).map((floor) => (
              floor.id === floorId
                ? {
                  ...floor,
                  rooms: (floor.rooms || []).map((room) => (
                    room.id === roomId ? { ...room, name: value } : room
                  ))
                }
                : floor
            ))
          }
          : building
      ))
    }));
  };

  const handleDeleteRoom = (buildingId, floorId, roomId) => removeStructureNodeWithUndo({
    steps: [{ key: 'buildings', id: buildingId }, { key: 'floors', id: floorId }, { key: 'rooms', id: roomId }],
    kind: isBmsProject ? 'Zone' : 'Room',
  });

  const handleAddPanel = (buildingId, floorId) => {
    const panel = createPanel();
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === buildingId
          ? {
            ...building,
            floors: (building.floors || []).map((floor) => (
              floor.id === floorId
                ? { ...floor, panels: [...(floor.panels || []), panel] }
                : floor
            ))
          }
          : building
      ))
    }));
    revealNewStructureNode(panel.id, [getBuildingCollapseKey(buildingId), getFloorCollapseKey(buildingId, floorId)]);
  };

  const handleAddPanelToRoom = (buildingId, floorId, roomId) => {
    const panel = createPanel();
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === buildingId
          ? {
            ...building,
            floors: (building.floors || []).map((floor) => (
              floor.id === floorId
                ? {
                  ...floor,
                  rooms: (floor.rooms || []).map((room) => (
                    room.id === roomId
                      ? { ...room, panels: [...(room.panels || []), panel] }
                      : room
                  ))
                }
                : floor
            ))
          }
          : building
      ))
    }));
    revealNewStructureNode(panel.id, [getBuildingCollapseKey(buildingId), getFloorCollapseKey(buildingId, floorId), getRoomCollapseKey(buildingId, floorId, roomId)]);
  };

  const handleAddZone = (buildingId, floorId) => {
    const zone = createZone();
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === buildingId
          ? {
            ...building,
            floors: (building.floors || []).map((floor) => (
              floor.id === floorId
                ? { ...floor, panels: [...(floor.panels || []), zone] }
                : floor
            ))
          }
          : building
      ))
    }));
    revealNewStructureNode(zone.id, [getBuildingCollapseKey(buildingId), getFloorCollapseKey(buildingId, floorId)]);
  };

  const handlePanelChange = (buildingId, floorId, panelId, value, panelType = '') => {
    const normalizedType = panelType === 'zone' ? 'zone' : panelType === 'panel' ? 'panel' : '';
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === buildingId
          ? {
            ...building,
            floors: (building.floors || []).map((floor) => (
              floor.id === floorId
                ? {
                  ...floor,
                  panels: (floor.panels || []).map((panel) => (
                    panel.id === panelId
                      ? {
                        ...panel,
                        name: value,
                        // Preserve explicit panel/zone type even when name is temporarily cleared while editing.
                        type: normalizedType || (() => {
                          const explicitType = String(panel.type || "").trim().toLowerCase();
                          if (explicitType === "zone" || explicitType === "panel") return explicitType;
                          return String(panel.name || "").trim().toLowerCase().startsWith("zone") ? "zone" : "panel";
                        })()
                      }
                      : panel
                  ))
                }
                : floor
            ))
          }
          : building
      ))
    }));
  };

  const handlePanelTypeChange = (buildingId, floorId, panelId, value) => {
    const nextType = value === 'zone' ? 'zone' : 'panel';
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === buildingId
          ? {
            ...building,
            floors: (building.floors || []).map((floor) => (
              floor.id === floorId
                ? {
                  ...floor,
                  panels: (floor.panels || []).map((panel) => (
                    panel.id === panelId ? { ...panel, type: nextType } : panel
                  ))
                }
                : floor
            ))
          }
          : building
      ))
    }));
  };

  const handleDeletePanel = (buildingId, floorId, panelId) => {
    const steps = [{ key: 'buildings', id: buildingId }, { key: 'floors', id: floorId }, { key: 'panels', id: panelId }];
    const panel = findAtPath(executionStructure, steps)?.node;
    return removeStructureNodeWithUndo({ steps, kind: String(panel?.type || '').toLowerCase() === 'zone' ? 'Zone' : 'Panel' });
  };

  const handleRoomPanelChange = (buildingId, floorId, roomId, panelId, value, panelType = '') => {
    const normalizedType = panelType === 'zone' ? 'zone' : 'panel';
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === buildingId
          ? {
            ...building,
            floors: (building.floors || []).map((floor) => (
              floor.id === floorId
                ? {
                  ...floor,
                  rooms: (floor.rooms || []).map((room) => (
                    room.id === roomId
                      ? {
                        ...room,
                        panels: (room.panels || []).map((panel) => (
                          panel.id === panelId ? { ...panel, name: value, type: normalizedType } : panel
                        ))
                      }
                      : room
                  ))
                }
                : floor
            ))
          }
          : building
      ))
    }));
  };

  const handleDeleteRoomPanel = (buildingId, floorId, roomId, panelId) => {
    const steps = [{ key: 'buildings', id: buildingId }, { key: 'floors', id: floorId }, { key: 'rooms', id: roomId }, { key: 'panels', id: panelId }];
    const panel = findAtPath(executionStructure, steps)?.node;
    return removeStructureNodeWithUndo({ steps, kind: String(panel?.type || '').toLowerCase() === 'zone' ? 'Zone' : 'Panel' });
  };

  const handleAddBmsRootSub = (buildingId) => {
    const newSub = createBmsRootSub();
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === buildingId
          ? { ...building, floors: [...(building.floors || []), newSub] }
          : building
      ))
    }));
    expandBmsSubPath(buildingId, newSub.id);
    revealNewStructureNode(newSub.id, [getBuildingCollapseKey(buildingId)]);
    return newSub.id;
  };

  const handleBmsRootSubChange = (buildingId, floorId, field, value) => {
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === buildingId
          ? {
            ...building,
            floors: (building.floors || []).map((floor) => (
              floor.id === floorId ? { ...floor, [field]: value } : floor
            ))
          }
          : building
      ))
    }));
  };

  const handleDeleteBmsRootSub = (buildingId, floorId) => removeStructureNodeWithUndo({
    steps: [{ key: 'buildings', id: buildingId }, { key: 'floors', id: floorId }],
    kind: 'Sub-item',
  });

  const updateBmsRootSubChildren = (buildingId, floorId, updater) => {
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === buildingId
          ? {
            ...building,
            floors: (building.floors || []).map((floor) => (
              floor.id === floorId ? { ...floor, devices: updater(floor.devices || []) } : floor
            ))
          }
          : building
      ))
    }));
  };

  const updateBmsSiblingGroup = (buildingId, floorId, path = [], updater) => {
    if (!path.length) {
      updateBmsRootSubChildren(buildingId, floorId, updater);
      return;
    }
    updateBmsRootSubChildren(buildingId, floorId, (devices) => (
      updateBmsNodeAtPath(devices || [], path, (device) => ({
        ...device,
        children: updater(getStructureNodeChildren(device))
      }))
    ));
  };

  const handleAddBmsSub = (buildingId, floorId, path = []) => {
    const newSub = createBmsSub();
    if (!path.length) {
      updateBmsRootSubChildren(buildingId, floorId, (devices) => [...(devices || []), newSub]);
      expandBmsSubPath(buildingId, floorId);
      return newSub.id;
    }
    updateBmsRootSubChildren(buildingId, floorId, (devices) => (
      appendBmsNodeAtPath(devices || [], path, newSub)
    ));
    expandBmsSubPath(buildingId, floorId, path);
    return newSub.id;
  };

  const handleToggleBmsSubSelection = (buildingId, floorId, path, itemId, checked) => {
    const selectionKey = getBmsSubSelectionKey(buildingId, floorId, path);
    setSelectedBmsSubs((prev) => ({
      ...prev,
      [selectionKey]: checked
        ? [...new Set([...(prev[selectionKey] || []), itemId])]
        : (prev[selectionKey] || []).filter((id) => id !== itemId)
    }));
  };

  const handleToggleAllBmsSubSelection = (buildingId, floorId, path, items, checked) => {
    const selectionKey = getBmsSubSelectionKey(buildingId, floorId, path);
    setSelectedBmsSubs((prev) => ({
      ...prev,
      [selectionKey]: checked ? items.map((item) => item.id) : []
    }));
  };

  const handleDuplicateSelectedBmsSubs = (buildingId, floorId, path = [], items = []) => {
    const selectionKey = getBmsSubSelectionKey(buildingId, floorId, path);
    const selectedIds = selectedBmsSubs[selectionKey] || [];
    if (!selectedIds.length) {
      toast.error('Select sub-items first');
      return;
    }
    const selectedSet = new Set(selectedIds);
    updateBmsSiblingGroup(buildingId, floorId, path, (siblings) => siblings.flatMap((item) => (
      selectedSet.has(item.id) ? [item, cloneBmsNode(item, allowDuplicateDeviceAddresses)] : [item]
    )));
    expandBmsSubPath(buildingId, floorId, path);
  };

  const handleCopySelectedBmsSubs = (buildingId, floorId, path = [], items = []) => {
    const selectionKey = getBmsSubSelectionKey(buildingId, floorId, path);
    const selectedIds = selectedBmsSubs[selectionKey] || [];
    if (!selectedIds.length) {
      toast.error('Select sub-items first');
      return;
    }
    const selectedSet = new Set(selectedIds);
    const copiedItems = (items || [])
      .filter((item) => selectedSet.has(item.id))
      .map((item) => cloneBmsNode(item, allowDuplicateDeviceAddresses));
    setCopiedBmsSubs(copiedItems);
    toast.success(`${copiedItems.length} sub-item${copiedItems.length === 1 ? '' : 's'} copied`);
  };

  const handlePasteBmsSubs = (buildingId, floorId, path = []) => {
    if (!copiedBmsSubs.length) {
      toast.error('Copy sub-items first');
      return;
    }
    updateBmsSiblingGroup(buildingId, floorId, path, (siblings) => [
      ...(siblings || []),
      ...copiedBmsSubs.map((item) => cloneBmsNode(item, allowDuplicateDeviceAddresses))
    ]);
    expandBmsSubPath(buildingId, floorId, path);
    toast.success(`${copiedBmsSubs.length} sub-item${copiedBmsSubs.length === 1 ? '' : 's'} pasted`);
  };

  const handleDeleteSelectedBmsSubs = (buildingId, floorId, path = []) => {
    const selectionKey = getBmsSubSelectionKey(buildingId, floorId, path);
    const selectedIds = selectedBmsSubs[selectionKey] || [];
    if (!selectedIds.length) {
      toast.error('Select sub-items first');
      return;
    }
    const selectedSet = new Set(selectedIds);
    const floorSteps = [{ key: 'buildings', id: buildingId }, { key: 'floors', id: floorId }];
    void removeStructureNodesWithUndo({
      title: `Delete ${selectedIds.length} selected sub-item${selectedIds.length === 1 ? '' : 's'}?`,
      count: selectedIds.length,
      kind: 'sub-item',
      apply: (structure) => {
        let next = structure;
        selectedIds.forEach((itemId) => {
          next = removeAtPath(next, [...floorSteps, ...bmsPathSteps([...path, itemId])])?.next || next;
        });
        return next;
      },
      afterRemove: () => {
        setSelectedBmsSubs((prev) => ({
          ...prev,
          [selectionKey]: (prev[selectionKey] || []).filter((itemId) => !selectedSet.has(itemId))
        }));
        expandBmsSubPath(buildingId, floorId, path);
      },
    });
  };

  const handleMoveSelectedBmsSubs = (buildingId, floorId, path = [], direction = 'up') => {
    const selectionKey = getBmsSubSelectionKey(buildingId, floorId, path);
    const selectedIds = selectedBmsSubs[selectionKey] || [];
    if (!selectedIds.length) {
      toast.error('Select sub-items first');
      return;
    }
    updateBmsSiblingGroup(buildingId, floorId, path, (siblings) => reorderSelectedItems(siblings || [], selectedIds, direction));
    expandBmsSubPath(buildingId, floorId, path);
  };

  const handleBmsSubChange = (buildingId, floorId, path, field, value) => {
    updateBmsRootSubChildren(buildingId, floorId, (devices) => (
      updateBmsNodeAtPath(devices || [], path, (device) => ({ ...device, [field]: value }))
    ));
  };

  const handleDeleteBmsSub = (buildingId, floorId, path) => removeStructureNodeWithUndo({
    steps: [{ key: 'buildings', id: buildingId }, { key: 'floors', id: floorId }, ...bmsPathSteps(path)],
    kind: 'Sub-item',
  });

  const handleOpenAddDevicesDialog = (buildingId, floorId, roomOrPanelId = null, maybePanelId) => {
    const { roomId, panelId } = resolveStructureIds(roomOrPanelId, maybePanelId);
    setDeviceBatchTarget({ buildingId, floorId, roomId, panelId });
    setDeviceBatchForm(createDeviceBatchForm());
    setDeviceBatchSearch('');
    setDeviceBatchDialogOpen(true);
    // the BOQ loads with the Material tab: opened from Structure first, load it now (never "no BOQ" while loading)
    void ensureResources(['boq']);
  };

  const updateDeviceBatchItemSetting = (itemId, patch) => {
    setDeviceBatchForm((prev) => {
      const current = prev.item_settings?.[itemId] || {};
      return {
        ...prev,
        item_settings: {
          ...(prev.item_settings || {}),
          [itemId]: {
            quantity: current.quantity ?? 1,
            start_index: current.start_index ?? 1,
            address_prefix: current.address_prefix ?? '',
            ...patch
          }
        }
      };
    });
  };

  const handleCreateDevicesBatch = () => {
    if (!deviceBatchTarget) return;
    const selectedIds = deviceBatchForm.boq_item_ids || [];
    const items = boqItems.filter((entry) => selectedIds.includes(entry.id));
    if (!items.length) {
      toast.error('Select at least one BOQ item first');
      return;
    }
    const devices = items.flatMap((item) => {
      const setting = deviceBatchForm.item_settings?.[item.id] || {};
      const quantity = Math.max(1, Number(setting.quantity ?? 1));
      const startIndex = Math.max(1, Number(setting.start_index ?? 1));
      const addressPrefix = String(setting.address_prefix ?? '').trim();

      return Array.from({ length: quantity }, (_, index) => {
        const sequence = startIndex + index;
        const address = addressPrefix ? `${addressPrefix}${sequence}` : '';
        return createDevice(item, address);
      });
    });

    const duplicateAddress = allowDuplicateDeviceAddresses ? '' : devices.find((device) => (
      hasDuplicateProjectDeviceAddress(executionStructure, device.address)
    ))?.address;
    if (duplicateAddress) {
      toast.error(`Device address already exists in this project: ${duplicateAddress}`);
      return;
    }

    const seenNewAddresses = new Set();
    const repeatedNewAddress = devices.find((device) => {
      const normalizedAddress = String(device.address || '').trim();
      if (!normalizedAddress) return false;
      if (seenNewAddresses.has(normalizedAddress)) return true;
      seenNewAddresses.add(normalizedAddress);
      return false;
    })?.address;
    if (repeatedNewAddress && !allowDuplicateDeviceAddresses) {
      toast.error(`Duplicate device address in this batch: ${repeatedNewAddress}`);
      return;
    }

    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === deviceBatchTarget.buildingId
          ? {
            ...building,
            floors: (building.floors || []).map((floor) => (
              floor.id === deviceBatchTarget.floorId
                ? {
                  ...floor,
                  ...(deviceBatchTarget.roomId
                    ? {
                      rooms: (floor.rooms || []).map((room) => (
                        room.id === deviceBatchTarget.roomId
                          ? {
                            ...room,
                            ...(deviceBatchTarget.panelId
                              ? {
                                panels: (room.panels || []).map((panel) => (
                                  panel.id === deviceBatchTarget.panelId
                                    ? { ...panel, devices: [...(panel.devices || []), ...devices] }
                                    : panel
                                ))
                              }
                              : { devices: [...(room.devices || []), ...devices] })
                          }
                          : room
                      ))
                    }
                    : deviceBatchTarget.panelId
                    ? {
                      panels: (floor.panels || []).map((panel) => (
                        panel.id === deviceBatchTarget.panelId
                          ? { ...panel, devices: [...(panel.devices || []), ...devices] }
                          : panel
                      ))
                    }
                    : { devices: [...(floor.devices || []), ...devices] })
                }
                : floor
            ))
          }
          : building
      ))
    }));

    setDeviceBatchDialogOpen(false);
    setDeviceBatchTarget(null);
    setDeviceBatchForm(createDeviceBatchForm());
  };

  const handleRoomDeviceChange = (buildingId, floorId, roomId, deviceId, field, value) => {
    if (!allowDuplicateDeviceAddresses && field === 'address' && hasDuplicateProjectDeviceAddress(executionStructure, value, deviceId)) {
      toast.error(`Device address already exists in this project: ${String(value || '').trim()}`);
      return;
    }
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === buildingId
          ? {
            ...building,
            floors: (building.floors || []).map((floor) => (
              floor.id === floorId
                ? {
                  ...floor,
                  rooms: (floor.rooms || []).map((room) => (
                    room.id === roomId
                      ? {
                        ...room,
                        devices: (room.devices || []).map((device) => (
                          device.id === deviceId ? { ...device, [field]: value } : device
                        ))
                      }
                      : room
                  ))
                }
                : floor
            ))
          }
          : building
      ))
    }));
  };

  const handleRoomPanelDeviceChange = (buildingId, floorId, roomId, panelId, deviceId, field, value) => {
    if (!allowDuplicateDeviceAddresses && field === 'address' && hasDuplicateProjectDeviceAddress(executionStructure, value, deviceId)) {
      toast.error(`Device address already exists in this project: ${String(value || '').trim()}`);
      return;
    }
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === buildingId
          ? {
            ...building,
            floors: (building.floors || []).map((floor) => (
              floor.id === floorId
                ? {
                  ...floor,
                  rooms: (floor.rooms || []).map((room) => (
                    room.id === roomId
                      ? {
                        ...room,
                        panels: (room.panels || []).map((panel) => (
                          panel.id === panelId
                            ? {
                              ...panel,
                              devices: (panel.devices || []).map((device) => (
                                device.id === deviceId ? { ...device, [field]: value } : device
                              ))
                            }
                            : panel
                        ))
                      }
                      : room
                  ))
                }
                : floor
            ))
          }
          : building
      ))
    }));
  };

  const handleDeleteRoomDevice = (buildingId, floorId, roomId, deviceId) => removeStructureNodeWithUndo({
    steps: [{ key: 'buildings', id: buildingId }, { key: 'floors', id: floorId }, { key: 'rooms', id: roomId }, { key: 'devices', id: deviceId }],
    kind: 'Device',
  });

  const handleDeleteRoomPanelDevice = (buildingId, floorId, roomId, panelId, deviceId) => removeStructureNodeWithUndo({
    steps: [
      { key: 'buildings', id: buildingId },
      { key: 'floors', id: floorId },
      { key: 'rooms', id: roomId },
      { key: 'panels', id: panelId },
      { key: 'devices', id: deviceId },
    ],
    kind: 'Device',
  });

  const handleDeviceChange = (buildingId, floorId, panelId, deviceId, field, value) => {
    if (!allowDuplicateDeviceAddresses && field === 'address' && hasDuplicateProjectDeviceAddress(executionStructure, value, deviceId)) {
      toast.error(`Device address already exists in this project: ${String(value || '').trim()}`);
      return;
    }
    handleExecutionStructureChange((prev) => ({
      ...prev,
      buildings: (prev.buildings || []).map((building) => (
        building.id === buildingId
          ? {
            ...building,
            floors: (building.floors || []).map((floor) => (
              floor.id === floorId
                ? {
                  ...floor,
                  ...(panelId
                    ? {
                      panels: (floor.panels || []).map((panel) => (
                        panel.id === panelId
                          ? {
                            ...panel,
                            devices: (panel.devices || []).map((device) => (
                              device.id === deviceId ? { ...device, [field]: value } : device
                            ))
                          }
                          : panel
                      ))
                    }
                    : {
                      devices: (floor.devices || []).map((device) => (
                        device.id === deviceId ? { ...device, [field]: value } : device
                      ))
                    })
                }
                : floor
            ))
          }
          : building
      ))
    }));
  };

  const handleDeleteDevice = (buildingId, floorId, panelId, deviceId) => {
    const panelKey = getPanelSelectionKey(buildingId, floorId, panelId);
    return removeStructureNodeWithUndo({
      steps: [
        { key: 'buildings', id: buildingId },
        { key: 'floors', id: floorId },
        ...(panelId ? [{ key: 'panels', id: panelId }] : []),
        { key: 'devices', id: deviceId },
      ],
      kind: 'Device',
      afterRemove: () => setSelectedStructureDevices((prev) => ({
        ...prev,
        [panelKey]: (prev[panelKey] || []).filter((selectedId) => selectedId !== deviceId)
      })),
    });
  };

  // roomId (final QA, 08 #1): devices in a room / a room's panel are selectable too
  const handleToggleStructureDeviceSelection = (buildingId, floorId, panelId, deviceId, checked, roomId = null) => {
    const panelKey = getPanelSelectionKey(buildingId, floorId, roomId, panelId || null);
    const currentFloor = (executionStructure.buildings || [])
      .find((building) => building.id === buildingId)
      ?.floors?.find((floor) => floor.id === floorId);
    const container = roomId ? currentFloor?.rooms?.find((room) => room.id === roomId) : currentFloor;
    const orderedDevices = panelId
      ? (container?.panels?.find((panel) => panel.id === panelId)?.devices || [])
      : (container?.devices || []);
    const shouldRangeSelect = structureSelectionInteractionRef.current.shiftKey;
    const anchorId = structureSelectionAnchorRef.current[panelKey];

    setSelectedStructureDevices((prev) => {
      const current = new Set(prev[panelKey] || []);

      if (shouldRangeSelect && anchorId) {
        const anchorIndex = orderedDevices.findIndex((device) => device.id === anchorId);
        const targetIndex = orderedDevices.findIndex((device) => device.id === deviceId);
        if (anchorIndex >= 0 && targetIndex >= 0) {
          orderedDevices
            .slice(Math.min(anchorIndex, targetIndex), Math.max(anchorIndex, targetIndex) + 1)
            .forEach((device) => {
              if (checked) {
                current.add(device.id);
              } else {
                current.delete(device.id);
              }
            });
        } else if (checked) {
          current.add(deviceId);
        } else {
          current.delete(deviceId);
        }
      } else if (checked) {
        current.add(deviceId);
      } else {
        current.delete(deviceId);
      }
      return {
        ...prev,
        [panelKey]: [...current]
      };
    });
    structureSelectionAnchorRef.current[panelKey] = deviceId;
    structureSelectionInteractionRef.current = { shiftKey: false };
  };

  const handleToggleAllPanelDevices = (buildingId, floorId, panelId, devices, checked, roomId = null) => {
    const panelKey = getPanelSelectionKey(buildingId, floorId, roomId, panelId || null);
    setSelectedStructureDevices((prev) => ({
      ...prev,
      [panelKey]: checked ? devices.map((device) => device.id) : []
    }));
  };

  const handleDeleteSelectedDevices = (buildingId, floorId, panelId, roomId = null) => {
    const panelKey = getPanelSelectionKey(buildingId, floorId, roomId, panelId || null);
    const selectedIds = selectedStructureDevices[panelKey] || [];
    if (!selectedIds.length) {
      toast.error('Select devices first');
      return;
    }
    const containerSteps = [
      { key: 'buildings', id: buildingId },
      { key: 'floors', id: floorId },
      ...(roomId ? [{ key: 'rooms', id: roomId }] : []),
      ...(panelId ? [{ key: 'panels', id: panelId }] : []),
    ];
    void removeStructureNodesWithUndo({
      title: `Delete ${selectedIds.length} selected device${selectedIds.length === 1 ? '' : 's'}?`,
      count: selectedIds.length,
      kind: 'device',
      apply: (structure) => {
        let next = structure;
        selectedIds.forEach((deviceId) => {
          next = removeAtPath(next, [...containerSteps, { key: 'devices', id: deviceId }])?.next || next;
        });
        return next;
      },
      afterRemove: () => setSelectedStructureDevices((prev) => ({
        ...prev,
        [panelKey]: []
      })),
    });
  };

  const buildStructureLocation = (buildingId, floorId, roomId = null, panelId = null) => ({
    buildingId,
    floorId,
    roomId: roomId || null,
    panelId: panelId || null
  });

  const getDevicesAtLocation = (structure, location) => {
    const building = (structure.buildings || []).find((entry) => entry.id === location.buildingId);
    const floor = building?.floors?.find((entry) => entry.id === location.floorId);
    if (!floor) return [];
    if (location.roomId) {
      const room = (floor.rooms || []).find((entry) => entry.id === location.roomId);
      if (!room) return [];
      if (location.panelId) {
        const panel = (room.panels || []).find((entry) => entry.id === location.panelId);
        return panel?.devices || [];
      }
      return room.devices || [];
    }
    if (location.panelId) {
      const panel = (floor.panels || []).find((entry) => entry.id === location.panelId);
      return panel?.devices || [];
    }
    return floor.devices || [];
  };

  const updateDevicesAtLocation = (structure, location, updater) => ({
    ...structure,
    buildings: (structure.buildings || []).map((building) => (
      building.id !== location.buildingId
        ? building
        : {
          ...building,
          floors: (building.floors || []).map((floor) => {
            if (floor.id !== location.floorId) return floor;
            if (location.roomId) {
              return {
                ...floor,
                rooms: (floor.rooms || []).map((room) => {
                  if (room.id !== location.roomId) return room;
                  if (location.panelId) {
                    return {
                      ...room,
                      panels: (room.panels || []).map((panel) => (
                        panel.id === location.panelId
                          ? { ...panel, devices: updater(panel.devices || []) }
                          : panel
                      ))
                    };
                  }
                  return { ...room, devices: updater(room.devices || []) };
                })
              };
            }
            if (location.panelId) {
              return {
                ...floor,
                panels: (floor.panels || []).map((panel) => (
                  panel.id === location.panelId
                    ? { ...panel, devices: updater(panel.devices || []) }
                    : panel
                ))
              };
            }
            return { ...floor, devices: updater(floor.devices || []) };
          })
        }
    ))
  });

  const removeDevicesFromList = (devices = [], removedIds) => {
    let removed = [];
    const next = (devices || []).flatMap((device) => {
      if (!device || typeof device !== 'object') return [];
      if (removedIds.has(device.id)) {
        removed.push(device);
        return [];
      }
      const children = getStructureNodeChildren(device);
      if (children.length > 0) {
        const result = removeDevicesFromList(children, removedIds);
        removed = removed.concat(result.removed);
        return [{ ...device, children: result.nextDevices }];
      }
      return [device];
    });
    return { nextDevices: next, removed };
  };

  const addDevicesToTargetDevice = (devices = [], targetDeviceId, movingDevices) => (
    (devices || []).map((device) => {
      if (device.id === targetDeviceId) {
        return { ...device, children: [...getStructureNodeChildren(device), ...movingDevices] };
      }
      const children = getStructureNodeChildren(device);
      if (children.length > 0) {
        return { ...device, children: addDevicesToTargetDevice(children, targetDeviceId, movingDevices) };
      }
      return device;
    })
  );

  const collectDeviceIds = (devices = []) => (
    flattenStructureDevices(devices).map((device) => device.id)
  );

  const clearMovedSelections = (movedIds) => {
    setSelectedStructureDevices((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, ids]) => {
        next[key] = (ids || []).filter((id) => !movedIds.includes(id));
      });
      return next;
    });
  };

  const buildBmsLocation = (buildingId, floorId, path = []) => ({
    buildingId,
    floorId,
    path
  });

  const getBmsNodesAtPath = (structure, location) => {
    const building = (structure?.buildings || []).find((entry) => entry.id === location.buildingId);
    if (!building) return [];
    const floor = (building.floors || []).find((entry) => entry.id === location.floorId);
    if (!floor) return [];
    let nodes = floor.devices || [];
    for (const nodeId of location.path || []) {
      const node = (nodes || []).find((entry) => entry.id === nodeId);
      if (!node) return [];
      nodes = getStructureNodeChildren(node);
    }
    return nodes || [];
  };

  const updateBmsNodesAtPath = (structure, location, updater) => ({
    ...(structure || {}),
    buildings: (structure?.buildings || []).map((building) => {
      if (building.id !== location.buildingId) return building;
      return {
        ...building,
        floors: (building.floors || []).map((floor) => {
          if (floor.id !== location.floorId) return floor;
          if (!location.path.length) {
            return { ...floor, devices: updater(floor.devices || []) };
          }
          return {
            ...floor,
            devices: updateBmsNodeAtPath(floor.devices || [], location.path, (device) => ({
              ...device,
              children: updater(getStructureNodeChildren(device))
            }))
          };
        })
      };
    })
  });

  const clearMovedBmsSelections = (movedIds) => {
    setSelectedBmsSubs((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, ids]) => {
        next[key] = (ids || []).filter((id) => !movedIds.includes(id));
      });
      return next;
    });
  };

  const handleBmsSubDragStart = (buildingId, floorId, path, deviceId) => {
    if (!isStageLinesEditMode || !bmsSubDragMode) return;
    const selectionKey = getBmsSubSelectionKey(buildingId, floorId, path);
    const selectedIds = selectedBmsSubs[selectionKey] || [];
    const nodeIds = selectedIds.includes(deviceId) ? selectedIds : [deviceId];
    setDraggingBmsSub({
      deviceId,
      nodeIds,
      selectionKey,
      sourceLocation: buildBmsLocation(buildingId, floorId, path)
    });
  };

  const moveBmsSubs = async (targetLocation, targetDeviceId = null) => {
    const source = draggingBmsSub;
    setDraggingBmsSub(null);
    if (!isStageLinesEditMode || !bmsSubDragMode || !source) return;

    const movingIds = source.nodeIds || [source.deviceId];
    const sourceNodes = getBmsNodesAtPath(executionStructure, source.sourceLocation);
    const removal = removeDevicesFromList(sourceNodes, new Set(movingIds));
    if (!removal.removed.length) return;

    const movingDescendantIds = new Set(collectDeviceIds(removal.removed));
    if (targetDeviceId && movingDescendantIds.has(targetDeviceId)) {
      toast.error('Cannot move a sub-item into itself');
      return;
    }

    let nextStructure = updateBmsNodesAtPath(executionStructure, source.sourceLocation, () => removal.nextDevices);

    if (targetDeviceId) {
      const targetNodes = getBmsNodesAtPath(nextStructure, targetLocation);
      const nextTargetNodes = addDevicesToTargetDevice(targetNodes, targetDeviceId, removal.removed);
      nextStructure = updateBmsNodesAtPath(nextStructure, targetLocation, () => nextTargetNodes);
      expandBmsSubPath(targetLocation.buildingId, targetLocation.floorId, [...targetLocation.path, targetDeviceId]);
    } else {
      nextStructure = updateBmsNodesAtPath(nextStructure, targetLocation, (nodes) => [...nodes, ...removal.removed]);
      expandBmsSubPath(targetLocation.buildingId, targetLocation.floorId, targetLocation.path);
    }

    setExecutionStructure(nextStructure);
    clearMovedBmsSelections(movingIds);
    try {
      await persistExecutionStructure(nextStructure);
      toast.success(targetDeviceId ? 'Sub-item moved into item' : 'Sub-item moved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move sub-item');
      fetchData();
    }
  };

  const handleBmsRootSubDragStart = (buildingId, floorId) => {
    if (!isStageLinesEditMode || !bmsSubDragMode) return;
    setDraggingBmsRootSub({ sourceBuildingId: buildingId, floorId });
  };

  const moveBmsRootSub = async (targetBuildingId, targetFloorId = null) => {
    const source = draggingBmsRootSub;
    setDraggingBmsRootSub(null);
    if (!isStageLinesEditMode || !bmsSubDragMode || !source) return;
    if (source.sourceBuildingId === targetBuildingId && source.floorId === targetFloorId) return;
    if (!(executionStructure?.buildings || []).some((building) => building.id === targetBuildingId)) return;

    let movingRootSub = null;
    const structureWithoutSource = {
      ...(executionStructure || {}),
      buildings: (executionStructure?.buildings || []).map((building) => {
        if (building.id !== source.sourceBuildingId) return building;
        return {
          ...building,
          floors: (building.floors || []).filter((floor) => {
            if (floor.id !== source.floorId) return true;
            movingRootSub = floor;
            return false;
          })
        };
      })
    };

    if (!movingRootSub) return;

    const nextStructure = {
      ...structureWithoutSource,
      buildings: (structureWithoutSource.buildings || []).map((building) => {
        if (building.id !== targetBuildingId) return building;
        const floors = [...(building.floors || [])];
        const targetIndex = targetFloorId ? floors.findIndex((floor) => floor.id === targetFloorId) : -1;
        if (targetIndex >= 0) {
          floors.splice(targetIndex + 1, 0, movingRootSub);
        } else {
          floors.push(movingRootSub);
        }
        return { ...building, floors };
      })
    };

    setExecutionStructure(nextStructure);
    setCollapsedStructureSections((prev) => ({
      ...prev,
      [getBuildingCollapseKey(targetBuildingId)]: false
    }));
    try {
      await persistExecutionStructure(nextStructure);
      toast.success('Sub-item moved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move sub-item');
      fetchData();
    }
  };

  const handleStructureDeviceDragStart = (buildingId, floorId, roomId, panelId, deviceId) => {
    if (!isStageLinesEditMode) return;
    const panelKey = getPanelSelectionKey(buildingId, floorId, roomId, panelId);
    const selectedIds = selectedStructureDevices[panelKey] || [];
    const deviceIds = selectedIds.includes(deviceId) ? selectedIds : [deviceId];
    setDraggingStructureDevice({
      deviceId,
      deviceIds,
      panelKey,
      sourceLocation: buildStructureLocation(buildingId, floorId, roomId, panelId)
    });
  };

  const moveStructureDevices = async (targetLocation, targetDeviceId = null) => {
    const source = draggingStructureDevice;
    setDraggingStructureDevice(null);
    if (!isStageLinesEditMode || !source) return;

    const movingIds = source.deviceIds || [source.deviceId];
    const sourceDevices = getDevicesAtLocation(executionStructure, source.sourceLocation);
    const removal = removeDevicesFromList(sourceDevices, new Set(movingIds));
    if (!removal.removed.length) return;

    const movingDescendantIds = new Set(collectDeviceIds(removal.removed));
    if (targetDeviceId && movingDescendantIds.has(targetDeviceId)) {
      toast.error('Cannot move a device into itself');
      return;
    }

    let nextStructure = updateDevicesAtLocation(executionStructure, source.sourceLocation, () => removal.nextDevices);

    if (targetDeviceId) {
      const targetDevices = getDevicesAtLocation(nextStructure, targetLocation);
      const nextTargetDevices = addDevicesToTargetDevice(targetDevices, targetDeviceId, removal.removed);
      nextStructure = updateDevicesAtLocation(nextStructure, targetLocation, () => nextTargetDevices);
    } else {
      nextStructure = updateDevicesAtLocation(nextStructure, targetLocation, (devices) => [...devices, ...removal.removed]);
    }

    setExecutionStructure(nextStructure);
    clearMovedSelections(movingIds);
    try {
      await persistExecutionStructure(nextStructure);
      toast.success(targetDeviceId ? 'Device moved into item' : 'Device moved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move device');
      fetchData();
    }
  };

  const handleTemplateItemChange = (templateId, field, value) => {
    setExecutionTemplate((prev) => prev.map((item) => (
      item.id === templateId ? { ...item, [field]: value } : item
    )));
  };

  const handleAddTemplateItem = () => {
    setExecutionTemplate((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: 'New Task',
        category: 'Execution',
        expansion: 'none',
        order: prev.length + 1
      }
    ]);
  };

  const handleDeleteTemplateItem = (templateId) => {
    setExecutionTemplate((prev) => prev
      .filter((item) => item.id !== templateId)
      .map((item, index) => ({ ...item, order: index + 1 })));
  };

  const handleMoveTemplateItem = (templateId, direction) => {
    setExecutionTemplate((prev) => {
      const index = prev.findIndex((item) => item.id === templateId);
      if (index < 0) return prev;
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next.map((item, orderIndex) => ({ ...item, order: orderIndex + 1 }));
    });
  };

  const handleSaveExecutionTemplate = async () => {
    try {
      const res = await api.put(`/projects/${id}/execution-template`, { execution_template: executionTemplate });
      setProject(res.data);
      setExecutionTemplateDialogOpen(false);
      toast.success('Execution template updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update execution template');
    }
  };

  const applyExecutionTaskChange = (tasks, taskId, field, value, timestamp) => {
    const cascadeFields = new Set([
      'status',
      'category',
      'start_date',
      'due_date',
      'done_by',
      'checked_by',
      'comments',
      'monitoring'
    ]);
    const descendantIds = cascadeFields.has(field)
      ? new Set(getDescendantIds(tasks, taskId))
      : null;

    const next = tasks.map((task) => {
      if (task.id === taskId) {
        return { ...task, [field]: value, updated: timestamp };
      }
      if (descendantIds?.has(task.id)) {
        return { ...task, [field]: value, updated: timestamp };
      }
      return task;
    });
    if (field !== 'status') return next;
    // Reopening a sub-item reopens its Done parents; otherwise the sync would mark it Done again.
    const reopened = value === 'Done' ? next : reopenDoneAncestors(next, [taskId], timestamp);
    return syncExecutionTaskHierarchy(reopened, timestamp);
  };

  // Applies an announced change (and its cascade) with one "… — also updated N sub-items" toast whose Undo
  // restores exactly the values this change replaced. `parentBefore` = what the parent held before an edit
  // that was committed on blur (the parent already shows the new value by then).
  const applyAnnouncedExecutionChange = (taskId, field, value, changedCount, { parentBefore } = {}) => {
    const timestamp = new Date().toISOString();
    const before = executionTasksRef.current;
    const after = applyExecutionTaskChange(before, taskId, field, value, timestamp);
    // Remember only what this change touched, so Undo restores those values even after other edits.
    const previousById = new Map();
    after.forEach((task, index) => {
      const previous = before[index];
      if (!previous || previous.id !== task.id) return;
      const statusChanged = previous.status !== task.status;
      if (previous[field] !== task[field] || statusChanged) {
        previousById.set(task.id, { value: previous[field], status: previous.status, statusChanged, updated: previous.updated });
      }
    });
    if (parentBefore) {
      previousById.set(taskId, {
        status: before.find((task) => task.id === taskId)?.status,
        statusChanged: false,
        ...previousById.get(taskId),
        value: parentBefore.value,
        updated: parentBefore.updated,
      });
    }
    setExecutionTasks(after);

    const parent = after.find((task) => task.id === taskId);
    const title = parent ? formatExecutionTaskTitle(parent, after) : 'Task';
    const fieldLabel = EXECUTION_FIELD_LABELS[field] || field;
    notify.success(`${title} set to ${formatExecutionFieldValue(field, value)} — also updated ${changedCount} sub-item${changedCount === 1 ? '' : 's'}`, {
      description: 'Not saved yet — press Save execution to keep it.',
      undo: () => setExecutionTasks((prev) => prev.map((task) => {
        const previous = previousById.get(task.id);
        if (!previous) return task;
        // A status change can also move parents (all children Done → parent Done): restore those statuses too.
        const restored = { ...task, [field]: previous.value, updated: previous.updated };
        if (previous.statusChanged) restored.status = previous.status;
        return restored;
      })),
      undoneMessage: `${title}: ${fieldLabel.toLowerCase()} change undone`,
    });
  };

  const confirmExecutionOverwrite = (title, field, value, cascade) => {
    const fieldLabel = EXECUTION_FIELD_LABELS[field] || field;
    return confirm({
      title: `Set ${fieldLabel.toLowerCase()} of “${title}” and its sub-items to ${formatExecutionFieldValue(field, value)}?`,
      subtitle: `${cascade.changed} of ${cascade.descendants} sub-item${cascade.descendants === 1 ? '' : 's'} will change`,
      description: `${cascade.overwrittenSummary}. Their ${fieldLabel.toLowerCase()} will be replaced.`,
      confirmLabel: `Update ${cascade.changed + 1} tasks`,
      note: 'Nothing is saved until you press Save execution, and the toast lets you undo.',
    });
  };

  // Cell edits (09 #1): the editors (menu, people / date / text popovers) hand over a finished value, so a
  // parent's sub-items follow at once — with a confirm when recorded work would be replaced, and one
  // "… — also updated N sub-items" toast with Undo.
  const handleExecutionTaskChange = async (taskId, field, value) => {
    const timestamp = new Date().toISOString();
    const hasSubItems = (executionTree.children.get(taskId) || []).length > 0;

    const announce = hasSubItems && EXECUTION_ANNOUNCED_FIELDS.includes(field);
    const cascade = announce ? describeExecutionCascade(executionTasks, taskId, field, value) : null;
    if (!cascade || cascade.changed === 0) {
      // Typing fields and changes without sub-items: apply silently, as before.
      setExecutionTasks((prev) => applyExecutionTaskChange(prev, taskId, field, value, timestamp));
      return;
    }
    if (cascade.overwritten > 0) {
      const parent = executionTaskById.get(taskId);
      const title = parent ? formatExecutionTaskTitle(parent, executionTaskById) : 'Task';
      if (!(await confirmExecutionOverwrite(title, field, value, cascade))) return;
    }
    applyAnnouncedExecutionChange(taskId, field, value, cascade.changed);
  };

  const toggleExecutionParent = (taskId) => {
    setCollapsedExecutionParents((prev) => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  const toggleStructureSection = (key) => {
    setCollapsedStructureSections((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const expandBmsSubPath = (buildingId, floorId, path = []) => {
    setCollapsedStructureSections((prev) => {
      const next = {
        ...prev,
        [getBuildingCollapseKey(buildingId)]: false,
        [getFloorCollapseKey(buildingId, floorId)]: false
      };
      for (let index = 0; index < path.length; index += 1) {
        next[getBmsSubCollapseKey(buildingId, floorId, path.slice(0, index + 1))] = false;
      }
      return next;
    });
  };

  const handleExecutionCellEdit = (taskId, field, value) => handleExecutionTaskChange(taskId, field, value);

  // 09 #6: Save execution (toolbar, save bar and the "save before leaving?" prompt). `rethrow` lets the
  // prompt show the error inside the dialog instead of closing.
  const saveExecutionTasks = async ({ rethrow = false } = {}) => {
    setExecutionSave({ status: 'saving', error: '' });
    try {
      const res = await api.put(`/projects/${id}/execution-tasks`, { execution_tasks: executionTasksRef.current });
      setProject(res.data);
      setExecutionSave({ status: 'saved', error: '' });
      markExecutionClean();
      toast.success('Execution tasks updated');
      return true;
    } catch (err) {
      const message = getErrorMessage(err, 'Failed to update execution tasks');
      setExecutionSave({ status: 'error', error: message });
      if (rethrow) throw new Error(message);
      notify.error('Could not save execution', { description: message });
      return false;
    }
  };
  const handleSaveExecutionTasks = () => saveExecutionTasks();

  const discardExecutionChanges = () => {
    const before = executionTasksRef.current;
    const count = executionDirtyCount;
    setExecutionTasks(project?.execution_tasks || []);
    setExecutionSave({ status: 'idle', error: '' });
    if (count) {
      notify.success(`Discarded ${count} execution change${count === 1 ? '' : 's'}`, {
        undo: () => setExecutionTasks(before),
        undoneMessage: 'Your execution changes are back',
      });
    }
  };

  // 09 #4: one change for every selected task; their sub-items follow (like a parent edit).
  const applyExecutionBulk = async (changes, what) => {
    const ids = selectedExecutionTaskIds;
    if (!ids.length) return;
    const plan = planBulkChange(executionTasksRef.current, ids, changes, executionTree);
    if (!plan.changed) {
      notify.info(`The selected task${ids.length === 1 ? ' already has' : 's already have'} ${what}`);
      return;
    }
    if (plan.overwritten) {
      const ok = await confirm({
        title: `Set ${what} on ${ids.length} task${ids.length === 1 ? '' : 's'} and their sub-items?`,
        subtitle: `${plan.changed} task${plan.changed === 1 ? '' : 's'} will change`,
        description: `${plan.overwrittenSummary}. Their value will be replaced.`,
        confirmLabel: `Update ${plan.changed} tasks`,
        note: 'Nothing is saved until you press Save execution, and the toast lets you undo.',
      });
      if (!ok) return;
    }
    const { next, previous } = applyBulkChange(executionTasksRef.current, plan.affectedIds, changes);
    setExecutionTasks(next);
    notify.success(
      `${ids.length} task${ids.length === 1 ? '' : 's'} set to ${what}${plan.subItems ? ` — also updated ${plan.subItems} sub-item${plan.subItems === 1 ? '' : 's'}` : ''}`,
      {
        description: 'Not saved yet — press Save execution to keep it.',
        undo: () => setExecutionTasks((prev) => revertBulkChange(prev, previous)),
        undoneMessage: 'Bulk change undone',
      }
    );
  };
  const handleBulkExecutionStatus = (status) => applyExecutionBulk({ status }, status);
  const handleBulkExecutionAssign = (names) => applyExecutionBulk({ done_by: names }, `Done by ${names}`);
  const handleBulkExecutionDates = (dates) => applyExecutionBulk(
    dates,
    [dates.start_date ? `start ${formatDate(dates.start_date)}` : '', dates.due_date ? `due ${formatDate(dates.due_date)}` : ''].filter(Boolean).join(', ')
  );

  /** Tasks Export / Print use: the selection (with sub-items) or, without one, everything the filters show. */
  const executionTasksForOutput = () => {
    if (selectedExecutionTaskIds.length) {
      return { tasks: expandSelection(executionTasks, selectedExecutionTaskIds, executionTree), label: 'Selected execution tasks', suffix: 'selected' };
    }
    const included = executionFiltered.included;
    return {
      tasks: included ? executionTasks.filter((task) => included.has(task.id)) : executionTasks,
      label: included ? 'Execution tasks (filtered)' : 'Execution tasks',
      suffix: included ? 'filtered' : 'all',
    };
  };
  const handleExportExecution = () => {
    const { tasks, suffix } = executionTasksForOutput();
    handleExportExecutionCsv(tasks, suffix);
  };
  const handlePrintExecutionList = () => {
    const { tasks, label } = executionTasksForOutput();
    handlePrintExecution(tasks, label);
  };

  const handleExportExecutionCsv = (tasks, suffix = 'selected') => {
    const exportRows = buildExecutionExportRows(tasks);
    if (!exportRows.length) {
      toast.error('No execution tasks to export');
      return;
    }

    const headers = ['Project', 'L1', 'L2', 'Level', 'Task', 'Status', 'Category', 'Start', 'Due', 'Done By', 'Checked By', 'Comments', 'Monitoring', 'Zone', 'Zone 2', 'Zone 3', 'Device Type', 'Updated'];
    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const lines = [
      headers.map(escapeCsv).join(','),
      ...exportRows.map((row) => ([
        row.project,
        row.level1,
        row.level2,
        row.level,
        row.task,
        row.status,
        row.category,
        row.start_date,
        row.due_date,
        row.done_by,
        row.checked_by,
        row.comments,
        row.monitoring,
        row.zone,
        row.zone_2,
        row.zone_3,
        row.device_type,
        row.updated
      ].map(escapeCsv).join(',')))
    ];

    const blob = new Blob([`﻿${lines.join('\n')}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${(project?.name || 'project').replace(/[^\w.-]+/g, '_')}-execution-${suffix}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const handlePrintExecution = (tasks, label) => {
    const exportRows = buildExecutionExportRows(tasks);
    if (!exportRows.length) {
      toast.error('No execution tasks to print');
      return;
    }

    const escapeHtml = (value) => String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    const rowsHtml = exportRows.map((row) => `
      <tr>
        <td>${escapeHtml(row.project)}</td>
        <td>${escapeHtml(row.level1)}</td>
        <td>${escapeHtml(row.level2)}</td>
        <td>${escapeHtml(row.level)}</td>
        <td>${escapeHtml(row.task)}</td>
        <td>${escapeHtml(row.status)}</td>
        <td>${escapeHtml(row.category)}</td>
        <td>${escapeHtml(row.start_date)}</td>
        <td>${escapeHtml(row.due_date)}</td>
        <td>${escapeHtml(row.done_by)}</td>
        <td>${escapeHtml(row.checked_by)}</td>
        <td>${escapeHtml(row.comments)}</td>
        <td>${escapeHtml(row.monitoring)}</td>
        <td>${escapeHtml(row.zone)}</td>
        <td>${escapeHtml(row.zone_2)}</td>
        <td>${escapeHtml(row.zone_3)}</td>
        <td>${escapeHtml(row.device_type)}</td>
        <td>${escapeHtml(row.updated)}</td>
      </tr>
    `).join('');

    const printWindow = window.open('', '_blank', 'width=1400,height=900');
    if (!printWindow) {
      toast.error('Allow pop-ups to print selected tasks');
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>${escapeHtml(project?.name || 'Project')} - Selected Execution Tasks</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #111; }
            h1 { margin: 0 0 8px; font-size: 22px; }
            p { margin: 0 0 16px; color: #555; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; vertical-align: top; }
            th { background: #f2f2f2; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1>${escapeHtml(project?.name || 'Project')}</h1>
          <p>${escapeHtml(label)} (${exportRows.length})</p>
          <table>
            <thead>
              <tr>
                <th>Project</th>
                <th>L1</th>
                <th>L2</th>
                <th>Level</th>
                <th>Task</th>
                <th>Status</th>
                <th>Category</th>
                <th>Start</th>
                <th>Due</th>
                <th>Done By</th>
                <th>Checked By</th>
                <th>Comments</th>
                <th>Monitoring</th>
                <th>Zone</th>
                <th>Zone 2</th>
                <th>Zone 3</th>
                <th>Device Type</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // ---- Wave 3 (project-detail-a): OneDrive scans / browse (06), store stock (05 #9), DN photo (05 #10) ----
  const onedriveFeature = useFeature('onedrive');
  // Until the status is known (or when it could not be read) keep the OneDrive controls: never claim "not set
  // up" without the server saying so — its own answer (503) is shown when someone uses them.
  const onedriveOn = onedriveFeature.placeholder ? true : Boolean(onedriveFeature.enabled);
  // The Stages tab needs the last scan too (06 #5: a stage links to its OneDrive folder) — only when one exists.
  const filesScans = useFilesScans(id, {
    enabled: canViewFiles && (activeTab === 'files' || (activeTab === 'stages' && Boolean(project?.files_scan_summary?.scanned_at))),
    refreshKey: filesAudit?.scan_id || '',
  });
  const materialStock = useMaterialStock(id, {
    enabled: canViewMaterial && activeTab === 'material' && boqItems.length > 0,
    refreshKey: `${boqItems.length}:${deliveryNotes.length}`,
  });
  const [selectedFolderPath, setSelectedFolderPath] = useState('');
  const [creatingFolders, setCreatingFolders] = useState(false);
  const [dnPhotoOpen, setDnPhotoOpen] = useState(false);

  // ---- Timeline, wave 3 (screen 10): Auto-plan (#3), Gantt + milestones (#2, #4), lookahead (#7), deps (#8) ----
  const workspace = useWorkspace();
  const [planSettingsState, setPlanSettingsState] = useState(null);
  const timelineToday = todayGst();
  const planSettings = useMemo(
    () => planSettingsState || { start: timelineToday, workWeek: workspace?.work_week || 'mon_sat', buffer: 10 },
    [planSettingsState, timelineToday, workspace?.work_week]
  );
  const [applyPlanOpen, setApplyPlanOpen] = useState(false);
  const planStages = useMemo(() => (project?.stages || []).filter((stage) => canSeeLpoLoiStage(stage, user)), [project?.stages, user]);
  const planWeekend = weekendFor(planSettings.workWeek);
  const autoPlan = useMemo(() => {
    if (activeTab !== 'timeline' || !project) return null;
    return buildAutoPlan({
      stages: planStages,
      mainTasks: executionTree.roots,
      template: project.execution_template || [],
      tree: executionTree,
      start: planSettings.start,
      weekend: planWeekend,
      bufferPercent: planSettings.buffer,
      // 10 #3: the plan card's "Re-plan all open items" switch (off = only slipped items and what they push)
      replan: Boolean(planSettings.replan),
    });
    // planWeekend follows planSettings.workWeek
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, project, planStages, executionTree, planSettings]);
  const timelineModel = useMemo(() => {
    if (activeTab !== 'timeline' || !project) return null;
    return buildTimelineModel({
      project,
      stages: planStages,
      tasks: executionTasks,
      tree: executionTree,
      invoices,
      variations,
      plan: autoPlan,
      group: timelineView.group,
      today: timelineToday,
      weekend: planWeekend,
      compact: isPhone,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, project, planStages, executionTasks, executionTree, invoices, variations, autoPlan, timelineView.group, timelineToday, planSettings.workWeek, isPhone]);

  if (loading) return <ProjectPageSkeleton />;
  if (!project) return <div className="p-8 text-ink-3">Project not found</div>;

  const visibleStages = (project.stages || []).filter((stage) => canSeeLpoLoiStage(stage, user));
  const stageWaitingOn = collectWaitingOn(visibleStages, stageLinesByStage);

  // ---- Project header + tab bar (04 #1, #2) ----
  const stagesSummary = summarizeStages(visibleStages);
  const loadedData = {
    boqItems,
    variations,
    invoices,
    variationInvoices,
    loaded: {
      boq: resourceStatus.boq === 'ready',
      variations: resourceStatus.variations === 'ready',
      invoices: resourceStatus.invoices === 'ready',
      variationInvoices: resourceStatus.variationInvoices === 'ready',
    },
  };
  const headerFacts = buildHeaderFacts({
    project,
    overview: projectOverview,
    data: loadedData,
    perms: { viewValue: canViewFinance, invoicing: canAccessInvoicing },
    stages: stagesSummary,
    execution: executionSummary,
  });
  const tabCounts = buildTabCounts({
    overview: projectOverview,
    data: loadedData,
    stages: stagesSummary,
    executionMainCount: executionMainTasksCount,
    structureBuildings: structureSummary.buildings,
  });
  // 06 #4: the scan just made, else the last stored one (manual or nightly) — no need to scan again.
  // Never the scan of another folder: after "Change" → "Save folder" the old checklist is not this folder's.
  const storedFilesScan = scanMatchesFolder(filesScans.data?.latest, project.files_root_path) ? filesScans.data.latest : null;
  const filesScan = filesAudit || storedFilesScan;
  const filesFolderChanged = !filesScan && Boolean(filesScans.data?.stale || (filesScans.data?.latest && !storedFilesScan));
  const filesWarn = canViewFiles ? filesAttention({ project, audit: filesScan, onedriveOn }) : null;
  const folderChecklist = buildFolderChecklist(filesScan);
  const folderHealthCounts = folderHealth(folderChecklist);
  const stageEvidence = buildStageEvidence(visibleStages, folderChecklist);
  const selectedFolderRow = folderChecklist.rows.find((row) => row.path === selectedFolderPath) || null;
  const selectedFolderLink = selectedFolderRow
    ? linkedStageForFolder(selectedFolderRow.path.split('/')[0], visibleStages)
    : null;
  const onedriveCanWrite = Boolean(filesScans.data?.can_write);
  const createFoldersState = !onedriveOn
    ? { allowed: false, reason: 'OneDrive is not set up on this server' }
    : !project.onedrive_status?.connected
      ? { allowed: false, reason: 'Connect OneDrive first' }
      : !canEditFiles
        ? { allowed: false, reason: 'Your role cannot change the project folder' }
        : !onedriveCanWrite
          ? { allowed: false, reason: 'The OneDrive connection can only read — reconnect OneDrive to allow creating folders' }
          : { allowed: true, reason: '' };
  // 04 #8: each stage's clock (days in its status / waiting to start, red past its SLA)
  const stageClocksById = stageClocks(visibleStages);
  // 07 #7: scheduled base-scope payments whose work is done in Execution (also a point of 04 #9)
  const readyToBill = findReadyToBill(invoices, executionTasks, {
    scopeValue: Number(project.original_scope_value ?? project.total_project_value ?? 0),
  });
  // 06 #5: a stage with no file of its own links to its OneDrive folder (the last scan's web link)
  const stageFolderFor = (stage) => {
    const items = (stageLinesByStage[stage.id] || stage.lines || []).filter((line) => String(line?.name || '').trim());
    const row = folderForStageLine(stage.name, items.length === 1 ? items[0].name : '', folderChecklist.rows);
    return row?.webUrl ? row : null;
  };
  const visibleTabs = PROJECT_TABS.filter((tab) => tabVisible[tab.value]);
  // The save state at the right of the tab bar follows the open tab (Material: typed quantities).
  // Another tab with unsaved edits (Structure, Execution) adds its count here and its leave check in
  // handleTabChange, so "N unsaved changes" and "Leave without saving?" behave the same on every tab.
  const activeTabDirtyCount = {
    material: materialDirtyCount,
    structure: structureDirtyCount,
    execution: executionDirtyCount,
  }[activeTab] || 0;
  const activeTabSaving = (activeTab === 'material' && materialSave.status === 'saving')
    || (activeTab === 'structure' && structureSaving)
    || (activeTab === 'execution' && executionSave.status === 'saving');
  const executionEmptyState = (
    <EmptyState
      icon={SearchX}
      title="No execution tasks match these filters"
      text="Try another view or clear the filters."
      action={<Button type="button" variant="outline" onClick={clearExecutionFilters}>Clear filters</Button>}
    />
  );
  // Execution edits: Edit level on Execution (canEdit also keeps Viewer accounts read-only; the backend checks the same).
  const canEditExecution = canEditExecutionModule && isStageLinesEditMode;
  // 09 #4: the bulk bar acts on rows you can see — Table (desktop) and List (phones), not Board / Matrix / Site mode.
  const executionSelectionShown = isPhone ? phoneExecutionView === 'list' : executionView === 'table';
  const executionSummaryBlock = (
    <ExecutionStatusSummary
      breakdown={executionStatusBreakdown(executionTasks)}
      total={executionMainTasksCount}
      subItems={executionTasks.length - executionMainTasksCount}
      datedCount={executionSummary.datedCount}
      onOpenTimeline={canViewTimeline ? () => handleTabChange('timeline') : null}
      compact={isPhone}
    />
  );
  const headerMenuItems = [
    { label: 'Copy link to this tab', icon: Link2, onSelect: copyPageLink },
    canEditProject && { label: 'Update handover date…', icon: CalendarClock, onSelect: () => setEndDateDialogOpen(true), separatorBefore: true },
    canEditProject && (project.status === 'On Hold'
      ? { label: 'Set Active', icon: CirclePlay, onSelect: () => handleSetProjectStatus('Active') }
      : { label: 'Put On Hold…', icon: CirclePause, onSelect: () => handleSetProjectStatus('On Hold') }),
  ];
  const variationsSummary = summarizeVariations(variations, variationInvoices, Number(project.original_scope_value ?? project.total_project_value ?? 0));
  // 11 #4: Admin › Access View/Edit levels — a View-only Variations role sees no Add / Edit / Delete / Record step (the server refuses them).
  const canWriteVariations = canEdit(user, 'variations');
  // The project folder path is saved with PUT /projects/{id}: Edit on Projects AND on Files (PROJECT_FIELD_MODULES).
  const canEditFolderPath = canEditProject && canEditFiles;
  const handoverState = getHandoverState(project);
  // 08 #8: the save bar — finished work a save would remove comes first, in red (amber: only empty sub-items).
  const structureImpactInfo = structureImpactSummary(structureImpact);
  const structureSaveDetailText = [structureImpactInfo.text, structureDiff.summary].filter(Boolean).join(' · ');
  const structureSaveDetail = (
    <span title={structureSaveDetailText} data-testid="structure-save-detail">
      {structureImpactInfo.warning ? (
        <span
          className={cn('mr-2 inline-flex items-center gap-1 font-semibold', structureImpactInfo.tone === 'red' ? 'text-status-red-ink' : 'text-status-amber-ink')}
          data-testid="structure-save-impact"
        >
          <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {structureImpactInfo.warning}
        </span>
      ) : null}
      {structureSaveDetailText}
    </span>
  );
  // 08 #1: what the two-pane structure editor may do — the same handlers the nested editor used.
  const structureLoc = (entry) => entry.location;
  const bmsSubTypeOptions = Array.from(new Set([...BMS_GENERIC_SUB_TYPES, ...customBmsSubTypes]));
  // 28 #8: a View-only Project Structure role reads the tree (no rename / add / delete / drag / device edits).
  const canEditStructureTree = canEditStructure && isStageLinesEditMode;
  const structureOps = {
    canEdit: canEditStructureTree,
    renamingId: renamingStructureId,
    startRename: focusStructureName,
    stopRename: stopStructureRename,
    commitName: (entry, name) => {
      const { buildingId, floorId, roomId, panelId } = structureLoc(entry);
      if (entry.kind === 'building') handleBuildingChange(buildingId, name);
      else if (entry.kind === 'floor') handleFloorChange(buildingId, floorId, name);
      else if (entry.kind === 'panel' || entry.kind === 'zone') handlePanelChange(buildingId, floorId, panelId, name, entry.kind);
      else if (entry.kind === 'room') handleRoomChange(buildingId, floorId, roomId, name);
      else if (entry.kind === 'roomPanel') handleRoomPanelChange(buildingId, floorId, roomId, panelId, name, entry.node.type);
      stopStructureRename();
    },
    rowMenus: (entry) => {
      const { buildingId, floorId, roomId, panelId } = structureLoc(entry);
      if (entry.kind === 'building') {
        return {
          addItems: [
            { label: 'Floor', icon: Layers, onSelect: () => handleAddFloor(buildingId) },
            { label: 'Panel', icon: Cpu, description: 'Adds a new floor with one panel', onSelect: () => handleAddPanelAtBuilding(buildingId) },
          ],
          menuItems: [
            { label: 'Rename', icon: Pencil, onSelect: () => focusStructureName(buildingId) },
            { label: 'Duplicate building', icon: Copy, onSelect: () => handleDuplicateBuilding(buildingId) },
            { label: 'Delete building…', icon: Trash2, danger: true, onSelect: () => handleDeleteBuilding(buildingId) },
          ],
        };
      }
      if (entry.kind === 'floor') {
        return {
          addItems: [
            { label: 'Devices…', icon: Cpu, onSelect: () => handleOpenAddDevicesDialog(buildingId, floorId, null) },
            { label: 'Panel', icon: Server, onSelect: () => handleAddPanel(buildingId, floorId) },
            { label: 'Room', icon: DoorOpen, onSelect: () => handleAddRoom(buildingId, floorId) },
            { label: 'Zone', icon: SquareDashed, onSelect: () => handleAddZone(buildingId, floorId) },
          ],
          menuItems: [
            { label: 'Rename', icon: Pencil, onSelect: () => focusStructureName(floorId) },
            { label: 'Duplicate floor', icon: Copy, onSelect: () => handleDuplicateFloor(buildingId, floorId) },
            { label: 'Delete floor…', icon: Trash2, danger: true, onSelect: () => handleDeleteFloor(buildingId, floorId) },
          ],
        };
      }
      if (entry.kind === 'panel' || entry.kind === 'zone') {
        const zone = entry.kind === 'zone';
        return {
          addItems: [{ label: 'Devices…', icon: Cpu, onSelect: () => handleOpenAddDevicesDialog(buildingId, floorId, panelId) }],
          menuItems: [
            { label: 'Rename', icon: Pencil, onSelect: () => focusStructureName(panelId) },
            { label: zone ? 'Make it a panel' : 'Make it a zone', icon: zone ? Cpu : SquareDashed, onSelect: () => handlePanelTypeChange(buildingId, floorId, panelId, zone ? 'panel' : 'zone') },
            { label: zone ? 'Delete zone…' : 'Delete panel…', icon: Trash2, danger: true, onSelect: () => handleDeletePanel(buildingId, floorId, panelId) },
          ],
        };
      }
      if (entry.kind === 'room') {
        return {
          addItems: [
            { label: 'Devices…', icon: Cpu, onSelect: () => handleOpenAddDevicesDialog(buildingId, floorId, roomId, null) },
            { label: 'Panel', icon: Server, onSelect: () => handleAddPanelToRoom(buildingId, floorId, roomId) },
          ],
          menuItems: [
            { label: 'Rename', icon: Pencil, onSelect: () => focusStructureName(roomId) },
            { label: 'Duplicate room', icon: Copy, onSelect: () => handleDuplicateRoom(buildingId, floorId, roomId) },
            { label: 'Delete room…', icon: Trash2, danger: true, onSelect: () => handleDeleteRoom(buildingId, floorId, roomId) },
          ],
        };
      }
      return {
        addItems: [{ label: 'Devices…', icon: Cpu, onSelect: () => handleOpenAddDevicesDialog(buildingId, floorId, roomId, panelId) }],
        menuItems: [
          { label: 'Rename', icon: Pencil, onSelect: () => focusStructureName(panelId) },
          { label: 'Delete panel…', icon: Trash2, danger: true, onSelect: () => handleDeleteRoomPanel(buildingId, floorId, roomId, panelId) },
        ],
      };
    },
    devicesOf: (entry) => (Array.isArray(entry.node?.devices) ? entry.node.devices : []),
    deviceChange: (entry, deviceId, field, value) => {
      const { buildingId, floorId, roomId, panelId } = structureLoc(entry);
      if (entry.kind === 'room') handleRoomDeviceChange(buildingId, floorId, roomId, deviceId, field, value);
      else if (entry.kind === 'roomPanel') handleRoomPanelDeviceChange(buildingId, floorId, roomId, panelId, deviceId, field, value);
      else handleDeviceChange(buildingId, floorId, panelId || null, deviceId, field, value);
    },
    deleteDevice: (entry, deviceId) => {
      const { buildingId, floorId, roomId, panelId } = structureLoc(entry);
      if (entry.kind === 'room') handleDeleteRoomDevice(buildingId, floorId, roomId, deviceId);
      else if (entry.kind === 'roomPanel') handleDeleteRoomPanelDevice(buildingId, floorId, roomId, panelId, deviceId);
      else handleDeleteDevice(buildingId, floorId, panelId || null, deviceId);
    },
    addDevices: (entry) => {
      const { buildingId, floorId, roomId, panelId } = structureLoc(entry);
      if (entry.kind === 'room') handleOpenAddDevicesDialog(buildingId, floorId, roomId, null);
      else if (entry.kind === 'roomPanel') handleOpenAddDevicesDialog(buildingId, floorId, roomId, panelId);
      else handleOpenAddDevicesDialog(buildingId, floorId, panelId || null);
    },
    // 08 #1 (final QA): rooms and a room's panels select and delete together too (most of LIV is rooms)
    selectable: (entry) => ['floor', 'panel', 'zone', 'room', 'roomPanel'].includes(entry.kind),
    selectedOf: (entry) => {
      const { buildingId, floorId, roomId, panelId } = structureLoc(entry);
      return selectedStructureDevices[getPanelSelectionKey(buildingId, floorId, roomId || null, panelId || null)] || [];
    },
    toggleDevice: (entry, deviceId, checked) => {
      const { buildingId, floorId, roomId, panelId } = structureLoc(entry);
      handleToggleStructureDeviceSelection(buildingId, floorId, panelId || null, deviceId, checked, roomId || null);
    },
    toggleAll: (entry, checked) => {
      const { buildingId, floorId, roomId, panelId } = structureLoc(entry);
      handleToggleAllPanelDevices(buildingId, floorId, panelId || null, entry.node?.devices || [], checked, roomId || null);
    },
    deleteSelected: (entry) => {
      const { buildingId, floorId, roomId, panelId } = structureLoc(entry);
      handleDeleteSelectedDevices(buildingId, floorId, panelId || null, roomId || null);
    },
    dragging: draggingStructureDevice,
    dragStart: (entry, deviceId) => {
      const { buildingId, floorId, roomId, panelId } = structureLoc(entry);
      handleStructureDeviceDragStart(buildingId, floorId, roomId || null, panelId || null, deviceId);
    },
    dragEnd: () => setDraggingStructureDevice(null),
    drop: (entry, beforeDeviceId) => {
      const { buildingId, floorId, roomId, panelId } = structureLoc(entry);
      void moveStructureDevices(buildStructureLocation(buildingId, floorId, roomId || null, panelId || null), beforeDeviceId || null);
    },
    editingDevice: editingStructureDevice,
    setEditingDevice: setEditingStructureDevice,
    focusClass: structureFocusClass,
    isImported: isImportedStructureNode,
    jump: openStructureNode,
  };
  // 08 #1 (final QA): BMS projects use the same two-pane editor. A sub's sub-items sit at path [] of the sub, a
  // sub-item's at its own node path; the handlers are the ones the old nested editor used.
  if (isBmsProject) {
    const bmsPathOf = (entry) => (entry.kind === 'bmsNode' ? entry.location.path : []);
    const bmsAt = (entry) => [entry.location.buildingId, entry.location.floorId, bmsPathOf(entry)];
    const bmsSelectionKey = (entry) => getBmsSubSelectionKey(...bmsAt(entry));
    structureOps.commitName = (entry, name) => {
      const { buildingId, floorId, path } = entry.location;
      if (entry.kind === 'building') handleBuildingChange(buildingId, name);
      else if (entry.kind === 'sub') handleBmsRootSubChange(buildingId, floorId, 'name', name);
      else handleBmsSubChange(buildingId, floorId, path, 'name', name);
      stopStructureRename();
    };
    structureOps.rowMenus = (entry) => {
      const { buildingId, floorId, path } = entry.location;
      if (entry.kind === 'building') {
        return {
          addItems: [{ label: 'Sub', icon: Server, onSelect: () => handleAddBmsRootSub(buildingId) }],
          menuItems: [
            { label: 'Rename', icon: Pencil, onSelect: () => focusStructureName(buildingId) },
            { label: 'Duplicate building', icon: Copy, onSelect: () => handleDuplicateBuilding(buildingId) },
            { label: 'Delete building…', icon: Trash2, danger: true, onSelect: () => handleDeleteBuilding(buildingId) },
          ],
        };
      }
      if (entry.kind === 'sub') {
        return {
          addItems: [{ label: 'Sub-item', icon: Cpu, onSelect: () => handleAddBmsSub(buildingId, floorId) }],
          menuItems: [
            { label: 'Rename', icon: Pencil, onSelect: () => focusStructureName(floorId) },
            { label: 'Duplicate sub', icon: Copy, onSelect: () => handleDuplicateFloor(buildingId, floorId) },
            { label: 'Delete sub…', icon: Trash2, danger: true, onSelect: () => handleDeleteBmsRootSub(buildingId, floorId) },
          ],
        };
      }
      return {
        addItems: [{ label: 'Sub-item', icon: Cpu, onSelect: () => handleAddBmsSub(buildingId, floorId, path) }],
        menuItems: [
          { label: 'Rename', icon: Pencil, onSelect: () => focusStructureName(entry.id) },
          {
            label: 'Duplicate',
            icon: Copy,
            onSelect: () => updateBmsSiblingGroup(buildingId, floorId, path.slice(0, -1), (siblings) => siblings.flatMap((item) => (
              item.id === entry.id ? [item, cloneBmsNode(item, allowDuplicateDeviceAddresses)] : [item]
            ))),
          },
          { label: 'Delete sub-item…', icon: Trash2, danger: true, onSelect: () => handleDeleteBmsSub(buildingId, floorId, path) },
        ],
      };
    };
    // "Drag subs" on: sub-items drop onto a sub / sub-item in the tree, subs onto a building
    structureOps.dragging = bmsSubDragMode ? (draggingBmsSub || draggingBmsRootSub) : null;
    structureOps.canDragRow = (entry) => bmsSubDragMode && entry.kind === 'sub';
    structureOps.dragRowStart = (entry) => handleBmsRootSubDragStart(entry.location.buildingId, entry.location.floorId);
    structureOps.dragEnd = () => { setDraggingBmsSub(null); setDraggingBmsRootSub(null); };
    structureOps.canDropOn = (entry) => bmsSubDragMode && (
      (Boolean(draggingBmsSub) && (entry.kind === 'sub' || entry.kind === 'bmsNode'))
      || (Boolean(draggingBmsRootSub) && entry.kind === 'building')
    );
    structureOps.drop = (entry) => {
      if (draggingBmsRootSub && entry.kind === 'building') { void moveBmsRootSub(entry.location.buildingId); return; }
      if (draggingBmsSub) void moveBmsSubs(buildBmsLocation(...bmsAt(entry)));
    };
    structureOps.isImported = (level, nodeId) => isImportedStructureNode(level === 'devices' ? 'devices' : level, nodeId);
    structureOps.bms = {
      typeOptions: bmsSubTypeOptions,
      createType: registerCustomBmsSubType,
      itemsOf: (entry) => (entry.kind === 'sub' ? (entry.node.devices || []) : getStructureNodeChildren(entry.node)),
      change: (entry, item, field, value) => handleBmsSubChange(entry.location.buildingId, entry.location.floorId, [...bmsPathOf(entry), item.id], field, value),
      changeSelf: (entry, field, value) => {
        if (entry.kind === 'sub') handleBmsRootSubChange(entry.location.buildingId, entry.location.floorId, field, value);
        else handleBmsSubChange(entry.location.buildingId, entry.location.floorId, entry.location.path, field, value);
      },
      add: (entry) => (entry.kind === 'building' ? handleAddBmsRootSub(entry.location.buildingId) : handleAddBmsSub(...bmsAt(entry))),
      addInside: (entry, item) => handleAddBmsSub(entry.location.buildingId, entry.location.floorId, [...bmsPathOf(entry), item.id]),
      duplicate: (entry, item) => updateBmsSiblingGroup(...bmsAt(entry), (siblings) => siblings.flatMap((sibling) => (
        sibling.id === item.id ? [sibling, cloneBmsNode(sibling, allowDuplicateDeviceAddresses)] : [sibling]
      ))),
      remove: (entry, item) => handleDeleteBmsSub(entry.location.buildingId, entry.location.floorId, [...bmsPathOf(entry), item.id]),
      selected: (entry) => selectedBmsSubs[bmsSelectionKey(entry)] || [],
      toggle: (entry, itemId, checked) => handleToggleBmsSubSelection(...bmsAt(entry), itemId, checked),
      toggleAll: (entry, items, checked) => handleToggleAllBmsSubSelection(...bmsAt(entry), items, checked),
      copy: (entry, items) => handleCopySelectedBmsSubs(...bmsAt(entry), items),
      clipboardCount: copiedBmsSubs.length,
      paste: (entry) => handlePasteBmsSubs(...bmsAt(entry)),
      duplicateSelected: (entry, items) => handleDuplicateSelectedBmsSubs(...bmsAt(entry), items),
      deleteSelected: (entry) => handleDeleteSelectedBmsSubs(...bmsAt(entry)),
      move: (entry, direction) => handleMoveSelectedBmsSubs(...bmsAt(entry), direction),
      dragMode: bmsSubDragMode,
      dragging: draggingBmsSub,
      dragStart: (entry, item) => handleBmsSubDragStart(...bmsAt(entry), item.id),
      dragEnd: () => setDraggingBmsSub(null),
      dropInto: (entry, item) => { void moveBmsSubs(buildBmsLocation(...bmsAt(entry)), item ? item.id : null); },
    };
  }
  // 10 #3: who may apply a plan (the server checks the same: Timeline edit + Stages / Execution for their dates).
  const planProposals = autoPlan ? autoPlan.items.filter((item) => item.proposed) : [];
  const planHasStageItems = planProposals.some((item) => item.kind === 'stage');
  const planHasTaskItems = planProposals.some((item) => item.kind === 'execution');
  const canApplyPlan = canEditTimeline
    && (!planHasStageItems || canEditStages) && (!planHasTaskItems || canEditExecutionModule);
  // A Completed / Cancelled project is not planned any more (the plan card says so; Apply plan is off).
  const projectClosedStatus = /completed|cancelled/i.test(String(project?.status || '')) ? project.status : '';
  const applyPlanBlocked = projectClosedStatus
    ? `The project is ${projectClosedStatus} — nothing to plan`
    : !planProposals.length
    ? 'Every open item has dates — nothing to plan'
    : !canApplyPlan
      ? 'Your role cannot change these dates'
      : executionDirtyCount > 0
        ? 'Save or discard your Execution edits first'
        : '';
  const handleApplyPlan = async ({ updateEndDate }) => {
    try {
      await api.post(`/projects/${id}/schedule/apply`, {
        start: autoPlan.start,
        work_week: planSettings.workWeek,
        buffer_percent: planSettings.buffer,
        handover: autoPlan.handover,
        update_end_date: Boolean(updateEndDate),
        stages: planProposals.filter((item) => item.kind === 'stage').map(({ id: itemId, start, end }) => ({ id: itemId, start, end })),
        execution: planProposals.filter((item) => item.kind === 'execution').map(({ id: itemId, start, end }) => ({ id: itemId, start, end })),
      });
    } catch (err) {
      throw new Error(getErrorMessage(err, 'Could not apply the plan'));
    }
    toast.success(`Plan applied · ${planProposals.length} item${planProposals.length === 1 ? '' : 's'} dated${updateEndDate ? ` · handover ${formatDate(autoPlan.handover)}` : ''}`);
    await fetchData();
  };
  const materialVariationsWithItems = variations.filter((variation) => Array.isArray(variation.items) && variation.items.length > 0).length;
  const materialVoNeeded = summarizeVariationNeeded(allowSubBoq ? mainBoqRows : materialRows);
  // 05 #8 / 11 #5: "Draft VO-00N" opens Add variation with the shortages filled in (Material and Variations tabs)
  const draftVariationFromMaterial = () => openVariationPanel(null, {
    description: `Additional materials — site needs exceed BOQ + VOs on ${materialVoNeeded.count} line${materialVoNeeded.count === 1 ? '' : 's'}`,
    items: materialVoNeeded.lines.map((line) => ({
      boq_item_id: line.id,
      part_number: line.part,
      description: line.description,
      unit: line.unit,
      qty: line.needed,
    })),
  });
  const materialSubtitle = [
    'BOQ, variations and site deliveries',
    `${boqItems.length} line${boqItems.length === 1 ? '' : 's'}`,
    `${materialVariationsWithItems} VO${materialVariationsWithItems === 1 ? '' : 's'}`,
    `${materialDeliveryNoteColumns.length} delivery note${materialDeliveryNoteColumns.length === 1 ? '' : 's'}`,
  ].join(' · ');
  const selectedMaterialRows = materialRows.filter((row) => selectedBoqRowIds.includes(row.id));
  const singleSelectedMaterialRow = selectedMaterialRows.length === 1 ? selectedMaterialRows[0] : null;
  const waitingPartyNames = stageWaitingOn.parties.filter((party) => party.items.length).map((party) => party.label);
  const stagesSubtitle = [
    `${visibleStages.length} delivery stage${visibleStages.length === 1 ? '' : 's'}`,
    `${visibleStages.filter((stage) => isStageDone(stage.status)).length} approved`,
    stageWaitingOn.total
      ? `${stageWaitingOn.total} item${stageWaitingOn.total === 1 ? '' : 's'} waiting on ${waitingPartyNames.join(' & ')}`
      : 'nothing waiting on others',
  ].join(' · ');
  const clientOptions = Array.from(new Set([...(projectFieldOptions.clients || []), projectForm.client || ''].filter(Boolean)));
  const consultantOptions = Array.from(new Set([...(projectFieldOptions.consultants || []), projectForm.consultant || ''].filter(Boolean)));
  const contractorOptions = Array.from(new Set([...(projectFieldOptions.contractors || []), projectForm.contractor || ''].filter(Boolean)));
  const systemTypeOptions = Array.from(new Set([...(projectFieldOptions.systemTypes || []), projectForm.system_type || ''].filter(Boolean)));
  const projectEngineerOptions = Array.from(
    new Set(
      platformUsers
        .map((entry) => String(entry.name || '').trim())
        .concat([projectForm.project_engineer || '', projectForm.senior_project_engineer || ''])
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b));
  const stageLineTaskIds = new Set(
    projectTasks
      .filter((task) => task.source === 'stage' && task.stage_line_id)
      .map((task) => task.stage_line_id)
  );
  const onedriveStatus = project.onedrive_status || { connected: false };
  const stageLinesSaved = (stage) => stageLinesByStage[stage.id] || normalizeLines(stage.lines);
  const panelStage = stagePanel.stageId ? visibleStages.find((stage) => stage.id === stagePanel.stageId) || null : null;
  const panelStageIndex = panelStage ? visibleStages.findIndex((stage) => stage.id === panelStage.id) : -1;
  const nextStage = stagesSummary.next;
  // 04 #9: "What is blocking this project?" — from the data on this page (AI wording on top when it is on)
  const stageBlockers = buildBlockers({
    project,
    stages: visibleStages,
    linesByStage: stageLinesByStage,
    clocks: stageClocksById,
    waitingOn: stageWaitingOn,
    execution: canViewExecution ? executionSummary : null,
    handover: handoverState,
    readyToBill: canAccessInvoicing && resourceStatus.invoices === 'ready' ? readyToBill : [],
  });
  const blockersBasis = ['stages', canViewExecution && 'execution', canAccessInvoicing && resourceStatus.invoices === 'ready' && 'invoicing'].filter(Boolean);
  const stageMenuFor = (stage, summary) => [
    // 28 #8: a View-only Stages role opens the same drawer read-only, so the label does not promise an edit
    {
      label: summary.count ? `${canEditStages ? 'Edit' : 'View'} ${summary.count} item${summary.count === 1 ? '' : 's'}` : 'Open stage',
      icon: canEditStages ? Pencil : Eye,
      onSelect: () => openStagePanel(stage),
    },
    canEditStages && { label: 'Add item', icon: SquarePlus, onSelect: () => openStagePanel(stage, { startWith: 'add-item' }) },
    canCreateTasks && { label: 'Create task from stage', icon: ListTodo, onSelect: () => handleOpenStageTaskDialog(stage, { id: '', name: '', note: '' }) },
    { label: 'Copy link to stage', icon: Link2, onSelect: () => copyStageLink(stage) },
    canEditStages && summary.count > 0 && {
      label: 'Delete items…',
      icon: Trash2,
      danger: true,
      onSelect: () => handleClearStageItems(stage, summary),
      testId: `stage-clear-items-${stage.id}`,
    },
  ];

  // ---- Invoicing render values (07) ----
  const invoicingReady = resourcesSettled(resourceStatus, TAB_BLOCKING_RESOURCES.invoicing);
  const approvedVariationCount = variations.filter((variation) => variation.status === 'Approved').length;
  const voCodeById = new Map(variations.map((variation) => [variation.id, variation.vo_number || 'VO']));
  const invoiceGroups = groupInvoices(
    sortedInvoices.map((invoice) => ({ ...invoice, scopeCode: invoice.variation_id ? voCodeById.get(invoice.variation_id) || 'VO' : '' })),
    { query: invoiceQuery },
  );
  const invoiceRows = invoiceGroups.flatMap((group) => group.rows);
  const selectedInvoices = invoiceRows.filter((invoice) => selectedInvoiceIds.includes(invoice.id));
  const quickBooksReady = Boolean(quickBooksStatus?.configured && quickBooksStatus?.connected);
  // Shown once the status is known (or could not be read), so the reason is visible, not only a tooltip.
  const quickBooksNoteShown = canEditInvoices && !quickBooksReady && ['ready', 'error'].includes(resourceStatus.qbStatus);
  const panelInvoice = invoicePanel.invoiceId ? [...invoices, ...variationInvoices].find((invoice) => invoice.id === invoicePanel.invoiceId) || null : null;
  const panelScopeId = invoicePanel.mode === 'add' ? invoiceScope : (panelInvoice?.variation_id || 'base');
  const panelVariation = panelScopeId !== 'base' ? variations.find((variation) => variation.id === panelScopeId) : null;
  const panelScopeLabel = panelScopeId === 'base' ? 'Base scope' : (panelVariation?.vo_number || 'Variation');
  const panelScopeValue = panelScopeId === 'base'
    ? Number(project.original_scope_value ?? project.total_project_value ?? 0)
    : Number(panelVariation?.total_value ?? panelVariation?.financial_impact ?? 0);
  const panelOtherTotal = (panelScopeId === 'base' ? invoices : variationInvoices.filter((invoice) => invoice.variation_id === panelScopeId))
    .filter((invoice) => invoice.id !== panelInvoice?.id)
    .reduce((sum, invoice) => sum + Number(invoice.amount || 0), 0);
  // 07 #8: overdue + DSO over the base scope and the approved variations (the money bar's contract)
  const approvedVariationIds = new Set(variations.filter((variation) => variation.status === 'Approved').map((variation) => variation.id));
  // The 12-month DLP runs from the real handover: while the planned date has passed and the project is not
  // Completed, retention has no due date yet ("handover pending").
  const projectHandedOver = project.status === 'Completed';
  const handoverPending = !projectHandedOver && Boolean(project.end_date) && String(project.end_date).slice(0, 10) < todayGst();
  const invoiceCollection = summarizeCollection(
    [...invoices, ...variationInvoices.filter((invoice) => approvedVariationIds.has(invoice.variation_id))],
    { handoverDate: project.end_date, handedOver: projectHandedOver },
  );
  const panelRetentionHint = panelInvoice && !panelInvoice.due_date && project.end_date
    && splitTypes(panelInvoice.invoice_type).some((type) => type.toLowerCase().includes('retention'))
    ? (handoverPending
      ? `Retention: due 12 months after handover — the handover (planned ${formatDate(project.end_date)}) has not happened yet — unless you set a date.`
      : `Retention: due after the 12-month defects period — ${formatDate(addDaysIso(project.end_date, RETENTION_DLP_DAYS))} — unless you set a date.`)
    : '';
  const invoiceRowMenu = (invoice) => {
    // same reason as the toolbar note: not set up on this server vs not connected
    const sync = canSyncToQuickBooks(invoice, { configured: quickBooksReady, status: quickBooksStatus });
    return [
      { label: canEditInvoices ? 'Edit invoice' : 'Open invoice', icon: Pencil, onSelect: () => openInvoicePanel('edit', invoice) },
      canCreateTasks && { label: 'Add task', icon: ListTodo, onSelect: () => handleOpenTaskDialog('invoicing', invoice) },
      canEditInvoices && { label: 'Attach photos', icon: ImagePlus, onSelect: () => openInvoicePanel('edit', invoice, 'photos') },
      // pushing to QuickBooks is an Invoicing change on the server (quickbooks_module) — not offered to View only
      canEditInvoices && { label: 'Sync to QuickBooks', icon: RefreshCw, onSelect: () => handleSyncInvoices([invoice]), disabled: !sync.ok || Boolean(qbSync), hint: sync.ok ? undefined : sync.reason },
      // Delete / Request deletion… / Approve deletion… (BUG-006) are added by InvoicesTable for this user
    ];
  };

  // ---- Material render values (05 #3 grouped columns, #5 inline quantities, filters) ----
  const materialReady = resourcesSettled(resourceStatus, TAB_BLOCKING_RESOURCES.material);
  const materialChangedKeys = new Set(materialDiff.changes.map((change) => change.key));
  const materialInvalidByKey = new Map(materialDiff.invalid.map((entry) => [entry.key, entry.error]));
  const cellState = (key, value) => ({
    value,
    changed: materialChangedKeys.has(key),
    invalid: materialInvalidByKey.has(key),
    error: materialInvalidByKey.get(key),
  });
  const deliveryNoteById = new Map(deliveryNotes.map((note) => [note.id, note]));
  const materialQtyCells = {
    boq: (row) => cellState(`boq:${row.id}`, boqEdits[row.id]?.boq_qty ?? row.boq_qty ?? 0),
    subBoq: (row) => cellState(`sub:${row.id}`, boqEdits[row.id]?.sub_boq ?? row.sub_boq ?? ''),
    dn: (column, row) => {
      const noteId = column.primary_note_id;
      const merged = (column.note_ids || []).reduce((sum, noteKey) => sum + Number(row.deliveredByDn?.[noteKey] || 0), 0);
      const note = deliveryNoteById.get(noteId);
      const inScope = !note || inDeliveryNoteScope(note, row);
      return {
        ...cellState(`dn:${noteId}:${row.id}`, dnEdits[noteId]?.[row.id] ?? merged),
        readOnly: !inScope,
        readOnlyReason: inScope ? '' : `${column.dn_number} is for Sub BOQ ${note.sub_boq || deliveryNoteScope(note)} — this line is in ${row.sub_boq || 'no Sub BOQ'}`,
      };
    },
    onBoqChange: handleBoqQtyDraftChange,
    onSubBoqChange: handleBoqSubGroupDraftChange,
    onDnChange: (column, rowId, value) => handleDnQtyChange(column.primary_note_id, rowId, value),
    onSubBoqQtyChange: handleSubBoqQtyDraftChange,
  };
  const materialSubBoqFilter = allowSubBoq && materialSubBoq && materialRowsBySubBoq[materialSubBoq] ? materialSubBoq : null;
  const materialFiltering = Boolean(materialQuery.trim()) || materialView !== 'all' || Boolean(materialSubBoqFilter);
  const materialMatches = (row) => {
    const query = materialQuery.trim().toLowerCase();
    if (query && ![row.part_number, row.description, row.sub_boq].some((value) => String(value || '').toLowerCase().includes(query))) return false;
    if (materialSubBoqFilter && normalizeSubBoqLabel(row.sub_boq) !== materialSubBoqFilter) return false;
    if (materialView === 'short') return row.remaining > 0;
    if (materialView === 'complete') return row.remaining <= 0;
    if (materialView === 'vo') return row.variationNeeded > 0;
    return true;
  };
  const materialNumberById = new Map(materialRows.map((row, index) => [row.id, index + 1]));
  // Filters use the saved numbers (a row doesn't vanish from "Short" while its qty is typed); rows show the preview.
  const materialPreviewById = new Map(materialPreviewRows.map((row) => [row.id, row]));
  const shownMaterialRow = (row) => materialPreviewById.get(row.id) || row;
  const materialEntries = !allowSubBoq
    ? materialRows.filter(materialMatches).map((row) => ({ type: 'row', row: shownMaterialRow(row), number: materialNumberById.get(row.id) }))
    : Object.keys(materialRowsBySubBoq).sort((a, b) => a.localeCompare(b)).flatMap((label) => {
      const rows = (materialRowsBySubBoq[label] || []).filter(materialMatches);
      if (materialFiltering && !rows.length) return [];
      const collapsed = !materialFiltering && collapsedMaterialSubBoqs[label] !== false;
      return [
        {
          type: 'header',
          label,
          count: rows.length,
          collapsed,
          qty: cellState(`subqty:${label}`, getSubBoqQtyDisplayValue(label)),
        },
        ...(collapsed ? [] : rows.map((row) => ({ type: 'row', row: shownMaterialRow(row), number: materialNumberById.get(row.id) }))),
      ];
    });
  const toggleMaterialGroupColumns = (kind) => {
    const setter = kind === 'vo' ? setMaterialVoOpen : setMaterialDnOpen;
    setter((open) => {
      writeStored(storageKey('project', 'material', kind === 'vo' ? 'voColumns' : 'dnColumns'), !open);
      return !open;
    });
  };

  return (
    // Wave 2: the page frame and all eight tabs use the app foundation (fonts, tokens). Only the BMS sub-item
    // rows and a few dialogs (apple-project-dialog) keep the older Apple skin — see index.css.
    <div data-testid="project-detail-page" className="min-w-0 space-y-0">
      <ProjectHeader
        project={project}
        facts={headerFacts}
        compact={isPhone}
        backTo={isPhone ? '/projects' : undefined}
        canViewHistory={canViewHistory}
        onOpenHistory={() => setHistoryDialogOpen(true)}
        canEditDetails={canEditProject}
        onEditDetails={handleOpenProjectEditor}
        menuItems={headerMenuItems}
        detailsOpen={detailsOpen}
        onDetailsOpenChange={handleDetailsOpenChange}
        canViewFinance={canViewFinance}
        details={(
          <ProjectDetailsList
            project={project}
            canViewValue={canViewFinance}
            paymentTerms={parseMultiSelectString(normalizePaymentTerms(project.payment_terms))}
            onedriveLabel={onedriveStatus.connected ? (onedriveStatus.user_email || onedriveStatus.user_name || 'Connected') : 'Not connected'}
            customFields={normalizeProjectCustomFields(project.custom_fields)}
            deviceAddressControl={(
              <p className="text-[13px] text-ink-2" data-testid="details-device-address-rule">
                {allowDuplicateDeviceAddresses ? 'Duplicates allowed in this project' : 'Each device address is used once'}
                {canViewProjectStructure ? (
                  <button
                    type="button"
                    className="ml-2 font-semibold text-brand-ink hover:underline"
                    onClick={() => handleTabChange('structure')}
                  >
                    Change in Project Structure
                  </button>
                ) : null}
              </p>
            )}
          />
        )}
      />

      {canViewHistory ? (
        <HistoryDialog
          open={historyDialogOpen}
          onOpenChange={setHistoryDialogOpen}
          title={`${project.name} History`}
          pageType="project"
          entityType="project"
          entityId={id}
        />
      ) : null}

      <UpdateEndDateDialog
        open={endDateDialogOpen}
        onOpenChange={setEndDateDialogOpen}
        currentDate={project.end_date}
        onSave={handleSaveEndDate}
      />

      <Tabs value={activeTab} onValueChange={handleTabChange} activationMode="manual" className="mt-5 w-full">
        <ProjectTabsBar
          tabs={visibleTabs}
          counts={tabCounts}
          filesWarn={filesWarn}
          dirtyCount={activeTabDirtyCount}
          saving={activeTabSaving}
          phone={isPhone}
        />

        <TabsContent value="stages" className="mt-5 space-y-4" data-testid="stages-content">
          <TabHeader
            title="Stages"
            subtitle={stagesSubtitle}
            // phones (as approved): the open tab pill names the tab and the waiting card says what waits — the
            // heading stays for screen readers only, so stage cards 01-03 are on the first screen
            className={isPhone ? 'sr-only' : undefined}
            actions={isPhone ? null : (
              <>
                {canCreateTasks ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleOpenStageTaskDialog(nextStage || visibleStages[0] || {}, { id: '', name: '', note: '' })}
                    disabled={!visibleStages.length}
                    data-testid="stages-create-task"
                  >
                    <ListTodo aria-hidden="true" />
                    Create task
                  </Button>
                ) : null}
                {canEditStages && nextStage ? (
                  <Button type="button" onClick={() => openStagePanel(nextStage)} data-testid="stages-update-next">
                    <Pencil aria-hidden="true" />
                    Update {nextStage.name}
                  </Button>
                ) : null}
              </>
            )}
          />
          {isPhone ? (
            <>
              <StageDotsStepper
                stages={visibleStages}
                nextStage={nextStage}
                nextNumber={stagesSummary.nextNumber}
                approved={stagesSummary.approved}
                onSelect={(stage) => openStagePanel(stage)}
              />
              <StageWaitingCard waitingOn={stageWaitingOn} stages={visibleStages} onOpen={(stage) => openStagePanel(stage)} />
              {/* the stage cards come first on phones (the first screen shows stages 01-03); blockers follow */}
              <div className="flex items-baseline justify-between gap-3 pt-1">
                <h3 className="text-[15px] font-bold text-ink">All stages</h3>
                <span className="text-[12.5px] text-ink-3">Tap a stage to {canEditStages ? 'update' : 'view'}</span>
              </div>
              <StagesList
                phone
                stages={visibleStages}
                linesByStage={stageLinesByStage}
                waitingByStage={stageWaitingOn.byStage}
                nextStageId={nextStage?.id}
                onOpen={(stage) => openStagePanel(stage)}
                onOpenReference={handleOpenReference}
                highlightedStageId={highlightedStageId}
                canEdit={canEditStages}
                clocks={stageClocksById}
                folderFor={canViewFiles ? stageFolderFor : undefined}
              />
              <BlockersCard
                projectId={id}
                projectName={project.name}
                points={stageBlockers}
                basis={blockersBasis}
                refreshKey={project.updated_at}
                canAskAi={hasPermission(user, 'ai')}
              />
              {canEditStages && nextStage ? (
                <div className="sticky bottom-[calc(12px+var(--app-bottom-nav-height,0px))] z-20 pt-2">
                  <Button type="button" size="lg" className="w-full shadow-overlay" onClick={() => openStagePanel(nextStage)} data-testid="stages-update-next">
                    <Pencil aria-hidden="true" />
                    Update {nextStage.name}
                  </Button>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <StagePipeline stages={visibleStages} waitingByStage={stageWaitingOn.byStage} clocks={stageClocksById} onSelect={focusStageCard} />
              <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
                <StagesList
                  stages={visibleStages}
                  linesByStage={stageLinesByStage}
                  waitingByStage={stageWaitingOn.byStage}
                  nextStageId={nextStage?.id}
                  onOpen={(stage) => openStagePanel(stage)}
                  onAddItem={(stage) => openStagePanel(stage, { startWith: 'add-item' })}
                  onOpenReference={handleOpenReference}
                  menuFor={stageMenuFor}
                  highlightedStageId={highlightedStageId}
                  canEdit={canEditStages}
                  clocks={stageClocksById}
                  folderFor={canViewFiles ? stageFolderFor : undefined}
                />
                <aside
                  aria-label="Stage overview"
                  className={`grid content-start items-start gap-4 xl:sticky xl:top-[calc(var(--app-header-height,64px)+16px)] ${canViewHistory ? 'md:grid-cols-2 xl:grid-cols-1' : ''}`}
                >
                  <BlockersCard
                    projectId={id}
                    projectName={project.name}
                    points={stageBlockers}
                    basis={blockersBasis}
                    refreshKey={project.updated_at}
                    canAskAi={hasPermission(user, 'ai')}
                    className={canViewHistory ? 'md:col-span-2 xl:col-span-1' : undefined}
                  />
                  <WaitingOnCard
                    waitingOn={stageWaitingOn}
                    onSelectItem={(item) => openStagePanel(visibleStages.find((stage) => stage.id === item.stageId), { focusLineId: item.lineId })}
                  />
                  {canViewHistory ? (
                    <RecentActivityCard
                      projectId={id}
                      refreshKey={project.updated_at}
                      onOpenHistory={() => setHistoryDialogOpen(true)}
                    />
                  ) : null}
                </aside>
              </div>
            </>
          )}
          <StageEditPanel
            open={stagePanel.open && Boolean(panelStage)}
            onOpenChange={(open) => setStagePanel((prev) => ({ ...prev, open }))}
            stage={panelStage}
            stageNumber={panelStage ? String(Number(panelStage.order) || panelStageIndex + 1).padStart(2, '0') : ''}
            lines={panelStage ? stageLinesSaved(panelStage) : []}
            statusOptions={STAGE_STATUS_OPTIONS}
            lineStatusOptions={STAGE_LINE_STATUS_OPTIONS}
            supportsChildren={panelStage ? supportsStageLineChildren(panelStage.name) : false}
            onSave={handleSaveStagePanel}
            onCreateTask={canCreateTasks ? (line) => panelStage && handleOpenStageTaskDialog(panelStage, line) : undefined}
            onOpenReference={handleOpenReference}
            taskLineIds={stageLineTaskIds}
            canEdit={canEditStages}
            startWith={stagePanel.startWith}
            focusLineId={stagePanel.focusLineId}
            phone={isPhone}
          />
        </TabsContent>

        <TabsContent value="material" className="mt-5 space-y-4" data-testid="material-content">
          <input
            ref={boqExcelInputRef}
            type="file"
            accept=".xlsx,.csv,.tsv,.txt"
            className="hidden"
            onChange={handleBoqExcelFileChange}
            data-testid="material-excel-input"
          />
          <MaterialToolbar
            subtitle={materialSubtitle}
            canEdit={canEditMaterial}
            canAddVariation={canWriteVariations}
            canEditSiteNeeds={canEditProject && canEditMaterial}
            canAddTask={canCreateTasks}
            hasBoq={boqItems.length > 0}
            canExport={materialRows.length > 0}
            onExport={handleExportMaterial}
            onAddManually={handleOpenAddBoqDialog}
            onImportExcel={handleTriggerBoqExcelImport}
            onPasteRows={() => handleOpenBoqDialogFor('paste')}
            onFromCatalog={() => handleOpenBoqDialogFor('catalog')}
            onDownloadTemplate={handleDownloadBoqTemplate}
            onNewDeliveryNote={openAddDeliveryDialog}
            onNewSiteRequest={singleSelectedMaterialRow && singleSelectedMaterialRow.remaining > 0 ? () => handleOpenSiteRequestDialog(singleSelectedMaterialRow) : null}
            siteRequestHint={singleSelectedMaterialRow ? (singleSelectedMaterialRow.remaining > 0 ? singleSelectedMaterialRow.part_number : 'Nothing remaining') : 'Select 1 line'}
            onNewVariation={() => openVariationPanel(null)}
            onNewSiteNeeds={handleOpenSiteNeedsDialog}
            onNewTask={() => handleOpenTaskDialog('material')}
            onDeliveryNoteFromPhoto={() => setDnPhotoOpen(true)}
          />

          {!materialReady ? <TabContentSkeleton label="Loading material" /> : (
            <>
              {boqItems.length > 0 ? <MaterialKpiStrip kpis={materialKpis} /> : null}

              <MaterialVariationCallout
                summary={materialVoNeeded}
                nextVoNumber={suggestNextVoNumber(variations)}
                canDraft={canWriteVariations && canViewVariations}
                onDraft={draftVariationFromMaterial}
              />

              <MaterialConflictCallout
                conflict={materialConflict}
                hasBoq={boqItems.length > 0}
                onOpenDeliveryNote={openDeliveryNoteDialog}
                onOpenVariations={canViewVariations ? () => handleTabChange('variations') : null}
              />

              {boqItems.length === 0 ? (
                <MaterialEmptyState
                  canEdit={canEditMaterial}
                  onImportExcel={handleTriggerBoqExcelImport}
                  onDownloadTemplate={handleDownloadBoqTemplate}
                  onPasteRows={() => handleOpenBoqDialogFor('paste')}
                  onFromCatalog={() => handleOpenBoqDialogFor('catalog')}
                />
              ) : null}

              {allowSubBoq && boqItems.length > 0 ? (
                <section className="overflow-hidden rounded-card border border-line bg-surface shadow-card" data-testid="material-main-boq">
                  <div className="flex flex-wrap items-baseline gap-x-2 border-b border-line px-4 py-3 sm:px-5">
                    <h3 className="text-[14px] font-bold text-ink">Main BOQ (summary)</h3>
                    <span className="text-[12px] text-ink-3">Totals of every Sub BOQ × its quantity · {mainBoqRows.length} part{mainBoqRows.length === 1 ? '' : 's'}</span>
                  </div>
                  <MaterialTable
                    variant="summary"
                    entries={mainBoqRows.map((row) => ({ type: 'row', row }))}
                    voColumns={materialVoColumns}
                    dnColumns={mainDeliveryNoteColumns}
                    showVoDetail={materialVoOpen}
                    onToggleVoDetail={() => toggleMaterialGroupColumns('vo')}
                    showDnDetail={materialDnOpen}
                    onToggleDnDetail={() => toggleMaterialGroupColumns('dn')}
                  />
                </section>
              ) : null}

              {boqItems.length > 0 ? (
                <section className="overflow-hidden rounded-card border border-line bg-surface shadow-card" data-testid="material-table">
                  <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5 sm:px-5">
                    <SearchField
                      value={materialQuery}
                      onChange={setMaterialQuery}
                      label="Search BOQ lines"
                      placeholder="Search part no. or description"
                      className="w-full sm:w-[280px]"
                      testId="material-search"
                    />
                    {allowSubBoq && Object.keys(materialRowsBySubBoq).length > 1 ? (
                      <ChipSelect
                        label="Sub BOQ"
                        value={materialSubBoqFilter}
                        onChange={(value) => setMaterialSubBoq(value || null)}
                        allLabel="All Sub BOQs"
                        options={Object.keys(materialRowsBySubBoq).sort((a, b) => a.localeCompare(b)).map((label) => ({ value: label, label, count: materialRowsBySubBoq[label].length }))}
                        testId="material-sub-boq-filter"
                      />
                    ) : null}
                    <SegmentedControl
                      size="sm"
                      ariaLabel="Which lines"
                      value={materialView}
                      onChange={setMaterialView}
                      className="sm:ml-auto"
                      options={[
                        { key: 'all', label: 'All', count: materialRows.length },
                        { key: 'short', label: 'Short', count: materialRows.filter((row) => row.remaining > 0).length },
                        { key: 'complete', label: 'Complete', count: materialRows.filter((row) => row.remaining <= 0).length },
                        { key: 'vo', label: 'VO needed', count: materialRows.filter((row) => row.variationNeeded > 0).length },
                      ]}
                      testId="material-view"
                    />
                  </div>
                  <MaterialTable
                    variant="detail"
                    entries={materialEntries}
                    voColumns={materialVoColumns}
                    dnColumns={materialDeliveryNoteColumns}
                    showVoDetail={materialVoOpen}
                    onToggleVoDetail={() => toggleMaterialGroupColumns('vo')}
                    showDnDetail={materialDnOpen}
                    onToggleDnDetail={() => toggleMaterialGroupColumns('dn')}
                    allowSubBoq={allowSubBoq}
                    canEdit={canEditMaterial}
                    subBoqOptions={subBoqOptions}
                    selection={{
                      selectedIds: selectedBoqRowIds,
                      onToggle: (rowId, checked) => handleToggleBoqSelection(rowId, checked),
                      onToggleAll: (checked) => {
                        const visibleIds = materialEntries.filter((entry) => entry.type === 'row').map((entry) => entry.row.id);
                        setSelectedBoqRowIds((prev) => (checked ? [...new Set([...prev, ...visibleIds])] : prev.filter((rowId) => !visibleIds.includes(rowId))));
                        if (!checked) boqSelectionAnchorRef.current = null;
                      },
                      onPointer: (event) => { boqSelectionInteractionRef.current = { shiftKey: event.shiftKey }; },
                    }}
                    qty={materialQtyCells}
                    expandedRowIds={expandedMaterialRows}
                    onToggleRow={toggleMaterialRow}
                    renderHistory={(row) => (
                      <MaterialRowHistory
                        history={materialRowHistory(row, { variationColumns: materialVoColumns, deliveryNoteColumns: materialDeliveryNoteColumns })}
                        onOpenDeliveryNote={openDeliveryNoteDialog}
                        onDeleteRequest={(request) => handleConfirmDeleteSiteRequest(request, row)}
                        canEdit={canEditMaterial}
                        onOpenTrace={row.part_number ? () => setMaterialTrace({ part: row.part_number, description: row.description || '' }) : null}
                      />
                    )}
                    rowMenu={canEditMaterial ? materialRowMenu : materialReadMenu}
                    onOpenDeliveryNote={openDeliveryNoteDialog}
                    onToggleGroup={handleToggleMaterialSubBoqCollapse}
                    drag={canEditMaterial && !materialQuery && materialView === 'all' ? {
                      active: Boolean(draggingBoqItemId),
                      draggingId: draggingBoqItemId,
                      onDragStart: handleBoqDragStart,
                      onDragEnd: () => setDraggingBoqItemId(null),
                      onDrop: (rowId) => { void handleBoqDrop(rowId); },
                    } : undefined}
                    footer={<MaterialTotalsFooter kpis={materialKpis} lineCount={materialRows.length} subBoq={allowSubBoq} />}
                    stock={materialStock.data}
                  />
                </section>
              ) : null}
              <MaterialTracePanel
                open={Boolean(materialTrace)}
                onOpenChange={(open) => { if (!open) setMaterialTrace(null); }}
                part={materialTrace?.part || ''}
                description={materialTrace?.description || ''}
                projectId={id}
                projectName={project.name}
              />

              <div ref={materialBarsRef} className="sticky bottom-[calc(1rem+var(--app-bottom-nav-height,0px))] z-20 space-y-2">
                <MaterialBulkBar
                  count={selectedBoqRowIds.length}
                  labels={selectedMaterialRows.map((row) => row.part_number || row.description).filter(Boolean)}
                  canEdit={canEditMaterial}
                  onDelete={handleDeleteSelectedBoqItems}
                  onSiteRequest={singleSelectedMaterialRow && singleSelectedMaterialRow.remaining > 0 ? () => handleOpenSiteRequestDialog(singleSelectedMaterialRow) : null}
                  onClear={() => {
                    setSelectedBoqRowIds([]);
                    boqSelectionAnchorRef.current = null;
                  }}
                />
                {canEditMaterial ? (
                <UnsavedBar
                  count={materialDirtyCount}
                  sticky={false}
                  tone="brand"
                  className="mt-0"
                  detail={(
                    <span className={MONO_CLASS}>
                      {[...materialDiff.invalid, ...materialDiff.changes].map((change) => change.label).filter((label, index, list) => list.indexOf(label) === index).slice(0, 4).join(', ')}
                      {materialDirtyCount > 4 ? ' …' : ''}
                    </span>
                  )}
                  status={materialSave.status === 'saved' && materialDirtyCount === 0 ? 'saved' : materialSave.status}
                  error={materialSave.error}
                  onSave={handleSaveMaterialChanges}
                  onDiscard={handleDiscardMaterialChanges}
                  saveLabel="Save changes"
                  data-testid="material-unsaved-bar"
                />
                ) : null}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="files" className="mt-5 space-y-4">
          <FilesToolbar
            subtitle={`OneDrive folder health for ${project.name} · ${REQUIRED_FOLDER_COUNT} required folders`}
            connected={Boolean(onedriveStatus.connected)}
            account={onedriveAccountLabel(onedriveStatus)}
            authLoading={onedriveAuthLoading}
            scanning={filesAuditLoading}
            hasPath={Boolean(String(filesPathDraft || '').trim())}
            onConnect={handleConnectOneDrive}
            onDisconnect={handleRequestDisconnectOneDrive}
            onScan={handleScanProjectFiles}
            onedriveOn={onedriveOn}
            canEdit={canEditFiles}
          />
          <FolderPathCard
            path={project.files_root_path || ''}
            onSave={handleSaveFolderPath}
            canEdit={canEditFolderPath}
            saving={filesPathSaving}
            scannedAt={filesScan?.scanned_at}
            scannedBy={filesScan && filesScan.trigger !== 'nightly' ? filesScan.scanned_by : ''}
            scannedLabel={filesFolderChanged ? 'Not scanned yet — folder changed' : undefined}
          />
          {!filesScan && !filesScans.loading && onedriveOn ? (
            <p className="flex items-center gap-2 rounded-card border border-dashed border-[color:var(--line-dashed)] px-4 py-3 text-[13px] text-ink-3" data-testid="files-not-scanned">
              {onedriveStatus.connected
                  ? `Not scanned yet${filesFolderChanged ? ' — the project folder changed since the last scan' : ''} — press Scan OneDrive to check this folder against the required structure.`
                  : canEditFiles
                    ? 'Connect OneDrive, set the project folder above, then press Scan OneDrive to check missing and empty folders.'
                    : 'OneDrive is not connected for this project — ask someone with Files edit access to connect it.'}
            </p>
          ) : null}
          <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_400px]">
            <div className="min-w-0 space-y-4">
              <FilesHealthCard
                health={folderHealthCounts}
                scanned={folderChecklist.scanned}
                handover={handoverState}
                onCreateMissing={canEditFiles ? handleCreateMissingFolders : undefined}
                creating={creatingFolders}
                createState={createFoldersState}
              />
              <FolderChecklist
                checklist={folderChecklist}
                onOpenFolder={handleOpenReference}
                scanning={filesAuditLoading}
                onedriveOn={onedriveOn}
                selectedPath={selectedFolderPath}
                onSelect={(row) => setSelectedFolderPath((current) => (current === row.path ? '' : row.path))}
                stageChipFor={(row) => {
                  const linked = linkedStageForFolder(row.name, visibleStages);
                  if (!linked) return null;
                  return (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        void jumpToStage(linked.stage.id);
                      }}
                      className="inline-flex h-6 shrink-0 items-center gap-1 rounded-md border border-line bg-surface px-1.5 text-[11.5px] font-semibold text-[color:var(--brand-ink)] hover:bg-brand-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      title={`This folder proves Stage ${linked.number} · ${linked.stage.name}. Open the stage`}
                      data-testid={`folder-stage-chip-${linked.stage.id}`}
                    >
                      <Link2 className="h-3 w-3" aria-hidden="true" />
                      Stage {linked.number}<span className="hidden sm:inline"> · {linked.stage.name}</span>
                    </button>
                  );
                }}
              />
            </div>
            <div className="min-w-0 space-y-4 lg:sticky lg:top-[calc(var(--app-header-height,64px)+16px)]">
              <FolderPreviewCard
                projectId={id}
                row={selectedFolderRow}
                linked={selectedFolderLink}
                onedriveOn={onedriveOn}
                connected={Boolean(onedriveStatus.connected)}
                canWrite={onedriveCanWrite && canEditFiles}
                canEdit={canEditFiles}
                onOpenUrl={handleOpenReference}
                onUploaded={() => filesScans.reload()}
              />
              <StageEvidenceCard
                evidence={stageEvidence}
                onOpenStage={(stage) => { void jumpToStage(stage.id); }}
              />
              <ScanHistoryCard history={filesScans.data?.history || []} activity={filesScans.data?.activity || []} nightly={filesScans.data?.nightly} />
            </div>
          </div>
        </TabsContent>

        {canAccessInvoicing && (
        <TabsContent value="invoicing" className="mt-5 space-y-4" data-testid="invoicing-content">
          <TabHeader
            title="Invoicing"
            subtitle={`Payment schedule and invoices · base scope${approvedVariationCount ? ` + ${approvedVariationCount} approved variation${approvedVariationCount === 1 ? '' : 's'}` : ''}`}
            actions={(
              <>
                <Button type="button" variant="ghost" onClick={() => handleExportInvoices(invoiceRows)} disabled={!invoiceRows.length} data-testid="invoices-export">
                  <Download aria-hidden="true" />
                  Export
                </Button>
                {/* 28 #8: Sync and Add are Invoicing changes — a View-only Invoicing role reads and exports only */}
                {canEditInvoices ? (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleSyncInvoices(invoiceRows)}
                      disabled={!quickBooksReady || Boolean(qbSync) || !invoiceRows.some((invoice) => canSyncToQuickBooks(invoice, { configured: true }).ok)}
                      title={!quickBooksReady ? 'QuickBooks is not connected — set it up in Admin › Integrations' : 'Send every issued invoice of this scope that is not in QuickBooks yet'}
                      aria-describedby={quickBooksNoteShown ? 'invoices-qb-note' : undefined}
                      data-testid="invoices-sync-all"
                    >
                      {qbSync ? <Loader2 className="animate-spin" aria-hidden="true" /> : <RefreshCw aria-hidden="true" />}
                      {qbSync ? `Syncing ${qbSync.done + 1} of ${qbSync.total}…` : 'Sync to QuickBooks'}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        if (invoiceScope === 'total') {
                          toast.error('Select Base scope or a specific variation to add an invoice');
                          return;
                        }
                        openInvoicePanel('add');
                      }}
                      title={invoiceScope === 'total' ? 'Pick Base scope or one variation first' : undefined}
                      data-testid="invoices-add"
                    >
                      <Plus aria-hidden="true" />
                      Add Invoice
                    </Button>
                  </>
                ) : null}
              </>
            )}
          />

          {quickBooksNoteShown ? (
            <QuickBooksSyncNote id="invoices-qb-note" status={quickBooksStatus} canSetUp={hasPermission(user, 'admin')} className="-mt-2 sm:justify-end" />
          ) : null}

          {!invoicingReady ? <TabContentSkeleton label="Loading invoices" /> : (
            <>
              <ContractMoneyBar bar={contractMoneyBar} approvedVariationCount={approvedVariationCount} collection={invoiceCollection} />

              <ReadyToBillCallout
                items={readyToBill}
                canRaise={canEditInvoices}
                onRaise={(invoice) => {
                  if (invoiceScope !== 'base') setInvoiceScope('base');
                  openInvoicePanel('raise', invoice);
                }}
              />

              <PaymentMilestonesTable
                summary={invoiceSummary}
                scopeValue={scopeTotalValue}
                title={`Payment milestones · ${invoiceScope === 'base' ? 'Base scope' : invoiceScope === 'total' ? 'All (base + approved variations)' : (selectedVariation?.vo_number || 'Variation')}`}
                meta={(() => {
                  const termCount = invoiceSummary.byType.filter((row) => row.totalAmount > 0).length;
                  return (
                    <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
                      <span>{termCount} term{termCount === 1 ? '' : 's'} ·</span>
                      <dl className="flex flex-wrap items-baseline gap-x-3 gap-y-1" aria-label={selectedScopeLabel} data-testid="scope-totals">
                      <div className="flex items-baseline gap-1">
                        <dt>Scope value</dt>
                        <dd className="font-semibold tabular-nums text-ink">{formatAmount(scopeTotalValue)}</dd>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <dt>Invoiced</dt>
                        <dd className="font-semibold tabular-nums text-ink">
                          {formatAmount(invoiceSummary.totalInvoiced)}
                          {scopeTotalValue > 0 ? <span className="font-normal text-ink-3"> ({formatNumber((invoiceSummary.totalInvoiced / scopeTotalValue) * 100, { decimals: 0 })}%)</span> : null}
                        </dd>
                      </div>
                      </dl>
                    </div>
                  );
                })()}
                note={invoiceScope !== 'base' && invoiceScope !== 'total' && selectedVariation?.status !== 'Approved'
                  ? 'This variation is excluded from Total until approved.' : null}
              />

              <div className="flex flex-wrap items-center gap-3">
                <InvoiceScopeSwitcher
                  value={invoiceScope}
                  onChange={(next) => { setInvoiceScope(next); setSelectedInvoiceIds([]); }}
                  variations={variations}
                  counts={invoiceScopeCounts}
                />
                <SearchField
                  value={invoiceQuery}
                  onChange={setInvoiceQuery}
                  label="Search invoices"
                  placeholder="Search invoice no. or note"
                  className="w-full sm:ml-auto sm:w-[260px]"
                  testId="invoices-search"
                />
              </div>

              <section className="overflow-hidden rounded-card border border-line bg-surface shadow-card">
                <InvoicesTable
                  groups={invoiceGroups}
                  scopeValue={scopeTotalValue}
                  selectedIds={selectedInvoiceIds}
                  onToggle={(invoiceId, checked) => setSelectedInvoiceIds((prev) => (checked ? [...new Set([...prev, invoiceId])] : prev.filter((entry) => entry !== invoiceId)))}
                  onToggleAll={(checked) => setSelectedInvoiceIds(checked ? invoiceRows.map((invoice) => invoice.id) : [])}
                  onOpen={(invoice) => openInvoicePanel('edit', invoice)}
                  onRaise={(invoice) => openInvoicePanel('raise', invoice)}
                  rowMenu={invoiceRowMenu}
                  canEdit={canEditInvoices}
                  onInvoicesChanged={fetchData}
                  showScopeCode={invoiceScope === 'total'}
                  handoverDate={project.end_date}
                  handedOver={projectHandedOver}
                  qbMatchFor={qbProjectMatchFor(qbDesktop.data)}
                  footer={(
                    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-line bg-surface-2 px-4 py-2.5 text-[12.5px] text-ink-3 sm:px-5">
                      <span>{invoiceScope === 'base' ? 'Base scope' : invoiceScope === 'total' ? 'All scopes' : (selectedVariation?.vo_number || 'Variation')} · {invoiceRows.length} row{invoiceRows.length === 1 ? '' : 's'}</span>
                      <span className="tabular-nums">
                        Total <b className="font-semibold text-ink">{formatAmount(invoiceRows.reduce((sum, invoice) => sum + Number(invoice.amount || 0), 0))}</b>
                        {scopeTotalValue > 0 ? ` · ${formatNumber((invoiceRows.reduce((sum, invoice) => sum + Number(invoice.amount || 0), 0) / scopeTotalValue) * 100, { decimals: 0 })}% of the scope` : ''}
                      </span>
                    </div>
                  )}
                />
              </section>

              {/* QuickBooks Desktop, read only: the linked customer / job's invoices, payments and costs */}
              <QbDesktopPanel
                projectId={id}
                data={qbDesktop.data}
                canEdit={canEditInvoices}
                onChanged={qbDesktop.reload}
                invoices={invoices}
                variationInvoices={variationInvoices}
                variations={variations}
              />

              <BulkBar
                count={selectedInvoices.length}
                noun="selected"
                onClear={() => setSelectedInvoiceIds([])}
                testId="invoices-bulk-bar"
                actions={[
                  canEditInvoices && {
                    label: 'Sync to QuickBooks',
                    icon: RefreshCw,
                    onSelect: () => handleSyncInvoices(selectedInvoices),
                    disabled: !quickBooksReady || Boolean(qbSync) || !selectedInvoices.some((invoice) => canSyncToQuickBooks(invoice, { configured: true }).ok),
                    title: !quickBooksReady ? 'QuickBooks is not connected' : undefined,
                  },
                  { label: 'Export', icon: Download, onSelect: () => handleExportInvoices(selectedInvoices) },
                ]}
              />
            </>
          )}

          <InvoicePanel
            open={invoicePanel.open}
            onOpenChange={(open) => setInvoicePanel((prev) => ({ ...prev, open }))}
            mode={invoicePanel.mode}
            invoice={panelInvoice}
            scopeLabel={panelScopeLabel}
            scopeValue={panelScopeValue}
            otherTotal={panelOtherTotal}
            typeOptions={INVOICE_TYPE_OPTIONS}
            statusOptions={INVOICE_STATUS_OPTIONS}
            onSave={canEditInvoices ? handleSaveInvoicePanel : undefined}
            readOnly={!canEditInvoices}
            onOpenPhoto={(url) => openAuthenticatedFile(url).catch((err) => notify.error('Could not open the photo', { description: getErrorMessage(err) }))}
            focus={invoicePanel.focus}
            scheduledCount={(panelScopeId === 'base' ? invoices : variationInvoices.filter((invoice) => invoice.variation_id === panelScopeId))
              .filter((invoice) => (invoice.status || 'Not Started') === 'Not Started').length}
            retentionDueHint={panelRetentionHint}
          />
        </TabsContent>
        )}

        <TabsContent value="structure" className="mt-5 space-y-4" data-testid="structure-content">
          <TabHeader
            title="Project Structure"
            subtitle={`${isBmsProject ? 'Buildings → subs → nested devices' : 'Buildings → floors → rooms → panels → devices'} · drives execution sub-items`}
            actions={(
              <>
                {/* 28 #8: Import / Drag subs / Add building / Save need Edit on Project Structure — View only exports */}
                {canEditStructureTree ? (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleTriggerTtlImport}
                    title={isBmsProject ? 'Import an Excel structure — you see the changes before anything is replaced' : 'Import a TTL, CSV or Excel structure — you see the changes before anything is replaced'}
                    data-testid="structure-import"
                  >
                    <Upload aria-hidden="true" />
                    {isBmsProject ? 'Import Excel' : 'Import'}
                  </Button>
                ) : null}
                {isBmsProject && canEditStructureTree ? (
                  <Button
                    type="button"
                    variant={bmsSubDragMode ? 'outline' : 'ghost'}
                    aria-pressed={bmsSubDragMode}
                    onClick={() => {
                      if (bmsSubDragMode) {
                        setDraggingBmsSub(null);
                        setDraggingBmsRootSub(null);
                      }
                      setBmsSubDragMode(!bmsSubDragMode);
                    }}
                    className={bmsSubDragMode ? 'border-brand-soft-border bg-brand-soft text-[color:var(--brand-ink)]' : ''}
                  >
                    <GripVertical aria-hidden="true" />
                    {bmsSubDragMode ? 'Drag subs: on' : 'Drag subs'}
                  </Button>
                ) : null}
                <Button type="button" variant="ghost" onClick={handleExportStructureExcel}>
                  <Download aria-hidden="true" />
                  Export Excel
                </Button>
                {canEditStructureTree ? (
                  <>
                    <Button type="button" variant="outline" onClick={handleAddBuilding} data-testid="structure-add-building">
                      <Plus aria-hidden="true" />
                      Add building
                    </Button>
                    <Button
                      type="button"
                      onClick={handleSaveExecutionStructure}
                      disabled={structureSaving || !structureDirtyCount}
                      title={structureDirtyCount ? undefined : 'No changes to save'}
                      aria-label={structureDirtyCount ? `Save structure (${structureDirtyCount} unsaved change${structureDirtyCount === 1 ? '' : 's'})` : 'Save structure'}
                      data-testid="structure-save"
                    >
                      {structureSaving ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Save aria-hidden="true" />}
                      Save structure
                      <SaveCountPill count={structureDirtyCount} />
                    </Button>
                  </>
                ) : null}
              </>
            )}
          />

          <StructureSummaryStrip summary={structureSummary} isBms={isBmsProject} conflicts={structureAddressConflicts.length} />

          {!['LCS', 'HA K', 'HA KNX', 'Home Automation KNX', 'BMS'].includes(project.system_type) && (
            <div className="rounded-card border border-line bg-surface px-4 py-3 text-[13px] text-ink-3 shadow-card">
              Auto-generated execution tasks are enabled for <b className="font-semibold text-ink">LCS</b>, <b className="font-semibold text-ink">HA KNX</b>, and <b className="font-semibold text-ink">BMS</b>. This project is currently <b className="font-semibold text-ink">{project.system_type || 'not assigned'}</b>.
            </div>
          )}

          {ttlImportErrorLog ? (
            <div className="space-y-2 rounded-card border border-[color:var(--danger-border,#f3c2c2)] bg-status-red-bg px-4 py-3" role="alert">
              <p className="text-[13px] font-semibold text-status-red-ink">Structure import error</p>
              <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-surface p-3 text-xs text-ink-2">{ttlImportErrorLog}</pre>
            </div>
          ) : null}

          {activeTab === 'structure' ? (
            <StructureEditor
              bms={isBmsProject}
              structure={executionStructure}
              selectedId={structureSelectedId}
              onSelect={setStructureSelectedId}
              collapsed={collapsedStructureSections}
              onToggle={toggleStructureSection}
              onExpandAll={(keys) => setCollapsedStructureSections((prev) => {
                const next = { ...prev };
                keys.forEach((key) => { next[key] = false; });
                return next;
              })}
              ops={structureOps}
              searchIndex={structureIndex}
              onOpenSearch={openStructureNode}
              conflicts={structureAddressConflicts}
              allowDuplicates={allowDuplicateDeviceAddresses}
              // changing the rule is PUT /projects/{id} (Projects + Structure edit); everyone else reads it as text
              onAllowDuplicatesChange={canEditProject ? handleAllowDuplicateDeviceAddressesChange : undefined}
              duplicateSaving={duplicateAddressSettingSaving}
              isPhone={isPhone}
              onAddBuilding={canEditStructureTree ? handleAddBuilding : undefined}
              changes={structureDiff.changes}
            />
          ) : null}

          {canEditStructureTree ? (
          <UnsavedBar
            count={structureDirtyCount}
            detail={structureSaveDetail}
            status={structureSaveStatus.status === 'saved' && structureDirtyCount === 0 ? 'saved' : structureSaveStatus.status}
            error={structureSaveStatus.error}
            onSave={handleSaveExecutionStructure}
            onDiscard={() => discardStructureChanges()}
            saveLabel="Save structure"
            data-testid="structure-unsaved-bar"
          >
            <StructureChangesReview changes={structureDiff.changes} />
          </UnsavedBar>
          ) : null}

          <StructureImportWizard
            open={structureImportOpen}
            onOpenChange={setStructureImportOpen}
            projectId={id}
            isBms={isBmsProject}
            dirtyCount={structureDirtyCount}
            onSaveFirst={() => saveStructure({ rethrow: true })}
            onApplied={applyImportedStructure}
          />
        </TabsContent>

        <TabsContent value="execution" className="mt-5 space-y-4" data-testid="execution-content">
          <TabHeader
            title="Execution"
            subtitle={isPhone
              ? `${executionMainTasksCount} main task${executionMainTasksCount === 1 ? '' : 's'} from Project Structure`
              : `${executionMainTasksCount} main task${executionMainTasksCount === 1 ? '' : 's'} generated from Project Structure · template edited ${formatDate(project.execution_template_updated_at || project.updated_at)}`}
            actions={(
              <>
                {isPhone ? (
                  <SegmentedControl
                    ariaLabel="Phone view"
                    size="sm"
                    value={phoneExecutionView}
                    onChange={(view) => { setSelectedExecutionTaskIds([]); setPhoneExecutionView(view); }}
                    options={[{ key: 'site', label: 'Site mode', testId: 'execution-phone-site' }, { key: 'list', label: 'List', testId: 'execution-phone-list' }]}
                  />
                ) : null}
                {isPhone ? (
                  // 09 #8 (phones): Edit template / Export / Print behind one ⋯ so the cards start above the fold.
                  <RowActions
                    label="More execution actions"
                    size="default"
                    data-testid="execution-more"
                    items={[
                      canEditExecution && { label: 'Edit template', icon: Settings2, onSelect: () => setExecutionTemplateDialogOpen(true) },
                      { label: 'Export (CSV)', icon: Download, onSelect: handleExportExecution, disabled: !executionTasks.length, testId: 'execution-export' },
                      { label: 'Print', icon: Printer, onSelect: handlePrintExecutionList, disabled: !executionTasks.length, testId: 'execution-print' },
                    ]}
                  />
                ) : (
                <>
                {/* 28 #8: the template is an Execution change — a View-only Execution role reads, exports and prints */}
                {canEditExecution ? (
                  <Button type="button" variant="ghost" onClick={() => setExecutionTemplateDialogOpen(true)} data-testid="execution-edit-template">
                    <Settings2 aria-hidden="true" />
                    Edit template
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleExportExecution}
                  disabled={!executionTasks.length}
                  title={selectedExecutionTaskIds.length ? 'Export the selected tasks and their sub-items (CSV)' : 'Export what the filters show (CSV)'}
                  data-testid="execution-export"
                >
                  <Download aria-hidden="true" />
                  Export
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handlePrintExecutionList}
                  disabled={!executionTasks.length}
                  title={selectedExecutionTaskIds.length ? 'Print the selected tasks and their sub-items' : 'Print what the filters show'}
                  data-testid="execution-print"
                >
                  <Printer aria-hidden="true" />
                  Print
                </Button>
                </>
                )}
                {executionDirtyCount > 0 && !isPhone && canEditExecution ? (
                  <Button type="button" variant="ghost" onClick={discardExecutionChanges} data-testid="execution-discard">
                    Discard
                  </Button>
                ) : null}
                {/* Phones: the sticky save bar saves; the header button only shows while there is something to save. */}
                {canEditExecution && (!isPhone || executionDirtyCount > 0) ? (
                <Button
                  type="button"
                  onClick={handleSaveExecutionTasks}
                  // 09 #6: nothing changed → nothing to PUT (it used to resend every task and say "updated")
                  disabled={!isStageLinesEditMode || executionTasks.length === 0 || executionDirtyCount === 0 || executionSave.status === 'saving' || !canEditExecution}
                  title={executionDirtyCount ? undefined : 'No changes to save'}
                  aria-label={executionDirtyCount ? `Save execution (${executionDirtyCount} unsaved change${executionDirtyCount === 1 ? '' : 's'})` : 'Save execution'}
                  data-testid="execution-save"
                >
                  {executionSave.status === 'saving' ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Save aria-hidden="true" />}
                  Save execution
                  <SaveCountPill count={executionDirtyCount} />
                </Button>
                ) : null}
              </>
            )}
          />

          {executionTasks.length === 0 ? (
            <div className="rounded-card border border-line bg-surface px-6 py-8 text-center text-[13px] text-ink-3 shadow-card">
              No execution tasks generated. Set the project system type to <b className="font-semibold text-ink">LCS</b>, <b className="font-semibold text-ink">HA KNX</b>, or <b className="font-semibold text-ink">BMS</b>, then define the project structure.
            </div>
          ) : (
            <>
              {!isPhone ? executionSummaryBlock : null}

              {/* 09 #7: the views and the Table | Board | Matrix switch (+ Columns) share one row; phones scroll it sideways. */}
              <div className={cn('flex min-w-0 items-center gap-2', isPhone ? '-mx-4 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden' : 'flex-wrap justify-between')}>
                <SavedViews views={executionViews} className={isPhone ? 'w-max flex-nowrap' : undefined} testId="execution-filters-views" />
                {!isPhone ? (
                  <div className="ml-auto flex items-center gap-2">
                    <ExecutionViewSwitch value={executionView} onChange={(view) => { setSelectedExecutionTaskIds([]); setExecutionView(view); }} />
                    {executionView === 'table' ? <ExecutionColumnsMenu value={executionColumns} onChange={setExecutionColumns} /> : null}
                  </div>
                ) : null}
              </div>

              <ExecutionFilterBar
                phoneScroll
                query={executionFilter.q || ''}
                onQueryChange={(q) => setExecutionFilter({ q })}
                search={{
                  placeholder: 'Search tasks, zones, devices…',
                  label: 'Search execution tasks',
                  testId: 'execution-search',
                }}
                filters={[
                  { key: 'status', label: 'Status', options: executionChipOptions.status, value: executionFilter.status, onChange: (status) => setExecutionFilter({ status }), testId: 'execution-filter-status' },
                  { key: 'category', label: 'Category', options: executionChipOptions.category, value: executionFilter.category, onChange: (category) => setExecutionFilter({ category }), testId: 'execution-filter-category' },
                  { key: 'building', label: 'Building', options: executionChipOptions.building, value: executionFilter.building, onChange: (building) => setExecutionFilter({ building }), hidden: !executionChipOptions.building.length, more: isPhone, testId: 'execution-filter-building' },
                  { key: 'doneBy', label: 'Done by', options: executionChipOptions.doneBy, value: executionFilter.doneBy, onChange: (doneBy) => setExecutionFilter({ doneBy }), testId: 'execution-filter-done-by' },
                  {
                    key: 'monitoring',
                    label: 'Monitoring',
                    multiple: false,
                    options: [{ value: 'on', label: 'Monitored' }, { value: 'off', label: 'Not monitored' }],
                    value: executionFilter.monitoring || null,
                    onChange: (monitoring) => setExecutionFilter({ monitoring: monitoring || '' }),
                    more: isPhone,
                    testId: 'execution-filter-monitoring',
                  },
                  { key: 'comments', label: 'Has comments', type: 'toggle', value: executionFilter.comments, onChange: (comments) => setExecutionFilter({ comments }), more: isPhone, testId: 'execution-filter-comments' },
                ]}
                onClearAll={clearExecutionFilters}
                summary={executionFiltered.active
                  ? <><b>{formatNumber(executionFiltered.matched.size, { decimals: 0 })}</b> matching</>
                  : null}
                testId="execution-filters"
              />

              {isPhone ? executionSummaryBlock : null}

              {isPhone && phoneExecutionView === 'site' ? (
                <SiteMode
                  projectId={id}
                  tasks={executionFiltered.active ? executionTasks.filter((task) => executionFiltered.included.has(task.id)) : executionTasks}
                  tree={executionTree}
                  userName={user?.name || ''}
                  canEdit={canEditExecution}
                  canRaiseVo={canEdit(user, 'variations') || canEdit(user, 'execution')}
                  reviewer={project.senior_project_engineer}
                  onEdit={handleExecutionCellEdit}
                  onSynced={() => { if (executionDirtyCount === 0) void fetchData(); }}
                />
              ) : !isPhone && executionView === 'board' ? (
                <ExecutionBoard
                  tasks={executionFiltered.active ? executionTasks.filter((task) => executionFiltered.included.has(task.id)) : executionTasks}
                  tree={executionTree}
                  canEdit={canEditExecution}
                  onMove={(task, status) => handleExecutionCellEdit(task.id, 'status', status)}
                  onOpen={(task) => { setExecutionView('table'); setCollapsedExecutionParents((prev) => ({ ...prev, [task.id]: false })); }}
                />
              ) : !isPhone && executionView === 'matrix' ? (
                <ExecutionMatrix
                  tasks={executionFiltered.active ? executionTasks.filter((task) => executionFiltered.included.has(task.id)) : executionTasks}
                  tree={executionTree}
                  metric={executionMatrixMetric}
                  onMetricChange={setExecutionMatrixMetric}
                  onCellClick={(building, task) => {
                    setExecutionView('table');
                    setExecutionFilter({ building: [building] });
                    if (task) setCollapsedExecutionParents((prev) => ({ ...prev, [task.id]: false }));
                  }}
                />
              ) : isPhone ? (
                <ExecutionCards
                  rows={executionRows}
                  canEdit={canEditExecution}
                  assignees={executionAssignees}
                  selectedIds={selectedExecutionTaskIds}
                  onSelectionChange={setSelectedExecutionTaskIds}
                  onToggle={toggleExecutionParent}
                  onEdit={handleExecutionCellEdit}
                  pagingKey={executionFilterKey}
                  empty={executionEmptyState}
                />
              ) : (
                <ExecutionTable
                  rows={executionRows}
                  columnKeys={executionColumns}
                  canEdit={canEditExecution}
                  assignees={executionAssignees}
                  selectedIds={selectedExecutionTaskIds}
                  onSelectionChange={setSelectedExecutionTaskIds}
                  onToggle={toggleExecutionParent}
                  onEdit={handleExecutionCellEdit}
                  pagingKey={executionFilterKey}
                  empty={executionEmptyState}
                />
              )}

              <div ref={executionBarsRef} className="sticky bottom-[calc(1rem+var(--app-bottom-nav-height,0px))] z-20 space-y-2">
                <ExecutionBulkBar
                  count={executionSelectionShown ? selectedExecutionTaskIds.length : 0}
                  canEdit={canEditExecution}
                  assignees={executionAssignees}
                  onSetStatus={handleBulkExecutionStatus}
                  onAssign={handleBulkExecutionAssign}
                  onSetDates={handleBulkExecutionDates}
                  onExport={handleExportExecution}
                  onPrint={handlePrintExecutionList}
                  onClear={() => setSelectedExecutionTaskIds([])}
                  compact={isPhone}
                />
                {canEditExecution ? (
                <UnsavedBar
                  count={executionDirtyCount}
                  sticky={false}
                  className="mt-0"
                  detail={executionChangesDetail}
                  status={executionSave.status === 'saved' && executionDirtyCount === 0 ? 'saved' : executionSave.status}
                  error={executionSave.error}
                  onSave={handleSaveExecutionTasks}
                  onDiscard={discardExecutionChanges}
                  saveLabel="Save execution"
                  data-testid="execution-unsaved-bar"
                />
                ) : null}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="timeline" className="mt-5 space-y-4" data-testid="timeline-content">
          <TabHeader
            title="Timeline"
            subtitle={timelineItems.length
              ? `Stages, execution and billing milestones on one time axis · execution grouped by ${TIMELINE_GROUP_LABELS[timelineView.group] || 'main task'}`
              : 'Stages, execution and billing milestones on one time axis'}
            actions={(
              <>
                <TimelineControls
                  zoom={timelineView.zoom}
                  group={timelineView.group}
                  onZoomChange={(zoom) => setTimelineView({ zoom })}
                  onGroupChange={(group) => setTimelineView({ group })}
                  // 10 #6: grouping works whenever Execution has rows on the Gantt — saved dates or Auto-plan proposals
                  groupDisabled={!(timelineModel?.sections.find((section) => section.key === 'execution')?.rows.length)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => timelineModel && openLookahead({ project, items: timelineModel.lookaheadItems, today: timelineToday, generatedBy: user?.name })}
                  disabled={!timelineModel}
                  title="The next three weeks — stages, tasks and who does them — as a page to save as PDF"
                  data-testid="timeline-lookahead"
                >
                  <FileDown aria-hidden="true" />
                  Export 3-week lookahead
                </Button>
                {/* 28 #8: Edit on Timeline, plus Stages / Execution when the plan moves their dates (Viewer accounts and
                    View-only roles read the plan; the button is hidden, not greyed) */}
                {canApplyPlan ? (
                  <Button
                    type="button"
                    onClick={() => setApplyPlanOpen(true)}
                    disabled={Boolean(applyPlanBlocked)}
                    title={applyPlanBlocked || 'Write the proposed dates to the project'}
                    data-testid="timeline-apply-plan"
                  >
                    <WandSparkles aria-hidden="true" />
                    Apply plan
                  </Button>
                ) : null}
              </>
            )}
          />

          <HandoverBanner
            handover={handoverState}
            execution={executionSummary}
            openStages={visibleStages.filter((stage) => !isStageDone(stage.status)).length}
            openTaskNames={openExecutionTaskNames}
            canEdit={canEditProject}
            busy={projectStatusSaving}
            onUpdateEndDate={() => setEndDateDialogOpen(true)}
            onPutOnHold={() => handleSetProjectStatus('On Hold')}
            onResume={() => handleSetProjectStatus('Active')}
          />

          <PlanCard
            plan={autoPlan}
            settings={planSettings}
            onSettingsChange={(changes) => setPlanSettingsState({ ...planSettings, ...changes })}
            hasDatedItems={timelineItems.length > 0 || planStages.some((stage) => stage.planned_start)}
            canPlan={!applyPlanBlocked || applyPlanBlocked.startsWith('Save or discard')}
            canSetDates={canEditExecutionModule && executionTasks.length > 0}
            onSetDates={handleOpenExecutionDates}
            appliedPlan={project.schedule_plan}
            closed={projectClosedStatus}
          />

          {timelineModel ? (
            <TimelineGantt
              model={timelineModel}
              zoom={timelineView.zoom}
              today={timelineToday}
              compact={isPhone}
              showProposalNote={planProposals.length > 0 && !projectClosedStatus}
            />
          ) : null}

          <ApplyPlanDialog
            open={applyPlanOpen}
            onOpenChange={setApplyPlanOpen}
            plan={autoPlan}
            endDate={project.end_date}
            canMoveEndDate={canEditProject}
            onApply={handleApplyPlan}
          />
        </TabsContent>

        {canViewVariations && (
          <TabsContent value="variations" className="mt-5 space-y-4" data-testid="variations-content">
            <TabHeader
              title="Variations"
              subtitle={canViewFinance && (project.original_scope_value ?? project.total_project_value) !== undefined && (project.original_scope_value ?? project.total_project_value) !== null
                ? `Change orders on top of the ${formatNumber(Number(project.original_scope_value ?? project.total_project_value ?? 0), { decimals: 0 })} base contract`
                : 'Change orders on top of the base contract'}
              actions={(
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => downloadTextFile(
                      `${(project?.name || 'project').replace(/[^\w.-]+/g, '_')}-variations.csv`,
                      `\uFEFF${variationsToCsv(variations, canAccessInvoicing ? variationInvoices : [], Number(project.original_scope_value ?? project.total_project_value ?? 0), { showFinance: canViewFinance })}`,
                      'text/csv;charset=utf-8',
                    )}
                    disabled={!variations.length}
                    title="The VO rows as CSV"
                    data-testid="variations-export"
                  >
                    <Download aria-hidden="true" />
                    Export
                  </Button>
                  {canWriteVariations ? (
                    <Button
                      type="button"
                      onClick={() => openVariationPanel(null)}
                      disabled={!isStageLinesEditMode}
                      data-testid="variation-add"
                    >
                      <Plus aria-hidden="true" />
                      Add Variation
                    </Button>
                  ) : null}
                </>
              )}
            />
            {/* Wave 2 (04 #2): the tab's records load when it opens — a skeleton until then, never a false "no variations". */}
            {!resourcesSettled(resourceStatus, TAB_BLOCKING_RESOURCES.variations) ? <TabContentSkeleton label="Loading variations" rows={4} /> : (
            <>
            <VariationsSummary
              summary={variationsSummary}
              variations={variations}
              consultant={project.consultant}
              showFinance={canViewFinance}
              onOpenInvoicing={canAccessInvoicing ? () => { setInvoiceScope('total'); setActiveTab('invoicing'); } : null}
            />

            {/* 11 #5: what Material found beyond BOQ + VOs, with the same "Draft VO" as the Material tab */}
            {canViewMaterial ? (
              <MaterialVariationCallout
                summary={materialVoNeeded}
                nextVoNumber={suggestNextVoNumber(variations)}
                canDraft={canWriteVariations}
                onDraft={draftVariationFromMaterial}
              />
            ) : null}

            <VariationsTable
              variations={variations}
              variationInvoices={variationInvoices}
              baseValue={Number(project.original_scope_value ?? project.total_project_value ?? 0)}
              canEdit={canWriteVariations && isStageLinesEditMode}
              showInvoices={canAccessInvoicing}
              showFinance={canViewFinance}
              expandedIds={expandedVariationIds}
              onToggle={toggleVariationRow}
              onEdit={(variation) => openVariationPanel(variation)}
              onDelete={(variation) => handleDeleteVariation(variation.id)}
              onOpenInvoicing={canAccessInvoicing ? handleOpenVariationInvoicing : null}
              onRaiseInvoice={canAccessInvoicing && canEdit(user, 'invoicing') ? handleRaiseVariationInvoice : null}
              onInvoicesChanged={fetchData}
              projectId={id}
              consultant={project.consultant}
              canRecordTrail={canWriteVariations && isStageLinesEditMode}
              onTrailChanged={handleVariationTrailChanged}
              emptyAction={canWriteVariations ? (
                <Button type="button" variant="outline" onClick={() => openVariationPanel(null)}>
                  <Plus aria-hidden="true" />
                  Add the first variation
                </Button>
              ) : null}
            />
            </>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* 05 #10: delivery note from a photo — the AI reads it, the person checks the lines, the DN form saves. */}
      {canViewMaterial ? (
        <DnPhotoPanel
          open={dnPhotoOpen}
          onOpenChange={setDnPhotoOpen}
          projectId={id}
          boqItems={deliveryBoqItems}
          onConfirm={handleConfirmDnPhoto}
        />
      ) : null}

      {/* 11 #4: Add / Edit variation drawer (opened from Variations and from Material's "New variation"). */}
      <VariationPanel
        open={variationPanel.open}
        onOpenChange={(open) => setVariationPanel((prev) => ({ ...prev, open }))}
        variation={variationPanel.variation}
        variations={variations}
        boqItems={boqItems}
        shortages={variationShortages}
        statusOptions={VARIATION_STATUS_OPTIONS}
        onSave={handleSaveVariationPanel}
        prefill={variationPanel.prefill}
        showFinance={canViewFinance}
      />

      <Dialog
        open={taskDialogOpen}
        onOpenChange={(open) => {
          setTaskDialogOpen(open);
          if (!open) {
            setTaskForm(createEmptyProjectTaskForm('material'));
          }
        }}
      >
        {/* Final QA: the foundation look (navy primary, app fields) instead of the old dark Apple skin */}
        <DialogContent className="max-w-md rounded-card border-line bg-surface text-ink" data-testid="project-task-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[18px] font-extrabold text-ink">Add task</DialogTitle>
            <DialogDescription className="text-[13px] text-ink-3">
              {`A task in Tasks, linked to ${project?.name || 'this project'}${taskForm.source === 'invoicing' ? ' and this invoice' : taskForm.source === 'stage' ? ' and this stage' : ''}.`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateProjectTask} className="space-y-3.5">
            {taskForm.source === 'invoicing' && (
              <div className="rounded-[10px] border border-line bg-surface-2 px-3 py-2 text-[12.5px] text-ink-2">
                Invoice <b className="font-semibold text-ink">{taskForm.invoice_reference || taskForm.invoice_id || '—'}</b>
              </div>
            )}
            {taskForm.source === 'stage' && (
              <div className="rounded-[10px] border border-line bg-surface-2 px-3 py-2 text-[12.5px] text-ink-2">
                Stage <b className="font-semibold text-ink">{taskForm.stage_name || '—'}</b>
                {taskForm.stage_line_name ? <> · Item <b className="font-semibold text-ink">{taskForm.stage_line_name}</b></> : null}
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="project-task-name" className="text-[12px] font-semibold text-ink-2">Task name</Label>
              <Input
                id="project-task-name"
                value={taskForm.name}
                onChange={(e) => setTaskForm((prev) => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="project-task-description" className="text-[12px] font-semibold text-ink-2">Description</Label>
              <Textarea
                id="project-task-description"
                value={taskForm.description}
                onChange={(e) => setTaskForm((prev) => ({ ...prev, description: e.target.value }))}
                className="min-h-[72px]"
              />
            </div>
            <div className="grid gap-3.5 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="project-task-assignee" className="text-[12px] font-semibold text-ink-2">Assigned to</Label>
                <Select
                  value={taskForm.assigned_to_user_id || '__none__'}
                  onValueChange={(value) => setTaskForm((prev) => ({ ...prev, assigned_to_user_id: value === '__none__' ? '' : value }))}
                >
                  <SelectTrigger id="project-task-assignee">
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Unassigned</SelectItem>
                    {platformUsers.map((entry) => (
                      <SelectItem key={entry.id} value={entry.id}>
                        {entry.name || entry.email || entry.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="project-task-due" className="text-[12px] font-semibold text-ink-2">Due date</Label>
                <Input
                  id="project-task-due"
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm((prev) => ({ ...prev, due_date: e.target.value }))}
                />
              </div>
            </div>
            <DialogFooter className="gap-2 pt-1 sm:space-x-0">
              <Button type="button" variant="ghost" onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="project-task-submit">
                <Plus aria-hidden="true" />
                Add task
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deviceBatchDialogOpen}
        onOpenChange={(open) => {
          setDeviceBatchDialogOpen(open);
          if (!open) {
            setDeviceBatchTarget(null);
            setDeviceBatchForm(createDeviceBatchForm());
            setDeviceBatchSearch('');
          }
        }}
      >
        {/* 08 #3: the foundation look (app fields, check boxes, primary + Cancel in the footer) */}
        <DialogContent className="max-w-xl rounded-card border-line bg-surface text-ink" data-testid="structure-add-devices-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[18px] font-extrabold text-ink">Add devices from the BOQ</DialogTitle>
            <DialogDescription className="text-[13px] text-ink-3">
              Tick BOQ lines, then say how many of each and how their addresses are numbered.
              {deviceBatchTargetLabel ? <> They go into <b className="font-semibold text-ink-2">{deviceBatchTargetLabel}</b>.</> : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3.5">
            {boqDeviceOptions.length === 0 && !['ready', 'error', 'skipped'].includes(resourceStatus.boq) ? (
              <p className="flex items-center justify-center gap-2 rounded-xl border border-line px-4 py-6 text-[13px] text-ink-3" role="status">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Loading the BOQ…
              </p>
            ) : boqDeviceOptions.length === 0 ? (
              <p className="rounded-xl border border-dashed border-[color:var(--line-dashed)] px-4 py-6 text-center text-[13px] text-ink-3">
                {resourceStatus.boq === 'ready' ? 'No BOQ items yet — add the project BOQ in Material first.' : 'The BOQ could not be loaded — open Material, then try again.'}
              </p>
            ) : (
              <>
                <SearchField
                  value={deviceBatchSearch}
                  onChange={setDeviceBatchSearch}
                  placeholder="Search part number or description"
                  label="Search BOQ devices"
                  testId="structure-add-devices-search"
                  shortcut={false}
                  className="w-full"
                />
                <div className="max-h-[240px] overflow-y-auto rounded-xl border border-line" role="group" aria-label="BOQ devices" data-testid="structure-add-devices-options">
                  {filteredBoqDeviceOptions.length ? (
                    <ul className="divide-y divide-line-2">
                      {filteredBoqDeviceOptions.map((item) => {
                        const checked = (deviceBatchForm.boq_item_ids || []).includes(item.id);
                        return (
                          <li key={item.id}>
                            <label className={cn('flex min-h-10 cursor-pointer items-center gap-3 px-3 py-2 text-[13px] hover:bg-surface-hover', checked && 'bg-brand-soft')}>
                              <TableCheckbox
                                checked={checked}
                                onCheckedChange={(next) => toggleDeviceBatchItem(item.id, Boolean(next))}
                                aria-label={`Add ${item.part_number || item.name}`}
                              />
                              {item.part_number ? <span className="shrink-0 font-mono text-[12.5px] font-semibold text-ink">{item.part_number}</span> : null}
                              <span className="min-w-0 truncate text-ink-2" title={item.name}>{item.name}</span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="px-3 py-4 text-center text-[13px] text-ink-3">No BOQ line matches “{deviceBatchSearch.trim()}”.</p>
                  )}
                </div>
                {boqDeviceOptions.length > filteredBoqDeviceOptions.length ? (
                  <p className="-mt-1.5 text-[12px] text-ink-3">
                    Showing {filteredBoqDeviceOptions.length} of {boqDeviceOptions.length} BOQ lines — type to narrow down.
                  </p>
                ) : null}
              </>
            )}

            {selectedBoqDeviceOptions.length > 0 ? (
              <div className="overflow-hidden rounded-xl border border-line" data-testid="structure-add-devices-settings">
                <div className="grid grid-cols-[minmax(0,1fr)_72px_72px_108px] items-center gap-2 border-b border-line bg-surface-2 px-3 py-2 text-[11.5px] font-bold text-ink-3">
                  <span>{selectedBoqDeviceOptions.length} selected</span>
                  <span>Quantity</span>
                  <span>Start no.</span>
                  <span>Address prefix</span>
                </div>
                <ul className="max-h-[220px] divide-y divide-line-2 overflow-y-auto">
                  {selectedBoqDeviceOptions.map((item) => {
                    const settings = deviceBatchForm.item_settings?.[item.id] || { quantity: 1, start_index: 1, address_prefix: '' };
                    const label = item.part_number || item.name;
                    return (
                      <li key={`settings-${item.id}`} className="grid grid-cols-[minmax(0,1fr)_72px_72px_108px] items-center gap-2 px-3 py-1.5">
                        <span className="min-w-0 truncate text-[13px] font-semibold text-ink" title={item.part_number ? `${item.part_number} — ${item.name}` : item.name}>{label}</span>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          value={settings.quantity ?? 1}
                          onChange={(e) => updateDeviceBatchItemSetting(item.id, { quantity: e.target.value })}
                          aria-label={`Quantity of ${label}`}
                          className="h-8 rounded-lg px-2 text-[13px] tabular-nums"
                        />
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          value={settings.start_index ?? 1}
                          onChange={(e) => updateDeviceBatchItemSetting(item.id, { start_index: e.target.value })}
                          aria-label={`Start number of ${label}`}
                          className="h-8 rounded-lg px-2 text-[13px] tabular-nums"
                        />
                        <Input
                          value={settings.address_prefix ?? ''}
                          onChange={(e) => updateDeviceBatchItemSetting(item.id, { address_prefix: e.target.value })}
                          placeholder="e.g. 1.1."
                          aria-label={`Address prefix of ${label}`}
                          className="h-8 rounded-lg px-2 font-mono text-[12.5px]"
                        />
                      </li>
                    );
                  })}
                </ul>
                <p className="border-t border-line-2 px-3 py-2 text-[12px] text-ink-3">
                  Addresses are the prefix + the number (1.1.<b className="font-semibold">1</b>, 1.1.<b className="font-semibold">2</b> …); leave the prefix empty for none.
                </p>
              </div>
            ) : null}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="ghost" onClick={() => setDeviceBatchDialogOpen(false)}>Cancel</Button>
            <Button
              type="button"
              onClick={handleCreateDevicesBatch}
              disabled={!selectedBoqDeviceOptions.length}
              data-testid="structure-add-devices-submit"
            >
              <Plus aria-hidden="true" />
              {deviceBatchCount ? `Add ${deviceBatchCount} device${deviceBatchCount === 1 ? '' : 's'}` : 'Add devices'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={executionTemplateDialogOpen} onOpenChange={setExecutionTemplateDialogOpen}>
        {/* 09 #9: built only while open — every template row has its own selects, rebuilt on each keystroke otherwise */}
        {executionTemplateDialogOpen ? (
        <DialogContent className="apple-project-dialog bg-zinc-900 border-zinc-800 max-w-6xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-zinc-100">Execution Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-zinc-400">Define main execution tasks, categories, structure expansion depth, how many working days each takes (Auto-plan) and what must finish first (Timeline dependencies).</p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleAddTemplateItem}
                  className="ap-text-btn"
                >
                  Add Task
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSaveExecutionTemplate}
                  className="rounded-sm bg-orange-500 text-white hover:bg-orange-600"
                >
                  Save Template
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              {executionTemplate.map((item, index) => (
                <div key={item.id} className="grid gap-3 rounded-sm border border-zinc-800/60 bg-zinc-950/40 p-3 lg:grid-cols-[3rem_minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1.25fr)_6.5rem_minmax(0,1.3fr)_auto]">
                  <div className="flex items-center">
                    <Badge variant="outline" className="rounded-sm border-zinc-700 text-[10px] text-zinc-400">#{index + 1}</Badge>
                  </div>
                  <Input
                    value={item.name}
                    onChange={(e) => handleTemplateItemChange(item.id, 'name', e.target.value)}
                    className="bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm"
                    placeholder="Task name"
                  />
                  <Select value={item.category || 'Execution'} onValueChange={(value) => handleTemplateItemChange(item.id, 'category', value)}>
                    <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="apple-project-dialog bg-zinc-900 border-zinc-800">
                      {EXECUTION_CATEGORY_OPTIONS.map((option) => (
                        <SelectItem key={option} value={option}>{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={item.expansion || 'none'} onValueChange={(value) => handleTemplateItemChange(item.id, 'expansion', value)}>
                    <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="apple-project-dialog bg-zinc-900 border-zinc-800">
                      {EXECUTION_EXPANSION_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <TemplateDurationInput item={item} onChange={(value) => handleTemplateItemChange(item.id, 'duration_days', value)} />
                  <TemplateDependsPicker item={item} items={executionTemplate} onChange={(value) => handleTemplateItemChange(item.id, 'depends_on', value)} />
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleMoveTemplateItem(item.id, -1)}
                      className="rounded-sm border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                      disabled={index === 0}
                    >
                      Up
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleMoveTemplateItem(item.id, 1)}
                      className="rounded-sm border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                      disabled={index === executionTemplate.length - 1}
                    >
                      Down
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteTemplateItem(item.id)}
                      className="rounded-sm border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
              {executionTemplate.length === 0 && (
                <div className="rounded-sm border border-zinc-800/60 bg-zinc-950/40 p-6 text-center text-sm text-zinc-500">
                  No template tasks configured.
                </div>
              )}
            </div>
            <p className="text-xs text-zinc-500">Saving the template updates this project immediately. New projects will also use the updated structure.</p>
          </div>
        </DialogContent>
        ) : null}
      </Dialog>

      <Dialog open={siteNeedsDialogOpen} onOpenChange={setSiteNeedsDialogOpen}>
        <DialogContent className="apple-project-dialog bg-zinc-900 border-zinc-800 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-zinc-100">Add Site Needs</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveSiteNeeds} className="space-y-4">
            <div className="rounded-sm border border-zinc-800/60 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800/50 hover:bg-transparent">
                    <TableHead className="text-zinc-500 text-[10px] uppercase">Part No</TableHead>
                    <TableHead className="text-zinc-500 text-[10px] uppercase">Description</TableHead>
                    <TableHead className="text-zinc-500 text-[10px] uppercase">Unit</TableHead>
                    <TableHead className="text-zinc-500 text-[10px] uppercase text-center">Site Needs Qty</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {boqItems.map((item) => (
                    <TableRow key={item.id} className="border-zinc-800/30">
                      <TableCell className="font-mono text-xs text-zinc-300">{item.part_number}</TableCell>
                      <TableCell className="text-xs text-zinc-400">{item.description}</TableCell>
                      <TableCell className="text-xs text-zinc-400">{item.unit || 'pcs'}</TableCell>
                      <TableCell className="text-center">
                        <Input
                          type="number"
                          min="0"
                          step="any"
                          value={siteNeedsDrafts[item.id] ?? 0}
                          onChange={(e) => handleSiteNeedsQtyChange(item.id, e.target.value)}
                          className="h-8 mx-auto w-24 bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm text-xs text-center"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-sm">
              Save Site Needs
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={addBooqDialogOpen}
        onOpenChange={handleAddBoqDialogOpenChange}
      >
        <DialogContent className="apple-project-dialog bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-zinc-100">Add BOQ Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Search Inventory</Label>
              <Input
                value={catalogSearch}
                onChange={(e) => setCatalogSearch(e.target.value)}
                data-boq-dialog-focus="catalog"
                placeholder="Search by part no / description / brand"
                className="bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm"
              />
            </div>
            {allowSubBoq && (
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm">Sub BOQ</Label>
                <SuggestionInput
                  value={boqSubGroupDraft}
                  onChange={(value) => setBoqSubGroupDraft(value)}
                  options={subBoqOptions}
                  placeholder="Sub BOQ name"
                  className="bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm"
                />
                <p className="text-[11px] text-zinc-500">Applies to newly created BOQ items.</p>
              </div>
            )}

            <div className="space-y-2 rounded-sm border border-zinc-800/60 bg-zinc-950/40 p-3">
              <Label className="text-zinc-400 text-sm">Paste from Excel (Part No, Description, BOQ Qty)</Label>
              <Textarea
                value={boqPasteText}
                onChange={(e) => setBoqPasteText(e.target.value)}
                data-boq-dialog-focus="paste"
                placeholder={'Example:\nMTN6705-0008\tSpaceLogic KNX Switch/Blind Master\t5'}
                className="min-h-[110px] bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleImportBoqFromPaste}
                className="rounded-sm border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                Import Pasted BOQ
              </Button>
            </div>

            <div className="rounded-sm border border-zinc-800/60">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800/50 hover:bg-transparent">
                    <TableHead className="text-zinc-500 text-[10px] uppercase">Select</TableHead>
                    <TableHead className="text-zinc-500 text-[10px] uppercase">Part No</TableHead>
                    <TableHead className="text-zinc-500 text-[10px] uppercase">Description</TableHead>
                    <TableHead className="text-zinc-500 text-[10px] uppercase">Unit</TableHead>
                    <TableHead className="text-zinc-500 text-[10px] uppercase">BOQ Qty</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {catalogOptions.map((item, index) => {
                    const selectionKey = getCatalogSelectionKey(item);
                    return (
                    // The catalog can list a part number twice: the row key must still be unique.
                    <TableRow key={`${selectionKey}::${item.id || index}`} className="border-zinc-800/30">
                      <TableCell>
                        <Checkbox
                          checked={Boolean(selectedCatalog[selectionKey]?.checked)}
                          onCheckedChange={(checked) => toggleCatalogItem(item, Boolean(checked))}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-xs text-zinc-300">{item.part_number}</TableCell>
                      <TableCell className="text-xs text-zinc-400">{item.description}</TableCell>
                      <TableCell className="text-xs text-zinc-400">{item.unit || 'pcs'}</TableCell>
                      <TableCell>
                        <Input
                          value={selectedCatalog[selectionKey]?.qty ?? 1}
                          onChange={(e) => updateCatalogQty(selectionKey, e.target.value)}
                          className="h-8 bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm text-xs"
                          style={autoFieldWidth(selectedCatalog[selectionKey]?.qty ?? 1, 5, 10)}
                          disabled={!selectedCatalog[selectionKey]?.checked}
                        />
                      </TableCell>
                    </TableRow>
                    );
                  })}
                  {catalogOptions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-zinc-500 text-sm">No items found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="space-y-3 rounded-sm border border-zinc-800/60 p-3">
              <p className="text-sm text-zinc-300">Add New Catalog Item (if missing)</p>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Part Number" value={newCatalogForm.part_number} onChange={(e) => setNewCatalogForm({ ...newCatalogForm, part_number: e.target.value })}
                  className="bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm" />
                <Input placeholder="Description" value={newCatalogForm.description} onChange={(e) => setNewCatalogForm({ ...newCatalogForm, description: e.target.value })}
                  className="bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm" />
                <Input placeholder="Unit" value={newCatalogForm.unit} onChange={(e) => setNewCatalogForm({ ...newCatalogForm, unit: e.target.value })}
                  className="bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm" />
                <Input placeholder="Category" value={newCatalogForm.category} onChange={(e) => setNewCatalogForm({ ...newCatalogForm, category: e.target.value })}
                  className="bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm" />
              </div>
              <Button type="button" variant="outline" onClick={handleCreateCatalogInline}
                className="rounded-sm border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                Add Catalog Item
              </Button>
            </div>

            <div className="space-y-2 rounded-sm border border-zinc-800/60 p-3">
              <p className="text-sm text-zinc-300">Photo Upload (auto-recognition/import option)</p>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setMaterialPhoto(e.target.files?.[0] || null)}
                className="bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm"
              />
              <Button type="button" variant="outline" onClick={handleImportPhoto}
                className="rounded-sm border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                Import From Photo
              </Button>
            </div>

            <Button type="button" className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-sm" onClick={handleAddSelectedBoqItems}>
              Add Selected BOQ Items
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={addDnDialogOpen}
        onOpenChange={(open) => {
          setAddDnDialogOpen(open);
          if (!open) {
            setEditingDeliveryNoteId(null);
            setDeliveryPasteText('');
          }
        }}
      >
        <DialogContent className="apple-project-dialog bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-zinc-100">
              {!canEditMaterial ? 'Delivery Note' : editingDeliveryNoteId ? 'Edit Delivery Note' : 'Add Delivery Note'}
            </DialogTitle>
          </DialogHeader>
          {/* 28 #8: a View-only Material role opens a DN (from its column) to READ it: every field is disabled (the
              fieldset), the Excel paste and Save / Delete are not offered. */}
          <form onSubmit={canEditMaterial ? handleSubmitDeliveryNote : (event) => event.preventDefault()} className="space-y-4">
            <fieldset
              disabled={!canEditMaterial}
              className="m-0 min-w-0 space-y-4 border-0 p-0 [&_:disabled]:!opacity-100"
              data-testid="delivery-note-fields"
            >
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="delivery-note-number" className="text-zinc-400 text-sm">DN Number</Label>
                <Input id="delivery-note-number" value={deliveryForm.dn_number} onChange={(e) => setDeliveryForm({ ...deliveryForm, dn_number: e.target.value })}
                  className="bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm" required />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm">Date</Label>
                <Input type="date" value={deliveryForm.delivery_date} onChange={(e) => setDeliveryForm({ ...deliveryForm, delivery_date: e.target.value })}
                  className="bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm">Supplier</Label>
                <Input value={deliveryForm.supplier} onChange={(e) => setDeliveryForm({ ...deliveryForm, supplier: e.target.value })}
                  className="bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm">Received By</Label>
                <Input value={deliveryForm.received_by} onChange={(e) => setDeliveryForm({ ...deliveryForm, received_by: e.target.value })}
                  className="bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm">Optional LPO</Label>
                <Input value={deliveryForm.lpo_id} onChange={(e) => setDeliveryForm({ ...deliveryForm, lpo_id: e.target.value })}
                  className="bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm" />
              </div>
	              <div className="space-y-2">
	                <Label className="text-zinc-400 text-sm">Type</Label>
	                <Select value={deliveryForm.delivery_type} onValueChange={(v) => setDeliveryForm({ ...deliveryForm, delivery_type: v })} disabled={!canEditMaterial}>
                  <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="apple-project-dialog bg-zinc-900 border-zinc-800">
                    <SelectItem value="to_stock">To Stock</SelectItem>
                    <SelectItem value="to_site">To Site</SelectItem>
	                  </SelectContent>
	                </Select>
	              </div>
	              <div className="space-y-2 col-span-2">
	                <Label htmlFor="delivery-note-notes" className="text-zinc-400 text-sm">Notes</Label>
                <Textarea id="delivery-note-notes" value={deliveryForm.notes} onChange={(e) => setDeliveryForm({ ...deliveryForm, notes: e.target.value })}
                  className="bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm" />
              </div>
            </div>

            {canEditMaterial ? (
            <div className="space-y-2 rounded-sm border border-zinc-800/60 bg-zinc-950/40 p-3" data-testid="delivery-note-paste">
              <Label className="text-zinc-400 text-sm">Paste from Excel (Part No, Description, Delivered Qty)</Label>
              <Textarea
                value={deliveryPasteText}
                onChange={(e) => setDeliveryPasteText(e.target.value)}
                placeholder={'Example:\nMTN6705-0008\tSpaceLogic KNX Switch/Blind Master\t3'}
                className="min-h-[110px] bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleApplyDeliveryPaste}
                className="rounded-sm border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                Apply Pasted Delivery Qty
              </Button>
            </div>
            ) : null}

	            <div className="rounded-sm border border-zinc-800/60 p-3 space-y-2">
	              {deliveryBoqItems.map((item) => (
	                <div key={item.id} className="grid grid-cols-[1fr_auto] gap-3 items-center">
	                  <div className="text-xs text-zinc-400">
	                    <span className="font-mono text-zinc-300">{item.part_number}</span> - {item.description}
                      <span className="ml-2 text-zinc-500">
                        BOQ: {item.boq_qty ?? 0}{item.remaining !== undefined ? ` · Remaining: ${item.remaining}` : ''}
                      </span>
	                  </div>
	                  <Input
	                    value={deliveryForm.line_qty[item.id] ?? 0}
	                    onChange={(e) => handleDeliveryQtyChange(item.id, e.target.value)}
                      aria-label={`Delivered quantity for ${item.part_number || 'no part number'} ${item.description || ''}`.trim()}
	                    className="h-8 bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm text-xs"
	                    style={autoFieldWidth(deliveryForm.line_qty[item.id] ?? 0, 5, 10)}
	                  />
	                </div>
	              ))}
	              {deliveryBoqItems.length === 0 && <p className="text-xs text-zinc-500">No BOQ items available</p>}
	            </div>
            </fieldset>

            <div className="flex flex-wrap items-center gap-2">
              {editingDeliveryNoteId && canEditMaterial ? (
                <Button
                  type="button"
                  variant="danger"
                  onClick={async () => {
                    const deleted = await handleDeleteDeliveryNote(editingDeliveryNoteId);
                    if (deleted) setAddDnDialogOpen(false);
                  }}
                  data-testid="delivery-note-delete"
                >
                  <Trash2 aria-hidden="true" />
                  Delete delivery note…
                </Button>
              ) : null}
              {canEditMaterial ? (
                <Button type="submit" className="ml-auto min-w-[200px] flex-1 sm:flex-none">
                  {editingDeliveryNoteId ? 'Save Delivery Note' : 'Add Delivery Note'}
                </Button>
              ) : null}
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={siteRequestDialogOpen} onOpenChange={handleSiteRequestDialogChange}>
        <DialogContent className="apple-project-dialog bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-zinc-100">Add Site Request</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSiteRequest} className="space-y-4">
            <div className="rounded-sm border border-zinc-800/60 bg-zinc-950/40 p-3 text-xs text-zinc-400">
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-zinc-200">{siteRequestTarget?.part_number || '-'}</span>
                <span>{siteRequestTarget?.unit || 'pcs'}</span>
              </div>
              <p className="mt-1 text-zinc-500">{siteRequestTarget?.description || 'No description'}</p>
              <p className="mt-2 text-zinc-500">Remaining qty: {siteRequestTarget?.remaining ?? 0}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm">Requested Qty</Label>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  value={siteRequestForm.requested_qty}
                  onChange={(e) => setSiteRequestForm((prev) => ({ ...prev, requested_qty: e.target.value }))}
                  className="bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm">Due Date</Label>
                <Input
                  type="date"
                  value={siteRequestForm.due_date}
                  onChange={(e) => setSiteRequestForm((prev) => ({ ...prev, due_date: e.target.value }))}
                  className="bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Notes</Label>
              <Textarea
                value={siteRequestForm.notes}
                onChange={(e) => setSiteRequestForm((prev) => ({ ...prev, notes: e.target.value }))}
                className="bg-zinc-800/50 border-zinc-700 text-zinc-100 rounded-sm"
                placeholder="Optional note for procurement"
              />
            </div>

            <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-sm">
              Save Site Request
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <SidePanel
        open={projectDialogOpen}
        onOpenChange={(open) => {
          setProjectDialogOpen(open);
          if (!open && project) setProjectForm(projectToForm(project));
        }}
        width="lg"
        title="Edit project details"
        subtitle={project.name}
        dirty={projectFormDirtyCount}
        onSave={handleSaveProjectDetails}
        saveLabel="Save details"
        confirmOptions={{
          title: 'Discard changes to the project details?',
          description: `${projectFormDirtyCount} unsaved change${projectFormDirtyCount === 1 ? '' : 's'} will be lost.`,
          confirmLabel: 'Discard changes',
          cancelLabel: 'Keep editing',
        }}
        testId="project-details-panel"
      >
        <SidePanelSection title="Project">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="project-form-name" className="text-[12px] font-semibold text-ink-2">Project name *</Label>
              <Input id="project-form-name" value={projectForm.name} onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12px] font-semibold text-ink-2">Client</Label>
              <SuggestionInput
                value={projectForm.client}
                onChange={(value) => setProjectForm({ ...projectForm, client: value })}
                options={clientOptions}
                placeholder="Client"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="project-form-system" className="text-[12px] font-semibold text-ink-2">System type</Label>
              <SuggestionInput
                value={projectForm.system_type}
                onChange={(value) => setProjectForm({ ...projectForm, system_type: value })}
                options={systemTypeOptions}
                placeholder="System type"
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="project-form-location" className="text-[12px] font-semibold text-ink-2">Location</Label>
              <Input id="project-form-location" value={projectForm.location} onChange={(e) => setProjectForm({ ...projectForm, location: e.target.value })} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="project-form-waze" className="text-[12px] font-semibold text-ink-2">Waze link</Label>
              <Input
                id="project-form-waze"
                type="url"
                value={projectForm.waze_url}
                onChange={(e) => setProjectForm({ ...projectForm, waze_url: e.target.value })}
                placeholder="https://waze.com/ul?ll=25.2048,55.2708&navigate=yes"
              />
              <p className="text-[12px] text-ink-3">A Waze link with coordinates also puts the project on the projects map.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12px] font-semibold text-ink-2">Status</Label>
              <Select value={projectForm.status} onValueChange={(value) => setProjectForm({ ...projectForm, status: value })}>
                <SelectTrigger aria-label="Project status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="On Hold">On Hold</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="hidden sm:block" aria-hidden="true" />
            <div className="space-y-1.5">
              <Label htmlFor="project-form-start" className="text-[12px] font-semibold text-ink-2">Start date</Label>
              <Input id="project-form-start" type="date" value={projectForm.start_date} onChange={(e) => setProjectForm({ ...projectForm, start_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="project-form-end" className="text-[12px] font-semibold text-ink-2">Handover (end) date</Label>
              <Input id="project-form-end" type="date" value={projectForm.end_date} onChange={(e) => setProjectForm({ ...projectForm, end_date: e.target.value })} />
            </div>
          </div>
        </SidePanelSection>

        <SidePanelSection title="Team">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-[12px] font-semibold text-ink-2">Project Engineer</Label>
              <Select
                value={projectForm.project_engineer || '__none__'}
                onValueChange={(value) => setProjectForm({ ...projectForm, project_engineer: value === '__none__' ? '' : value })}
              >
                <SelectTrigger aria-label="Project Engineer"><SelectValue placeholder="Select project engineer" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Unassigned</SelectItem>
                  {projectEngineerOptions.map((name) => (
                    <SelectItem key={`project-engineer-${name}`} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12px] font-semibold text-ink-2">Senior Project Engineer</Label>
              <Select
                value={projectForm.senior_project_engineer || '__none__'}
                onValueChange={(value) => setProjectForm({ ...projectForm, senior_project_engineer: value === '__none__' ? '' : value })}
              >
                <SelectTrigger aria-label="Senior Project Engineer"><SelectValue placeholder="Select senior project engineer" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Unassigned</SelectItem>
                  {projectEngineerOptions.map((name) => (
                    <SelectItem key={`senior-project-engineer-${name}`} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12px] font-semibold text-ink-2">Consultant</Label>
              <SuggestionInput
                value={projectForm.consultant}
                onChange={(value) => setProjectForm({ ...projectForm, consultant: value })}
                options={consultantOptions}
                placeholder="Consultant"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12px] font-semibold text-ink-2">Contractor / GC</Label>
              <SuggestionInput
                value={projectForm.contractor}
                onChange={(value) => setProjectForm({ ...projectForm, contractor: value })}
                options={contractorOptions}
                placeholder="Contractor / GC"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[12px] font-semibold text-ink-2">Salesman</Label>
              <Select value={projectForm.salesman || undefined} onValueChange={(value) => setProjectForm({ ...projectForm, salesman: value })}>
                <SelectTrigger aria-label="Salesman"><SelectValue placeholder="Select salesman" /></SelectTrigger>
                <SelectContent>
                  {PROJECT_SALESMEN.map((name) => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </SidePanelSection>

        <SidePanelSection title="Commercial">
          <div className="grid gap-3 sm:grid-cols-2">
            {canViewFinance ? (
              <div className="space-y-1.5">
                <Label htmlFor="project-form-value" className="text-[12px] font-semibold text-ink-2">Original scope amount (AED)</Label>
                <Input
                  id="project-form-value"
                  type="number"
                  value={projectForm.original_scope_value}
                  onChange={(e) => setProjectForm({ ...projectForm, original_scope_value: e.target.value })}
                />
              </div>
            ) : null}
            {canViewFinance ? (
              <div className="space-y-1.5 sm:col-span-2" data-testid="project-form-payment-terms">
                <Label className="text-[12px] font-semibold text-ink-2">Payment terms</Label>
                {/* 28 #8: a field owned by a tab follows that tab's level too (PROJECT_FIELD_MODULES on the server) */}
                <MultiSelectPopover
                  value={projectForm.payment_terms}
                  onChange={(value) => setProjectForm((prev) => ({ ...prev, payment_terms: value }))}
                  options={INVOICE_TYPE_OPTIONS}
                  placeholder={canEditInvoices ? 'Select payment terms' : 'None set'}
                  className="w-full justify-between"
                  disabled={!canEditInvoices}
                  ariaLabel="Payment terms"
                />
                {!canEditInvoices ? <ViewOnlyFieldNote module="Invoicing" /> : null}
              </div>
            ) : null}
            <fieldset className="space-y-1.5 sm:col-span-2">
              <legend className="text-[12px] font-semibold text-ink-2">Scope</legend>
              <div className="grid grid-cols-2 gap-2 rounded-control border border-line bg-surface-2 p-3">
                {PROJECT_SCOPE_OPTIONS.map((item) => (
                  <label key={item} className="flex cursor-pointer items-center gap-2 text-[13px] text-ink">
                    <Checkbox
                      checked={(Array.isArray(projectForm.scope) ? projectForm.scope : []).includes(item)}
                      onCheckedChange={(checked) => handleProjectScopeToggle(item, Boolean(checked))}
                    />
                    <span>{item}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>
        </SidePanelSection>

        <SidePanelSection title="Files and notes">
          <div className="space-y-1.5">
            <Label htmlFor="project-form-folder" className="text-[12px] font-semibold text-ink-2">OneDrive folder path</Label>
            <Input
              id="project-form-folder"
              value={projectForm.files_root_path}
              onChange={(e) => setProjectForm({ ...projectForm, files_root_path: e.target.value })}
              placeholder="Projects\35. LIV Waterside\HA"
              disabled={!canEditFiles}
            />
            {!canEditFiles ? <ViewOnlyFieldNote module="Files" /> : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="project-form-notes" className="text-[12px] font-semibold text-ink-2">Notes</Label>
            <Textarea id="project-form-notes" value={projectForm.notes} onChange={(e) => setProjectForm({ ...projectForm, notes: e.target.value })} />
          </div>
          <ProjectTagsInput tags={projectForm.tags} suggestions={projectFieldOptions.tags || []} onChange={(tags) => setProjectForm((prev) => ({ ...prev, tags }))} />
        </SidePanelSection>

        <SidePanelSection
          title="Extra fields"
          actions={(
            <Button type="button" variant="ghost" size="xs" className="text-[color:var(--brand-ink)]" onClick={handleAddProjectCustomField}>
              <Plus aria-hidden="true" />
              Add field
            </Button>
          )}
        >
          {normalizeProjectCustomFields(projectForm.custom_fields).length === 0 ? (
            <p className="text-[12.5px] text-ink-3">No extra fields. Add one for anything the form does not cover (e.g. Permit number).</p>
          ) : null}
          {normalizeProjectCustomFields(projectForm.custom_fields).map((field, index) => (
            <div key={field.id} className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)_auto] items-center gap-2">
              <Input
                value={field.title}
                onChange={(e) => handleProjectCustomFieldChange(field.id, 'title', e.target.value)}
                placeholder="Field title"
                aria-label={`Extra field ${index + 1} title`}
              />
              <Input
                value={field.content}
                onChange={(e) => handleProjectCustomFieldChange(field.id, 'content', e.target.value)}
                placeholder="Value"
                aria-label={`Extra field ${index + 1} value`}
              />
              <Button
                type="button"
                size="icon-sm"
                variant="ghost"
                onClick={() => handleDeleteProjectCustomField(field.id)}
                aria-label={`Remove extra field ${field.title || index + 1}`}
              >
                <Minus aria-hidden="true" />
              </Button>
            </div>
          ))}
        </SidePanelSection>

        <SidePanelSection title="Settings" collapsible defaultOpen={false}>
          <label className="flex items-start gap-2.5 text-[13px] text-ink">
            <Switch
              checked={Boolean(projectForm.allow_sub_boq)}
              onCheckedChange={(checked) => setProjectForm({ ...projectForm, allow_sub_boq: Boolean(checked) })}
              aria-label="Allow sub BOQ"
              disabled={!canEditMaterial}
            />
            <span>
              <span className="block font-semibold">Allow sub BOQ</span>
              <span className="block text-[12px] text-ink-3">Group BOQ lines into Sub BOQs (each with a quantity) on the Material tab.</span>
              {!canEditMaterial ? <ViewOnlyFieldNote module="Material" /> : null}
            </span>
          </label>
          <label htmlFor="allow-duplicate-device-addresses" className="flex items-start gap-2.5 text-[13px] text-ink">
            <Checkbox
              id="allow-duplicate-device-addresses"
              checked={Boolean(projectForm.allow_duplicate_device_addresses)}
              onCheckedChange={(checked) => setProjectForm({ ...projectForm, allow_duplicate_device_addresses: Boolean(checked) })}
              className="mt-0.5"
              disabled={!canEditStructure}
            />
            <span>
              <span className="block font-semibold">Allow duplicate device addresses</span>
              <span className="block text-[12px] text-ink-3">Skips the duplicate-address check of Project Structure for this project.</span>
              {!canEditStructure ? <ViewOnlyFieldNote module="Project Structure" /> : null}
            </span>
          </label>
        </SidePanelSection>
      </SidePanel>

    </div>
  );
}
